import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useVirtualizer, defaultRangeExtractor } from '@tanstack/react-virtual';
import { setTabExpandedSections, setTabRefAgentExpanded, setWritingRulesPending, resolveWritingRules, clearWritingRulesNoChange } from '../../../store/genbiSlice';

import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import { postNdjson } from '../../utils/postNdjson';
import { StatusBadge, StatusBadgeSm, StatusBadgeXSm } from '../../utils/StatusBadge';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import WarningPopup from '../../utils/popup/WarningPopup';
import DocumentSelector from './DocumentSelector';
import DocumentPreview from '../../utils/DocumentPreview';
import GenbiResponseComponent from './GenbiResponseComponent';
import ErrorBoundary from '../../utils/ErrorBoundary';
import { flushSync, createPortal } from 'react-dom';
import ReportModalForm from './ReportModalForm';
import { ColorPickerButton, withTocDefaults } from './ReportFormattingModal';
import ValueMessageSelector from './ValueMessageSelector';
import ToggleMenu from './ToggleMenu';
import ConfirmationModal from '../../utils/ConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import VersionSelector, { AgentHeaderRow } from './VersionSelector';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

import AgentConfigure from './AgentConfigure';
import AgentVersionComparePanel from './AgentVersionComparePanel';
import AgentTranslationModal from './AgentTranslationModal';
import FilterPickerModal from './FilterPickerModal';
import SyncDocsModal from './SyncDocsModal';
import { uploadCroppedImages } from './responseImageUpload';
import TableStructureSelector from './TableStructureSelector';
import { PERMISSIONS, capabilityChecker } from '../../utils/permissionRoutes';

// Feature toggle: set to true to bring back "Import Agent Context" in the More Actions menu.
const SHOW_IMPORT_AGENT_CONTEXT = false;

const deriveTemplateStatus = (data) => {
  const status = data.status || '';
  const total = data.agent_count || 0;
  const done = data.completed_agents || 0;

  // Imported, not yet run: status is empty, content is mapped
  if (!status && data.generation_status === 'COMPLETED') return 'MAPPED';

  if (status === 'COMPLETED' && total > 0) {
    // Nothing finished yet: don't claim COMPLETED
    if (done === 0) return '';
    // Some, but not all, finished: progress badge, pinned to 1..99
    if (done < total) {
      const pct = Math.round((done / total) * 100);
      return `PROGRESS:${Math.min(99, Math.max(1, pct))}`;
    }
  }
  return status;
};
// Keeps keystrokes in local state and only pushes upward after a pause or on
// blur. Without this, one character re-renders every agent in the template.
const DeferredTextControl = React.memo(({ as: Tag = 'textarea', value, onCommit, onBlur, commitDelay = 250, ...rest }) => {
  const [local, setLocal] = useState(value ?? '');
  const committedRef = useRef(value ?? '');
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  // The debounced commit must call the CURRENT onCommit: the queued timer would
  // otherwise fire against the closure from the render that scheduled it, whose
  // guards and formData are already stale.
  const onCommitRef = useRef(onCommit);
  useEffect(() => { onCommitRef.current = onCommit; });

  // Adopt changes that came from anywhere other than this control. Done during
  const incomingValue = value ?? '';
  if (incomingValue !== committedRef.current) {
    committedRef.current = incomingValue;
    pendingRef.current = null;
    setLocal(incomingValue);
  }

  const commit = useCallback((next) => {
    clearTimeout(timerRef.current);
    pendingRef.current = null;
    if (next === committedRef.current) return;
    committedRef.current = next;
    onCommitRef.current(next);
  }, []);

  // Flush on unmount so an edit still inside the debounce window is not lost
  // when the control disappears without a blur (filter change, refresh, tab).
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    const pending = pendingRef.current;
    if (pending !== null && pending !== committedRef.current) {
      onCommitRef.current(pending);
    }
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    setLocal(next);
    pendingRef.current = next;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(next), commitDelay);
  };

  const handleBlur = (e) => {
    commit(e.target.value);
    if (onBlur) onBlur(e);
  };

  return <Tag {...rest} value={local} onChange={handleChange} onBlur={handleBlur} />;
});

// Permanently stable identity that always invokes the latest closure. Handlers
// that read formData can't use a plain useCallback (formData is in their deps,
// so the identity churns every edit and defeats memo on every child).
// Event handlers only — never call during render.
const useStableCallback = (fn) => {
  const ref = useRef(fn);
  useEffect(() => { ref.current = fn; });
  return useCallback((...args) => ref.current(...args), []);
};

// The heavy part of an agent: markdown, tables, Plotly. Memoised so that
// per-agent UI state elsewhere in the template (the Thinking/Quick switch,
// dropdowns, selection) re-renders the lightweight shell without touching
// hundreds of response subtrees. Props below must all be stable.
// Mirrors the backend: a German citation style drops citations from level-1 sections.
const citationsHiddenFor = (formatJson, section) => {
  const cf = formatJson?.citationFormat;
  const style = typeof cf === 'string' ? cf : (cf?.style || '');
  return ['GERMAN', 'GERMANY'].includes(String(style).toUpperCase())
    && !String(section?.section_ordering_id ?? '').includes('.');
};

const AgentResponseSlot = React.memo(function AgentResponseSlot({
  agent, sectionKey, agentKey,
  onTranslate, onSaveResponse, onUpdateAgentField,
  onToggleParagraphDropdown, onCloseParagraphDropdown,
  referenceIndex, formatJson, fabricId, templateId,
  docIdToNameMap, docIdToThreadMap,
  showNoDocumentsNotice, citationsHidden = false, isSummarySection,
  paragraphStyleOptions, openParagraphDropdown, dropdownRef,
  canTranslate = false, canEditResponse = false, isLocked = false, canCreateReference = false,
  hasSectionManagementAccess = false, onEditingChange,
  onApplyWritingRules, writingRulesLabel = null, isStopping = false,
}) {
  const handleEditingChange = useCallback(
    (editing) => onEditingChange && onEditingChange(sectionKey, agentKey, editing),
    [onEditingChange, sectionKey, agentKey]
  );
  const agentDocs = useMemo(() => (
    (agent.document_mappings || [])
      .filter(m => m.selected !== false && docIdToNameMap[m.doc_id] != null)
      .map(m => ({
        id: m.doc_id,
        name: docIdToNameMap[m.doc_id],
        groupId: docIdToThreadMap[m.doc_id]?.threadId ?? null,
        groupName: docIdToThreadMap[m.doc_id]?.threadName ?? null,
        tags: docIdToThreadMap[m.doc_id]?.threadTags || [],
      }))
  ), [agent.document_mappings, docIdToNameMap, docIdToThreadMap]);

  const responseHeaderNotice = useMemo(() => ((showNoDocumentsNotice || citationsHidden) ? (
    <>
      {showNoDocumentsNotice && (
        <span className="ev-alert-card-warning ev-btn-yellow-outline-round genbi-no-doc-notice ev-bold-reg d-inline-flex align-items-center ms-1" style={{ whiteSpace: 'nowrap' }}>
          <i className="bi bi-exclamation-triangle-fill me-1"></i>
          No docs configured
        </span>
      )}
      {citationsHidden && (
        <span className="ev-alert-card-warning ev-btn-yellow-outline-round genbi-no-doc-notice ev-bold-reg d-inline-flex align-items-center ms-1 avatar-tooltip"
          style={{ whiteSpace: 'nowrap' }}
          data-tooltip="German citation style: citations are not shown in top-level sections">
          <i className="bi bi-info-circle-fill me-1"></i>
          Citations hidden
        </span>
      )}
    </>
  ) : null), [showNoDocumentsNotice, citationsHidden]);


  const isParagraphOpen = openParagraphDropdown === agentKey;

  const headerExtra = useMemo(() => {
    if (!paragraphStyleOptions.length || isSummarySection || !hasSectionManagementAccess) return null;
    return (
      <div ref={isParagraphOpen ? dropdownRef : null} className="position-relative d-inline-block">
        <button type="button" className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg border-0 avatar-tooltip"
          onClick={() => !isLocked && onToggleParagraphDropdown(agentKey)}
          disabled={isLocked}
          data-tooltip={isLocked ? 'Unlock agent to change style' : undefined}>
          <i className="bi bi-text-paragraph me-1"></i>
          Style
          <i className="bi bi-chevron-down ms-1"></i>
        </button>
        {isParagraphOpen && (
          <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2">
            <span className="ev-small-p-blue ev-bold-med px-1 mb-1">Select Paragraph Style</span>
            {paragraphStyleOptions.map((style) => {
              const isSelected = String(agent.par_style ?? '') === String(style.value ?? '');
              return (
                <button key={style.value ?? 'default'} type="button"
                  className={`par-style-item d-flex align-items-center ev-fs-12 py-2 px-2 ${isSelected ? 'active' : ''}`}
                  onClick={() => { onUpdateAgentField(sectionKey, agentKey, 'par_style', isSelected ? null : style.value); onCloseParagraphDropdown(); }}>
                  <i className={`bi ${isSelected ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                  {style.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [paragraphStyleOptions, isSummarySection, isParagraphOpen, dropdownRef, agent.par_style,
      sectionKey, agentKey, isLocked, onToggleParagraphDropdown, onCloseParagraphDropdown, onUpdateAgentField]);

  // Apply Writing Rules: only for projects whose country has rules, on a generated response
  const headerActions = useMemo(() => {
    if (!writingRulesLabel || !onApplyWritingRules || agent.status !== 'COMPLETED' || agent.is_static_text) return headerExtra;
    return (
      <>
        {headerExtra}
        <button type="button" className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg border-0 avatar-tooltip"
          onClick={() => onApplyWritingRules([agent.id])}
          disabled={isLocked}
          data-tooltip={isLocked ? 'Unlock agent to apply writing rules' : `Apply ${writingRulesLabel} writing rules`}>
          <i className="bi bi-spellcheck"></i>
        </button>
      </>
    );
  }, [headerExtra, writingRulesLabel, onApplyWritingRules, agent.status, agent.id, agent.is_static_text, isLocked]);

  return (
    <GenbiResponseComponent
      agent={agent}
      status={agent.status}
      onTranslate={onTranslate}
      isTranslateDisabled={agent.status !== 'COMPLETED'}
      canTranslate={canTranslate}
      canEditResponse={canEditResponse}
      isLocked={isLocked}
      canCreateReference={canCreateReference}
      onSaveResponse={onSaveResponse}
      referenceIndex={referenceIndex}
      formatJson={formatJson}
      fabricId={fabricId}
      templateId={templateId}
      agentDocs={agentDocs}
      responseHeaderNotice={responseHeaderNotice}
      headerExtra={headerActions}
      onEditingChange={handleEditingChange}
      isStopping={isStopping}
      isSpecialSection={isSummarySection}
    />
  );
});

const LoadingOverlay = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className="template-overlay">
      <div className="text-center">
        <div className="template-loader"></div>
      </div>
    </div>
  );
};

// ── Prompt Version Selector — mirrors VersionSelector.jsx structure exactly ──
const PromptVersionSelector = ({ promptVersions, selectedPromptVersion, onShow, onRestore, isLocked, isRunning = false, canRestore = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Only listen while open. One instance renders per agent, so registering
  // unconditionally put a capture-phase scroll listener on the window for every
  // agent in the template — hundreds of handlers firing on each scroll event.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleScroll = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => { setIsOpen(false); }, [promptVersions]);

  if (!promptVersions || promptVersions.length === 0) return null;
  const latestVersion = promptVersions[0];
  const activeVersion = selectedPromptVersion || latestVersion.version;

  return (
    <div ref={containerRef} className="d-inline-block position-relative ms-2">
      <button
        type="button"
        className="ev-message-card1 ev-radius-large d-flex align-items-center gap-0 prompt-version-trigger ms-1 mb-1 avatar-tooltip"
        onClick={() => !isLocked && setIsOpen(!isOpen)}
        data-tooltip={isRunning ? 'Agent is running' : isLocked ? 'Agent is locked' : undefined}
      >
        <span className="ev-fs-10 ev-reg-p-blue">Version&nbsp;</span>
        <span className="ev-fs-10 ev-reg-p-blue">{activeVersion}</span>
        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} ev-fs-9 ev-reg-p-blue ms-1`}></i>
      </button>
      {isOpen && (
        <div className="ev-card-4 position-absolute top-100 mt-2 p-2 z-1000" style={{ minWidth: '320px' }}>
          <div className="scrollable-element vh-20 p-1">
            {promptVersions.map(pv => {
              const versionName = `Prompt Version ${pv.version}`;
              const isLatest = pv.version === latestVersion.version;
              return (
                <div key={pv.version} className="ev-card-7 d-flex justify-content-between align-items-center mb-2 p-2 ev-radius-xsmall">
                  <span className="ev-small-p-dark me-1">{versionName}</span>
                  <div>
                    {canRestore && (
                      <button type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg" disabled={isLatest || isLocked}
                        onClick={() => { onRestore(pv.version, versionName); setIsOpen(false); }}>Restore</button>
                    )}
                    <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg ms-1" disabled={isLocked}
                      onClick={() => { onShow(pv.version); setIsOpen(false); }}>Show</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Kept alongside the viewer so the commented-report UI stays in this file.
const OriginalCommentsModal = ({ commentData, onClose }) => {
  const [expandedComments, setExpandedComments] = useState({});
  const comments = commentData?.annotations?.length
    ? commentData.annotations
    : (commentData?.original_comments || []).map(comment => ({ comment }));

  useEffect(() => {
    const handleKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="comment-success-modal-overlay" onClick={onClose}>
      <div className="comment-success-modal ev-message-card2" role="dialog" aria-modal="true"
        aria-label="Original comments" onClick={(event) => event.stopPropagation()}>
        <div className="comment-success-modal-header">
          <h5 className="comment-success-modal-title mb-0">
            <i className="bi bi-chat-left-text-fill"></i>
            Comments
            <span className="ev-message-card3">{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
          </h5>
          <button type="button" className="ev-btn-text-only-fill ev-fs-20" aria-label="Close" onClick={onClose}>
            <i className="bi bi-x"></i>
          </button>
        </div>
        <div className="comment-success-modal-body scrollable-element">
          {comments.length === 0 ? (
            <div className="no-comments-message">No original comments available</div>
          ) : comments.map((annotation, index) => {
            const selectedText = annotation.selected_text || '';
            const isLong = selectedText.length > 200;
            const expanded = !!expandedComments[index];
            const excerpt = selectedText.slice(0, 200).replace(/\s+\S*$/, '');
            return (
              <div key={annotation.comment_id ?? index} className="ev-card-8 mb-2">
                <p className="ev-small-p-blue ev-bold-reg mb-0">Comment {index + 1}</p>
                <div className="original-comment-item-inline comment-box-plain ev-bold-reg">{annotation.comment || 'No comment text included.'}</div>
                {selectedText && (
                  <div className="mt-2">
                    <p className="ev-small-p-blue mb-0">Selected text</p>
                    <div className="consolidated-feedback-text-inline comment-box-plain">{isLong && !expanded ? `${excerpt}…` : selectedText}</div>
                    {isLong && (
                      <button type="button" className="ev-btn-text-only-fill ev-fs-12 p-0 mt-1"
                        onClick={() => setExpandedComments(previous => ({ ...previous, [index]: !previous[index] }))}>
                        {expanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="comment-success-modal-footer">
          <button type="button" className="ev-btn-blue-fill-round ev-bold-reg" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Country names for the Apply Writing Rules tooltips (ISO alpha-3 -> name)
const WRITING_RULES_COUNTRY_NAMES = { DEU: 'German' };

// TEMP [STOP-DEBUG]: investigation of agents stuck on RUNNING in the UI. Remove once fixed.
const stopDebug = (...args) => console.log('[STOP-DEBUG]', new Date().toISOString().slice(11, 23), ...args);
const debugStatusesFromSections = (sections) => {
  const out = {};
  (sections || []).forEach(s => (s.agents || []).forEach(a => { if (a.id) out[a.id] = a.status; }));
  return out;
};
const debugStatusesFromDict = (sectionsDict) => {
  const out = {};
  Object.values(sectionsDict || {}).forEach(s => Object.values(s.agentsDict || {}).forEach(a => {
    if (a.id) out[a.id] = a._responsePending ? `${a.status}(pending)` : a.status;
  }));
  return out;
};

const GenbiTemplateTabViewer = ({
  templateId,
  templateName,
  fabricId,
  templateDataCache,
  setTemplateDataCache,
  onError,
  onProcessingStart,
  onProcessingEnd,
  onRefreshTemplateList,
  vmConfig,
  onTemplateNameSaved,
  langId,
  onAccessLost,
  runtimeStatus,
  registerRuntimeRefresh,
  onRuntimeWake,
  onRuntimeStale
}) => {
  const dispatch = useDispatch();
  const persistedExpandedSections = useSelector(state => state.genbi.tabExpandedSections[templateId]);
  const persistedRefAgentExpanded = useSelector(state => state.genbi.tabRefAgentExpanded[templateId]);
   const writingRulesPending = useSelector(state => state.genbi.tabWritingRulesPending[templateId]);
  const writingRulesNoChange = useSelector(state => state.genbi.tabWritingRulesNoChange[templateId]);

  const [formData, setFormData] = useState({
    template_name: '',
    fabric_id: null,
    fabric_name: '',
    sectionsDict: {},
    sectionOrder: [],
    vm_config: false,
    lang_id: 1
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!templateDataCache[templateId]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  const [fabricTreeData, setFabricTreeData] = useState([]);
  // Extend / Amend (bulk in "Modify & Pick Agents" and per agent in Agent Settings) are off
  // for some project countries (FabricsService.BULK_EXTEND_AMEND_HIDDEN_COUNTRIES, e.g.
  // Germany). Same project-tree check the Resubmission Dossier used; shown until the tree
  // says otherwise.
  const showBulkExtendAmend = !(
    formData.fabric_id &&
    fabricTreeData[0] &&
    String(fabricTreeData[0].id) === String(formData.fabric_id) &&
    fabricTreeData[0].bulk_extend_amend_enabled === false
  );
  // Project country with writing rules (country_rules/<ISO>.json in the consumers), or null.
  // Same project-tree check as showBulkExtendAmend.
  const writingRulesCountry = (
    formData.fabric_id &&
    fabricTreeData[0] &&
    String(fabricTreeData[0].id) === String(formData.fabric_id)
  ) ? (fabricTreeData[0].writing_rules_country || null) : null;
  const writingRulesLabel = WRITING_RULES_COUNTRY_NAMES[writingRulesCountry] || writingRulesCountry;
  const [tableStructures, setTableStructures] = useState([]);
  const [tableStructuresLoading, setTableStructuresLoading] = useState(true);
  const [documentSelectorOpen, setDocumentSelectorOpen] = useState(false);
  const [currentAgentIndex, setCurrentAgentIndex] = useState({ sectionKey: null, agentKey: null });
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  // "sectionKey::agentKey" -> sectionKey for every agent response open in edit mode.
  // The section list is virtualised; these sections are pinned in the rendered range
  // so scrolling them out of view doesn't unmount the editor and lose the edit.
  const [editingAgents, setEditingAgents] = useState({});
  const handleAgentEditingChange = useCallback((sectionKey, agentKey, editing) => {
    const id = `${sectionKey}::${agentKey}`;
    setEditingAgents(prev => {
      if (editing === (id in prev)) return prev;
      const next = { ...prev };
      if (editing) next[id] = sectionKey;
      else delete next[id];
      return next;
    });
  }, []);

  const [showRunConfirmation, setShowRunConfirmation] = useState(false);
  const [runType, setRunType] = useState('');
  // Apply Writing Rules: agent ids waiting for confirmation (null = modal closed)
  const [writingRulesAgentIds, setWritingRulesAgentIds] = useState(null);
  const [expandedSections, setExpandedSectionsLocal] = useState(persistedExpandedSections || {});

  // Ref agent selector expand state (persisted across modal open/close & tab switches)
  const [refAgentExpanded, setRefAgentExpandedLocal] = useState(persistedRefAgentExpanded || {});
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  // Wrappers: update local state only. React invokes state updaters during the
  // render phase, so dispatching Redux inside one notifies subscribers mid-render;
  // useSelector then forces another render, React re-invokes the pending updater,
  // it dispatches again — "Maximum update depth exceeded". The Redux sync happens
  // in the effects below instead, after the commit.
  const setExpandedSections = useCallback((updater) => {
    setExpandedSectionsLocal(prev => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const setRefAgentExpanded = useCallback((updater) => {
    setRefAgentExpandedLocal(prev => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  // Persist to Redux after commit, and only when the value actually changed for
  // the same template — otherwise switching tabs would write one tab's state
  // onto another, and an unchanged value would notify subscribers for nothing.
  const expandedSyncRef = useRef({ templateId, value: expandedSections });
  useEffect(() => {
    const prev = expandedSyncRef.current;
    expandedSyncRef.current = { templateId, value: expandedSections };
    if (prev.templateId !== templateId || prev.value === expandedSections) return;
    dispatch(setTabExpandedSections({ tabId: templateId, expandedSections }));
  }, [dispatch, templateId, expandedSections]);

  const refAgentSyncRef = useRef({ templateId, value: refAgentExpanded });
  useEffect(() => {
    const prev = refAgentSyncRef.current;
    refAgentSyncRef.current = { templateId, value: refAgentExpanded };
    if (prev.templateId !== templateId || prev.value === refAgentExpanded) return;
    dispatch(setTabRefAgentExpanded({ tabId: templateId, expandedSections: refAgentExpanded }));
  }, [dispatch, templateId, refAgentExpanded]);

  const [agentFeedback, setAgentFeedback] = useState({});
  const [agentGrounding, setAgentGrounding] = useState({});
  const [singleAgentExecution, setSingleAgentExecution] = useState({ agentId: null, feedback: null });
  const [agentQuickEdit, setAgentQuickEdit] = useState({}); // agentId -> bool (Quick Edit / non-CoT toggle)
  const [agentWarningCollapsed, setAgentWarningCollapsed] = useState({}); // agentId -> bool (feedback warning box collapsed)
  // Static text agents whose body is collapsed to the header row. UI-only, keyed `${sectionKey}:${agentKey}`.
  const [collapsedStaticAgents, setCollapsedStaticAgents] = useState({});
  const templateStateCache = useRef({});
  // Selection → feedback attachment (per-agent, keyed by agent.id)
  const [agentSelectedText, setAgentSelectedText] = useState({});   // agentId -> string
  const [agentSelectedContext, setAgentSelectedContext] = useState({}); // agentId -> string (e.g. "table cell (column \"X\", row \"Y\")")
  const [agentPreviewOpen, setAgentPreviewOpen] = useState({});   // agentId -> bool
  const [selectionFloatBtn, setSelectionFloatBtn] = useState(null); // { agentId, x, y, text } | null
  const responseWrapperRefs = useRef({});                            // agentId -> DOM node
  const [masterFeedback, setMasterFeedback] = useState('');

  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const scrollContainerRef = useRef(null);
  // Latest virtualiser + row keys, so handlers created earlier in this body can
  // scroll to a section that is not currently mounted.
  const virtualListRef = useRef({ keys: [], virtualizer: null });

  const savedExpandedStateRef = useRef({});

  // Frontend-only persistence for table/graph/image deletions. Keyed by agent id;
  // merged onto whatever the backend returns on every fetch, so a deletion sticks
  // regardless of whether /update-template + /get-template-details round-trip it back.
  const deletedItemsByAgentRef = useRef({});

  const applyPersistedDeletions = useCallback((sectionsDict) => {
    const persisted = deletedItemsByAgentRef.current;
    if (!persisted || Object.keys(persisted).length === 0) return sectionsDict;

    const nextSectionsDict = { ...sectionsDict };
    Object.keys(nextSectionsDict).forEach(sectionKey => {
      const section = nextSectionsDict[sectionKey];
      if (!section?.agentsDict) return;
      let sectionChanged = false;
      const nextAgentsDict = { ...section.agentsDict };
      Object.keys(nextAgentsDict).forEach(agentKey => {
        const agent = nextAgentsDict[agentKey];
        const record = agent?.id && persisted[agent.id];
        if (!record) return;
        sectionChanged = true;
        nextAgentsDict[agentKey] = {
          ...agent,
          editedResponse: {
            ...(agent.editedResponse || {}),
            ...(record.deleted_table_ids?.length && { deleted_table_ids: record.deleted_table_ids }),
            ...(record.deleted_graph_ids?.length && { deleted_graph_ids: record.deleted_graph_ids }),
            ...(record.deleted_image_ids?.length && { deleted_image_ids: record.deleted_image_ids }),
          }
        };
      });
      if (sectionChanged) {
        nextSectionsDict[sectionKey] = { ...section, agentsDict: nextAgentsDict };
      }
    });
    return nextSectionsDict;
  }, []);

  const [vmSelectorOpen, setVmSelectorOpen] = useState(false);
  const [currentSectionForVM, setCurrentSectionForVM] = useState(null);
  const [valueMessages, setValueMessages] = useState([]);
  // Routes this user holds on THIS template's project, from get-template-details.
  // Replaces the old /section-management-access probe, which only reported
  // whether the route was held on some project — so a role on an unrelated
  // project lit up the controls here. Empty until details load, so nothing is
  // offered early.
  const [capabilities, setCapabilities] = useState({});
  const canHere = capabilityChecker(capabilities);
  // Every section/agent edit ends in an update-template, so they all ride on it.
  const hasSectionManagementAccess = canHere(PERMISSIONS.TEMPLATE_UPDATE);
  // Section / Super Section / Special Section buttons: Developer permission set only
  // Also gates the Meta Tags block in the Agent Settings dropdown
  const canSectionManagement = canHere(PERMISSIONS.SECTION_MANAGEMENT);
  const canRunTemplate = canHere(PERMISSIONS.TEMPLATE_RUN);
  const [vmLoading, setVmLoading] = useState(false);
  const [vmSearch, setVmSearch] = useState("");
  const [vmSearchInput, setVmSearchInput] = useState("");
  const [vmPage, setVmPage] = useState(1);
  const [selectedVMIds, setSelectedVMIds] = useState([]);
  const [vmCategory, setVmCategory] = useState("");
  const [vmAddedBy, setVmAddedBy] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
  // Comment processing states
  const [isProcessingComments, setIsProcessingComments] = useState(false);
  const [commentProcessingMessage, setCommentProcessingMessage] = useState('');
  const [agentsWithComments, setAgentsWithComments] = useState(new Set());
  const [commentFeedbackData, setCommentFeedbackData] = useState({});
  const [showCommentSuccessModal, setShowCommentSuccessModal] = useState(false);
  const [commentProcessingStats, setCommentProcessingStats] = useState(null);
  const [commentFeedbackList, setCommentFeedbackList] = useState([]);
  const [commentPage, setCommentPage] = useState(1);
  const [showVerifyCommentsConfirmation, setShowVerifyCommentsConfirmation] = useState(false);
  const [verifyCommentsMode, setVerifyCommentsMode] = useState('all');
  const [verifyCommentsCount, setVerifyCommentsCount] = useState(0);
  const fileInputRef = useRef(null);
  const [commentNavExpanded, setCommentNavExpanded] = useState({});
  const [originalCommentsModalOpen, setOriginalCommentsModalOpen] = useState(false);
  const [selectedAgentForComments, setSelectedAgentForComments] = useState(null);
  const [showDiscardCommentModal, setShowDiscardCommentModal] = useState(false);
  const [discardCommentInfo, setDiscardCommentInfo] = useState(null);
  const [showDiscardAllCommentsModal, setShowDiscardAllCommentsModal] = useState(false);


  const [restoreVersionInfo, setRestoreVersionInfo] = useState({ isOpen: false, agentId: null, version: null, versionName: '', docsChanged: false, restoreDocs: false, versionDocIds: [], currentDocIds: [], docNameMap: {} });

  const [selectedVersions, setSelectedVersions] = useState({});
  const [openVersionDropdown, setOpenVersionDropdown] = useState(null);
  const [comparePanelState, setComparePanelState] = useState(null);
  // ── Prompt version state ──
  const [restorePromptVersionInfo, setRestorePromptVersionInfo] = useState({ isOpen: false, agentId: null, version: null, versionName: '' });
  const [selectedPromptVersions, setSelectedPromptVersions] = useState({});
  const [promptVersionPreviewAgents, setPromptVersionPreviewAgents] = useState(new Set());
  const [showPromptVersionWarning, setShowPromptVersionWarning] = useState(false);
  const [pendingPromptVersionShow, setPendingPromptVersionShow] = useState(null);
  const [translateDropdown, setTranslateDropdown] = useState(null);
  const [allLanguages, setAllLanguages] = useState([]);

  // FILTER & SELECTION ---
  const [agentFilter, setAgentFilter] = useState(['ALL']);
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [stoppingAgentIds, setStoppingAgentIds] = useState(new Set());
  const [stopConfirmIds, setStopConfirmIds] = useState(null);
  const [stopNotice, setStopNotice] = useState(null);
  const [quickFeedbackNotice, setQuickFeedbackNotice] = useState(null);
  const statusFenceRef = useRef(0);
  useEffect(() => {
    stopDebug('TemplateTabViewer MOUNT', { templateId });
    return () => stopDebug('TemplateTabViewer UNMOUNT', { templateId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);

  // Sync Docs Modal
  const [syncDocsModalOpen, setSyncDocsModalOpen] = useState(false);
  const [syncDocsChanges, setSyncDocsChanges] = useState([]);
  const [syncDocsLoading, setSyncDocsLoading] = useState(false);

  const [filterPickerOpen, setFilterPickerOpen] = useState(false);

  const selectDropdownRef = useRef(null);

  const prevTemplateIdRef = useRef(templateId);
  // Save/restore per-template state when switching between templates
  useEffect(() => {
    const prevId = prevTemplateIdRef.current;
    if (prevId !== templateId) {
      templateStateCache.current[prevId] = {
        agentFeedback, agentsWithComments, agentQuickEdit,
      };
      const cached = templateStateCache.current[templateId];
      setSelectedAgentIds(new Set());
      setAgentFeedback(cached?.agentFeedback || {});
      setAgentsWithComments(cached?.agentsWithComments || new Set());
      setAgentQuickEdit(cached?.agentQuickEdit || {});
      prevTemplateIdRef.current = templateId;
    }
  }, [templateId]);

  // Clear feedback/comment state when switching between templates
  // (prevents Address All / Discard All leaking into unrelated tabs)
  useEffect(() => {
    setAgentFeedback({});
    setAgentsWithComments(new Set());
    setCommentFeedbackData({});
    setCommentFeedbackList([]);
  }, [templateId]);

  // Click-outside + scroll handler for select dropdown
  useEffect(() => {
    if (!selectDropdownOpen) return undefined;
    const handleClickOutside = (e) => {
      if (selectDropdownRef.current && !selectDropdownRef.current.contains(e.target)) {
        setSelectDropdownOpen(false);
      }
    };
    const handleScroll = (e) => {
      if (selectDropdownRef.current && selectDropdownRef.current.contains(e.target)) return;
      setSelectDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [selectDropdownOpen]);

  const isAgentVisible = (agent) => {
    if (!agent) return false;
    const status = agent.status || 'PENDING';
    if (agentFilter.includes('ALL')) return true;
    if (agentFilter.includes('PENDING') && (status === 'PENDING' || status === 'NOT_BEGAN' || status === '')) return true;
    return agentFilter.includes(status);
  };


  const isSectionVisibleUnderFilter = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;
    if (agentFilter.includes('ALL')) return true;

    // Check direct agents in this section
    const hasVisibleAgent = section.agentOrder.some(agentKey => {
      return isAgentVisible(section.agentsDict[agentKey]);
    });
    if (hasVisibleAgent) return true;

    // Check child sections
    const childSections = formData.sectionOrder.filter(key => {
      const child = formData.sectionsDict[key];
      return child && child.parent_section_id === sectionKey;
    });

    return childSections.some(childKey => isSectionVisibleUnderFilter(childKey));
  };

  const getVisibleAgentIdsInSection = (sectionKey) => {
    let ids = [];
    const section = formData.sectionsDict[sectionKey];
    if (!section) return ids;

    section.agentOrder.forEach(agentKey => {
      const agent = section.agentsDict[agentKey];
      // only include agents that have an ID (saved to DB) and are visible
      if (agent && agent.id && isAgentVisible(agent)) {
        ids.push(agent.id);
      }
    });

    const childSections = formData.sectionOrder.filter(key => formData.sectionsDict[key]?.parent_section_id === sectionKey);
    childSections.forEach(childKey => {
      ids = [...ids, ...getVisibleAgentIdsInSection(childKey)];
    });
    return ids;
  };
  const getAllAgentIdsInTemplate = () => {
    const ids = [];
    formData.sectionOrder.forEach(sectionKey => {
      const section = formData.sectionsDict[sectionKey];
      if (!section) return;
      section.agentOrder.forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id) ids.push(agent.id);
      });
    });
    return ids;
  };
  const handleSectionCheckbox = (sectionKey, checked) => {
    const visibleIds = getVisibleAgentIdsInSection(sectionKey);
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      visibleIds.forEach(id => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const toggleAgentSelection = (agentId) => {
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  const selectAllVisible = () => {
    const next = new Set(selectedAgentIds);
    formData.sectionOrder.forEach(sectionKey => {
      // Start from root sections to avoid duplicating logic
      if (!formData.sectionsDict[sectionKey]?.parent_section_id) {
        const ids = getVisibleAgentIdsInSection(sectionKey);
        ids.forEach(id => next.add(id));
      }
    });
    setSelectedAgentIds(next);
  };

  const deselectAllVisible = () => {
    const next = new Set(selectedAgentIds);
    formData.sectionOrder.forEach(sectionKey => {
      if (!formData.sectionsDict[sectionKey]?.parent_section_id) {
        const ids = getVisibleAgentIdsInSection(sectionKey);
        ids.forEach(id => next.delete(id));
      }
    });
    setSelectedAgentIds(next);
  };

  const toggleFilter = (value) => {
    setAgentFilter(prev => {
      if (value === 'ALL') return ['ALL'];
      let updated = prev.filter(v => v !== 'ALL');
      if (updated.includes(value)) updated = updated.filter(v => v !== value);
      else updated.push(value);
      return updated.length ? updated : ['ALL'];
    });
  };

  const handleSelectAll = useCallback(() => {
    selectAllVisible();
    setSelectDropdownOpen(false);
  }, [selectedAgentIds, formData.sectionOrder, formData.sectionsDict, agentFilter]);

  const handleDeselectAll = useCallback(() => {
    deselectAllVisible();
    setSelectDropdownOpen(false);
  }, [selectedAgentIds, formData.sectionOrder, formData.sectionsDict, agentFilter]);

  const handleStatusSelection = useCallback((status, add) => {
    if (status === 'ALL') {
      add ? selectAllVisible() : deselectAllVisible();
      return;
    }
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      Object.values(formData.sectionsDict).forEach(sec => {
        if (sec?.autogen_after) return;
        sec?.agentOrder?.forEach(aKey => {
          const ag = sec.agentsDict[aKey];
          if (ag?.id) {
            const st = ag.status || 'PENDING';
            const match = status === 'PENDING'
              ? (st === 'PENDING' || st === 'NOT_BEGAN' || st === '')
              : st === status;
            if (match) add ? next.add(ag.id) : next.delete(ag.id);
          }
        });
      });
      return next;
    });
  }, [formData.sectionsDict, selectAllVisible, deselectAllVisible]);

  const [isGenReportModalVisible, setIsGenReportModalVisible] = useState(false)
  const [isAbbrevModalVisible, setIsAbbrevModalVisible] = useState(false)
  const [isContextMappingDocSelectorOpen, setIsContextMappingDocSelectorOpen] = useState(false);
  const [activePromptTab, setActivePromptTab] = useState({});
  // Holds the open agent's key, or null. (Was initialised to {}, which is
  // truthy but never equal to any agentKey — it already behaved as "closed".)
  const [openParagraphDropdown, setOpenParagraphDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [openAgentSettingsDropdown, setOpenAgentSettingsDropdown] = useState(null);
  const settingsDropdownRef = useRef(null);
  // Meta Tags popover inside the Settings dropdown — holds the open agent's key.
  const [openAgentTagPopover, setOpenAgentTagPopover] = useState(null);
  const tagPopoverRef = useRef(null);
  const [openSpecialSectionConfig, setOpenSpecialSectionConfig] = useState(null);
  const specialSectionConfigRef = useRef(null);
  const [openSectionTypeDropdown, setOpenSectionTypeDropdown] = useState(null);
  const sectionTypeDropdownRef = useRef(null);

  const [templateData, setTemplateData] = useState({
    agent_count: 0,
    completed_agents: 0,
    running_agents: 0,
    failed_agents: 0,
    pending_agents: 0,
    status: '',
    context_mapping_status: ''
  });
  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [currentAgentConfig, setCurrentAgentConfig] = useState({
    sectionKey: null,
    agentKey: null,
  });

  const COMMENTS_PER_PAGE = 5;

  // Expand/Collapse All
  const getExpandableSectionKeys = () => {
    return (formData.sectionOrder || []).filter((k) => hasAnyContent(k));
  };

  const areAllExpandableExpanded = () => {
    const keys = getExpandableSectionKeys();
    if (keys.length === 0) return false;
    return keys.every((k) => expandedSections[k]);
  };
  // Was a function called once per agent during render; now computed once.
  const paragraphStyleOptions = useMemo(() => {
    const paragraphConfigs = Array.isArray(formData.format_json?.paragraph)
      ? formData.format_json.paragraph
      : [];

    const customParagraphs = paragraphConfigs.filter(
      (p) => (p?.id || "default") !== "default"
    );

    return customParagraphs.map((p, index) => ({
      label: p.name || `Paragraph (Style ${index + 1})`,
      value: p.level ?? (index + 1),
    }));
  }, [formData.format_json]);

  const toggleParagraphDropdown = (agentKey) => {
    setOpenParagraphDropdown((prev) => (prev === agentKey ? null : agentKey));
  };

  const closeParagraphDropdown = () => {
    setOpenParagraphDropdown(null);
  };

  // Each of these listens only while its dropdown is open. Registered
  // unconditionally they ran on every mousedown/scroll in the template.
  useEffect(() => {
    if (!openParagraphDropdown) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeParagraphDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openParagraphDropdown]);

  useEffect(() => {
    if (!openAgentSettingsDropdown) return undefined;
    const handleSettingsClickOutside = (event) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setOpenAgentSettingsDropdown(null);
        setOpenAgentTagPopover(null);
      }
    };
    document.addEventListener("mousedown", handleSettingsClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleSettingsClickOutside);
    };
  }, [openAgentSettingsDropdown]);

  // Same contract as the Settings dropdown, one level in: a click anywhere
  // outside the pill panel (including elsewhere in the dropdown) closes it.
  useEffect(() => {
    if (!openAgentTagPopover) return undefined;
    const handleTagClickOutside = (event) => {
      if (tagPopoverRef.current && !tagPopoverRef.current.contains(event.target)) {
        setOpenAgentTagPopover(null);
      }
    };
    document.addEventListener("mousedown", handleTagClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleTagClickOutside);
    };
  }, [openAgentTagPopover]);

  const toggleSectionTypeDropdown = (sectionKey) => {
    setOpenSectionTypeDropdown((prev) => (prev === sectionKey ? null : sectionKey));
  };

  const closeSectionTypeDropdown = () => {
    setOpenSectionTypeDropdown(null);
  };

  useEffect(() => {
    if (!openSectionTypeDropdown) return undefined;
    const handleClickOutsideSectionType = (event) => {
      if (sectionTypeDropdownRef.current && !sectionTypeDropdownRef.current.contains(event.target)) {
        closeSectionTypeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutsideSectionType);
    return () => document.removeEventListener("mousedown", handleClickOutsideSectionType);
  }, [openSectionTypeDropdown]);

  // Special Section Config click-outside. The scroll handler here had no guard
  // at all, so every scroll event set state and re-rendered the whole template.
  useEffect(() => {
    if (!openSpecialSectionConfig) return undefined;
    const handleSpecialConfigClickOutside = (event) => {
      if (specialSectionConfigRef.current && !specialSectionConfigRef.current.contains(event.target)) {
        setOpenSpecialSectionConfig(null);
      }
    };
    const handleSpecialConfigScroll = () => setOpenSpecialSectionConfig(null);
    document.addEventListener("mousedown", handleSpecialConfigClickOutside);
    document.addEventListener("scroll", handleSpecialConfigScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleSpecialConfigClickOutside);
      document.removeEventListener("scroll", handleSpecialConfigScroll, true);
    };
  }, [openSpecialSectionConfig]);

  const handleToggleExpandCollapseAll = () => {
    const keys = getExpandableSectionKeys();
    if (keys.length === 0) return;

    const shouldExpand = !keys.every((k) => expandedSections[k]); // if not all expanded -> expand
    setExpandedSections((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = shouldExpand;
      });
      return next;
    });
  };

  // Expand/Collapse all static text agents. Independent of the section toggle above:
  // that one hides whole sections, this one only folds static agent bodies.
  const staticAgentCollapseKeys = useMemo(() => {
    const keys = [];
    (formData.sectionOrder || []).forEach((sectionKey) => {
      const section = formData.sectionsDict?.[sectionKey];
      (section?.agentOrder || []).forEach((agentKey) => {
        if (section.agentsDict?.[agentKey]?.is_static_text) keys.push(`${sectionKey}:${agentKey}`);
      });
    });
    return keys;
  }, [formData.sectionOrder, formData.sectionsDict]);

  const areAllStaticAgentsCollapsed =
    staticAgentCollapseKeys.length > 0 && staticAgentCollapseKeys.every((k) => collapsedStaticAgents[k]);

  const handleToggleAllStaticAgents = () => {
    const collapse = !areAllStaticAgentsCollapsed;
    setCollapsedStaticAgents((prev) => {
      const next = { ...prev };
      staticAgentCollapseKeys.forEach((k) => { next[k] = collapse; });
      return next;
    });
  };
  // Distinguish user edit from api edits
  const isUserEditRef = useRef(false);
  const savedTemplateFingerprintRef = useRef(null);
  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/genbi';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Flat doc_id -> doc_name lookup, used to build each agent's "agentDocs"
  // (Documents tab in the citation @-mention dropdown).
  const docIdToNameMap = useMemo(() => {
    const map = {};
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        thread.documents?.forEach(doc => {
          map[doc.id] = doc.document_name;
        });
      });
    });
    return map;
  }, [fabricTreeData]);

  // doc_id -> parent thread/collection (same grouping DocumentSelector's
  // "Collections" view uses), so the citation Documents tab can group the
  // same way.
  const docIdToThreadMap = useMemo(() => {
    const map = {};
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        thread.documents?.forEach(doc => {
          map[doc.id] = { threadId: thread.id, threadName: thread.name, threadTags: thread.tags || [] };
        });
      });
    });
    return map;
  }, [fabricTreeData]);

  // The meta-tag vocabulary offered in Agent Settings: every tag on the Data
  // Folders of this project, deduped case-insensitively (first spelling wins).
  // fabricTreeData is already this project's tree and each thread carries .tags,
  // so there is nothing extra to fetch. "unclassified" is dropped — the tag ->
  // document query behind Sync Docs can never match it.
  const templateTags = useMemo(() => {
    const seen = new Map();
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        (thread.tags || []).forEach(tag => {
          const name = (tag || '').trim();
          const key = name.toLowerCase();
          if (!name || key === 'unclassified' || seen.has(key)) return;
          seen.set(key, name);
        });
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [fabricTreeData]);

  const openVMSelector = (sectionKey) => {
    setCurrentSectionForVM(sectionKey);
    setVmSelectorOpen(true);
    fetchValueMessages();
  };

  const handleVmSearchInputChange = (e) => {
    setVmSearchInput(e.target.value);
  };

  const handleVmSearch = () => {
    setVmSearch(vmSearchInput);
    setVmPage(1);
  };

  const handleVmKeyPress = (e) => {
    if (e.key === "Enter") {
      handleVmSearch();
    }
  };
  const buildCommentNavigationStructure = () => {
    const sectionsDict = formData.sectionsDict || {};
    const sectionOrder = formData.sectionOrder || [];
    const childrenOf = {};
    sectionOrder.forEach(key => {
      const parent = sectionsDict[key]?.parent_section_id;
      if (parent && sectionsDict[parent]) (childrenOf[parent] = childrenOf[parent] || []).push(key);
    });

    const buildNode = (sectionKey) => {
      const section = sectionsDict[sectionKey];
      if (!section) return null;
      const agents = (section.agentOrder || [])
        .map(agentKey => ({ agentKey, agent: section.agentsDict?.[agentKey] }))
        .filter(({ agent }) => agent && agentsWithComments.has(agent.id))
        .map(({ agentKey, agent }) => ({
          agentKey,
          agentId: agent.id,
          agentName: agent.agent_name || `Agent ${agent.agent_ordering_id}`,
          sectionOrdering: section.section_ordering_id,
          agentOrdering: agent.agent_ordering_id,
          commentData: commentFeedbackData[agent.id]
        }))
        .sort((a, b) => a.agentOrdering - b.agentOrdering);
      const subsections = (childrenOf[sectionKey] || [])
        .map(buildNode)
        .filter(Boolean)
        .sort((a, b) => naturalCompare(a.sectionOrdering, b.sectionOrdering));
      if (agents.length === 0 && subsections.length === 0) return null;
      return {
        sectionKey,
        sectionName: section.section_name,
        sectionOrdering: section.section_ordering_id || '',
        agents,
        subsections
      };
    };

    const structure = {};
    sectionOrder.forEach(key => {
      const parent = sectionsDict[key]?.parent_section_id;
      if (parent && sectionsDict[parent]) return;
      const node = buildNode(key);
      if (node) structure[key] = node;
    });
    return structure;
  };

  const countAgentsInSection = (sectionData) =>
    sectionData.agents.length +
    sectionData.subsections.reduce((sum, sub) => sum + countAgentsInSection(sub), 0);

  const renderCommentNavAgent = (agent) => (
    <div key={agent.agentId} className="comment-nav-agent-wrapper">
      <div
        className="comment-nav-agent-item"
        onClick={() => toggleCommentNavSection(`agent-${agent.agentId}`)}
        style={{ cursor: 'pointer' }}
      >
        <i className={`bi ${commentNavExpanded[`agent-${agent.agentId}`] ? 'bi-chevron-down' : 'bi-chevron-right'} comment-nav-agent-chevron`}></i>
        <i className="bi bi-robot comment-nav-agent-icon"></i>
        <span className='ev-small-p-blue'>
          {agent.sectionOrdering}.{agent.agentOrdering} - {agent.agentName}
        </span>
        <span className="ms-auto comment-count-badge-sm">
          {agent.commentData?.comment_count || 0} comments
        </span>
      </div>

      {commentNavExpanded[`agent-${agent.agentId}`] && agent.commentData && (
        <div className="comment-nav-agent-details">
          <div className="ev-small-p-blue consolidated-feedback-section">
            <p className="ev-small-p-blue">Consolidated Feedback:</p>
            <div className="consolidated-feedback-text-inline">
              {agent.commentData.consolidated_feedback}
            </div>
          </div>

          {agent.commentData.original_comments && agent.commentData.original_comments.length > 0 && (
            <div className="ev-small-p-blue original-comments-section">
              <p className="ev-small-p-blue">Original comments ({agent.commentData.comment_count}):</p>
              {agent.commentData.original_comments.map((comment, idx) => (
                <div key={idx} className="original-comment-item-inline">
                  "{comment}"
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCommentNavSubsection = (subsection) => (
    <div key={subsection.sectionKey}>
      <div className="ev-small-p-blue comment-nav-subsection-item">
        Section {subsection.sectionOrdering}: {subsection.sectionName}
      </div>
      {subsection.agents.map(renderCommentNavAgent)}
      {subsection.subsections.length > 0 && (
        <div className="comment-nav-subsection">
          {subsection.subsections.map(renderCommentNavSubsection)}
        </div>
      )}
    </div>
  );

  // Add function to handle opening original comments modal
  const openOriginalCommentsModal = (sectionKey, agentKey) => {
    setSelectedAgentForComments({ sectionKey, agentKey });
    setOriginalCommentsModalOpen(true);
  };

  // Add function to toggle section expansion in navigation
  const toggleCommentNavSection = (sectionKey) => {
    setCommentNavExpanded(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const fetchValueMessages = async () => {
    setVmLoading(true);
    try {
      const requestBody = {
        template_id: templateId,
        fabric_id: fabricId,
        page: 1,
        size: 1000
      };

      if (vmCategory) {
        requestBody.category = vmCategory;
      }
      if (vmAddedBy) {
        requestBody.added_by = String(vmAddedBy).toUpperCase();
      }

      const response = await axios.post(
        `${API_BASE_URL}/value-message/list-value-messages`,
        requestBody,
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to load value messages');
        return;
      }

      setValueMessages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching value messages:', error);
      onError(error.message || 'Failed to load value messages');
    } finally {
      setVmLoading(false);
    }
  };

  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

  const handleVMSelection = (sectionKey, selectedVMIds) => {
    isUserEditRef.current = true;
    setFormData(prev => ({
      ...prev,
      sectionsDict: {
        ...prev.sectionsDict,
        [sectionKey]: {
          ...prev.sectionsDict[sectionKey],
          vm_mappings: selectedVMIds
        }
      }
    }));
    setVmSelectorOpen(false);
    setCurrentSectionForVM(null);
    setHasPendingChanges(true);
  };

  useEffect(() => {
    if (vmSelectorOpen && currentSectionForVM) {
      const section = formData.sectionsDict[currentSectionForVM];
      setSelectedVMIds(section?.vm_mappings || []);
    }
  }, [vmSelectorOpen, currentSectionForVM]);

  const getSelectedVMCount = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    return section?.vm_mappings?.length || 0;
  };

  const saveCurrentState = () => {
    if (scrollContainerRef.current) {
      setSavedScrollPosition(scrollContainerRef.current.scrollTop);
    }

    savedExpandedStateRef.current = { ...expandedSections };
  };
  /**
* Open discard comment modal
*/
  const openDiscardCommentModal = (sectionKey, agentKey) => {
    const section = formData.sectionsDict[sectionKey];
    const agent = section?.agentsDict[agentKey];

    if (!agent) return;

    const commentData = getCommentData(sectionKey, agentKey);
    const feedback = getAgentFeedback(sectionKey, agentKey);
    const quickEdit = agent.status === 'COMPLETED' && !agent._promptModified && !!agentQuickEdit[agent.id];
    if (quickEdit && (!feedback || !feedback.trim())) {
      onError('Enter an exact change in Feedback, or switch to Thinking.');
      return;
    }
    setDiscardCommentInfo({
      sectionKey,
      agentKey,
      agentId: agent.id,
      agentName: agent.agent_name || `Agent ${agent.agent_ordering_id}`,
      sectionName: section.section_name,
      sectionOrdering: section.section_ordering_id,
      agentOrdering: agent.agent_ordering_id,
      consolidatedFeedback: feedback,
      originalComments: commentData?.original_comments || [],
      commentCount: commentData?.comment_count || 0
    });

    setShowDiscardCommentModal(true);
  };

  /**
   * Execute discard comment
   */
  const executeDiscardComment = () => {
    if (!discardCommentInfo) return;

    const { sectionKey, agentKey, agentId } = discardCommentInfo;

    // Clear feedback for this agent
    setAgentFeedback(prev => {
      const newFeedback = { ...prev };
      delete newFeedback[agentId];
      return newFeedback;
    });

    // Remove from agents with comments
    setAgentsWithComments(prev => {
      const newSet = new Set(prev);
      newSet.delete(agentId);
      return newSet;
    });

    // Remove from comment feedback data
    setCommentFeedbackData(prev => {
      const newData = { ...prev };
      delete newData[agentId];
      return newData;
    });

    // Remove from comment feedback list
    setCommentFeedbackList(prev =>
      prev.filter(item => item.agentId !== agentId)
    );

    // Close modal and reset
    setShowDiscardCommentModal(false);
    setDiscardCommentInfo(null);
  };
  // Add this useEffect after comment processing completes
  useEffect(() => {
    if (showCommentSuccessModal && commentFeedbackList.length > 0) {
      // Auto-expand all parent sections on modal open
      const structure = buildCommentNavigationStructure();
      const autoExpandState = {};

      Object.keys(structure).forEach(sectionKey => {
        autoExpandState[sectionKey] = true; // Expand parent sections by default
      });

      setCommentNavExpanded(autoExpandState);
    }
  }, [showCommentSuccessModal]);

  const restoreCurrentState = () => {
    if (Object.keys(savedExpandedStateRef.current).length > 0) {
      setExpandedSections(savedExpandedStateRef.current);
    }
    setTimeout(() => {
      if (scrollContainerRef.current && savedScrollPosition > 0) {
        scrollContainerRef.current.scrollTop = savedScrollPosition;
      }
    }, 100);
  };

  const updateAgentFeedback = (sectionKey, agentKey, feedback) => {
    const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
    if (agentId) {
      setAgentFeedback(prev => ({
        ...prev,
        [agentId]: feedback
      }));
    }
  };

  const getAgentFeedback = (sectionKey, agentKey) => {
    const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
    return agentId ? agentFeedback[agentId] || '' : '';
  };
  const getSelectionContext = (sel, wrapper) => {
    if (!sel || sel.rangeCount === 0) return null;
    const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '');

    const range = sel.getRangeAt(0);
    let startNode = range.startContainer;
    if (startNode.nodeType === Node.TEXT_NODE) startNode = startNode.parentNode;
    if (!startNode || !wrapper.contains(startNode)) return null;

    // Walk up
    let cursor = startNode;
    while (cursor && cursor !== wrapper && cursor.nodeType === Node.ELEMENT_NODE) {
      const tag = cursor.tagName;

      if (tag === 'TABLE') {
        // Find the cell we're in
        const cell = (startNode.closest && startNode.closest('td, th'));
        if (cell && cursor.contains(cell)) {
          const row = cell.parentElement;
          const cellIdx = Array.from(row.children).indexOf(cell);
          // Column header: first row's cell at same index (works for <thead> or <tbody> tables)
          const headerRow = cursor.querySelector('thead tr') || cursor.querySelector('tr');
          const headerCell = headerRow && headerRow.children[cellIdx];
          const columnLabel = headerCell && headerCell !== cell
            ? headerCell.innerText.trim()
            : null;
          // Row label: first cell of the row (if it's not the selected cell itself)
          const firstCell = row.children[0];
          const rowLabel = firstCell && firstCell !== cell
            ? firstCell.innerText.trim()
            : null;
          const parts = [];
          if (columnLabel) parts.push(`column "${truncate(columnLabel, 60)}"`);
          if (rowLabel) parts.push(`row "${truncate(rowLabel, 60)}"`);
          return `table cell${parts.length ? ' (' + parts.join(', ') + ')' : ''}`;
        }
        return 'table';
      }

      if (/^H[1-6]$/.test(tag)) return `heading (${tag.toLowerCase()})`;
      if (tag === 'LI') return 'list item';
      if (tag === 'PRE' || tag === 'CODE') return 'code block';
      if (tag === 'BLOCKQUOTE') return 'quote';

      cursor = cursor.parentNode;
    }
    return null; // plain paragraph text — no special context
  };

  // A plain click produces a collapsed selection, so the clear below runs on
  // every mouseup. Returning the previous value when it is already null lets
  // React bail out instead of re-rendering the whole template each click.
  const clearSelectionFloatBtn = useCallback(() => {
    setSelectionFloatBtn(prev => (prev === null ? prev : null));
  }, []);

  const handleResponseMouseUp = (agentId) => () => {
    if (agentsWithComments.has(agentId)) { clearSelectionFloatBtn(); return; }
    // Defer so getSelection() reflects the just-completed selection
    setTimeout(() => {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.isCollapsed) { clearSelectionFloatBtn(); return; }

      const text = sel.toString().trim();
      if (!text) { clearSelectionFloatBtn(); return; }

      const wrapper = responseWrapperRefs.current[agentId];
      if (!wrapper) return;

      // Only accept selections that started inside this wrapper
      const anchor = sel.anchorNode;
      if (!anchor || !wrapper.contains(anchor)) return;

      const anchorEl = anchor.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor;
      if (anchorEl && anchorEl.closest('[contenteditable="true"]')) {
        clearSelectionFloatBtn();
        return;
      }

      // ...but not inside the References/Cross References card — that's a
      // summary of citations, not response text, so it shouldn't be quotable
      // into feedback.
      if (anchorEl && anchorEl.closest('[data-genbi-no-feedback-select]')) {
        clearSelectionFloatBtn();
        return;
      }

      try {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const wrapRect = wrapper.getBoundingClientRect();
        const context = getSelectionContext(sel, wrapper);
        setSelectionFloatBtn({
          agentId,
          x: rect.left - wrapRect.left + rect.width / 2,
          y: rect.top - wrapRect.top,   // button sits above via CSS translate
          text,
          context,
        });
      } catch (_) {
        clearSelectionFloatBtn();
      }
    }, 0);
  };

  const attachSelectionToFeedback = () => {
    if (!selectionFloatBtn) return;
    const { agentId, text, context } = selectionFloatBtn;
    if (agentsWithComments.has(agentId)) { clearSelectionFloatBtn(); return; }
    setAgentSelectedText(prev => ({ ...prev, [agentId]: text }));
    setAgentSelectedContext(prev => ({ ...prev, [agentId]: context || null }));
    setAgentPreviewOpen(prev => ({ ...prev, [agentId]: false })); // start closed
    clearSelectionFloatBtn();
    try { window.getSelection().removeAllRanges(); } catch (_) { }
  };

  const togglePreviewOpen = (agentId) => {
    setAgentPreviewOpen(prev => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const removeSelectionAttachment = (agentId) => {
    setAgentSelectedText(prev => { const n = { ...prev }; delete n[agentId]; return n; });
    setAgentSelectedContext(prev => { const n = { ...prev }; delete n[agentId]; return n; });
    setAgentPreviewOpen(prev => { const n = { ...prev }; delete n[agentId]; return n; });
  };

  const clearSelectionAttachments = (agentIds) => {
    const ids = Array.isArray(agentIds) ? agentIds : [agentIds];
    setAgentSelectedText(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
    setAgentSelectedContext(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
    setAgentPreviewOpen(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  };

  /** Clear commented-report state for agents that have run. */
  const clearCommentState = (agentIds) => {
    const ids = Array.isArray(agentIds) ? agentIds : [agentIds];
    setAgentsWithComments(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
    setCommentFeedbackData(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  };

  /** Clear feedback, selection and commented-report state for agents whose response changed. */
  const clearAgentFeedbackState = (agentIds) => {
    const ids = Array.isArray(agentIds) ? agentIds : [agentIds];
    setAgentFeedback(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
    clearSelectionAttachments(ids);
    clearCommentState(ids);
  };

  /** Drop per-agent feedback state for agents no longer in the loaded template. */
  const pruneAgentStateToTemplate = (sectionsDict) => {
    const liveIds = new Set();
    Object.values(sectionsDict || {}).forEach(section => {
      Object.values(section?.agentsDict || {}).forEach(agent => {
        if (agent?.id) liveIds.add(String(agent.id));
      });
    });
    const keepLive = (prev) => {
      const stale = Object.keys(prev).filter(id => !liveIds.has(id));
      if (stale.length === 0) return prev;
      const next = { ...prev };
      stale.forEach(id => delete next[id]);
      return next;
    };
    setAgentFeedback(keepLive);
    setAgentSelectedText(keepLive);
    setAgentSelectedContext(keepLive);
    setAgentPreviewOpen(keepLive);
    setCommentFeedbackData(keepLive);
    setAgentsWithComments(prev => {
      const stale = [...prev].filter(id => !liveIds.has(String(id)));
      if (stale.length === 0) return prev;
      const next = new Set(prev);
      stale.forEach(id => next.delete(id));
      return next;
    });
  };

  /** Merge attached selection with typed feedback for submission. */
  const getAgentById = (agentId) => {
    for (const sKey of (formData.sectionOrder || [])) {
      const section = formData.sectionsDict?.[sKey];
      for (const aKey of (section?.agentOrder || [])) {
        const ag = section.agentsDict?.[aKey];
        if (ag?.id === agentId) return ag;
      }
    }
    return null;
  };

  const getAgentPersistRunOptions = (agent) => {
    const preserve = !!agent?.preserve_response;
    const amend = !!agent?.amend_response;
    return {
      preserve_response: preserve,
      amend_response: amend,
      match_mapped_style: amend && !!agent?.match_mapped_style,
      response_colour: (preserve || amend) ? (agent?.response_colour || null) : null,
    };
  };

  /** Distinct commented texts from the uploaded report for an agent. */
  const getReportAnnotationTexts = (agentId) => {
    if (!agentsWithComments.has(agentId)) return [];
    const annotations = commentFeedbackData[agentId]?.annotations || [];
    const texts = annotations
      .map(a => (a?.selected_text || '').trim())
      .filter(Boolean);
    return [...new Set(texts)];
  };

  const buildFinalFeedback = (agentId, typed) => {
    const reportTexts = getReportAnnotationTexts(agentId);
    if (reportTexts.length > 0) {
      const regarding = reportTexts.length === 1
        ? `Regarding this text: "${reportTexts[0]}"`
        : reportTexts.map((t, i) => `Regarding this text (${i + 1}): "${t}"`).join('\n');
      return `${regarding}\n\nChange requested: ${typed || ''}`;
    }
    const attached = agentSelectedText[agentId];
    const context = agentSelectedContext[agentId];
    if (attached && attached.trim()) {
      const prefix = context
        ? `Regarding this text from a ${context}: "${attached}"`
        : `Regarding this text: "${attached}"`;
      return `${prefix}\n\nChange requested: ${typed || ''}`;
    }
    return typed || '';
  };

  const isLatestManualResponse = (agent) => {
    const latestSource = agent?.versions?.[0]?.source || '';
    return agent?.status === 'COMPLETED' && latestSource === 'manual';
  };

  const handleRunSingleAgent = (sectionKey, agentKey) => {
    const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
    if (!agent || !agent.id) return;

    if (promptVersionPreviewAgents.has(agent.id)) {
      setPendingPromptVersionShow({ agentId: agent.id, sectionKey, agentKey });
      setShowPromptVersionWarning(true);
      return;
    }

    const feedback = getAgentFeedback(sectionKey, agentKey);
    const quickEdit = agent.status === 'COMPLETED' && !agent._promptModified && !!agentQuickEdit[agent.id];
    const finalFeedback = buildFinalFeedback(agent.id, feedback);
    if (quickEdit && (!feedback || !feedback.trim())) {
      onError('Enter an exact change in Feedback, or switch to Thinking.');
      return;
    }
    const runOptions = getAgentPersistRunOptions(agent);
    setSingleAgentExecution({ agentId: agent.id, feedback: finalFeedback, use_feedback_editor: quickEdit, ...runOptions });
    setRunType('single_agent');
    setShowRunConfirmation(true);
  };
  /**
 * Get section ordering ID for an agent
 */
  const getAgentSectionOrdering = (agentId) => {
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      if (!section) continue;

      for (const agentKey of section.agentOrder || []) {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id === agentId) {
          return section.section_ordering_id;
        }
      }
    }
    return null;
  };

  /**
   * Get agents that reference (depend on) the given agent ID (reverse lookup)
   * Returns array of strings like ["S.3. Agent 1", "S.4. Agent 2"]
   */
  const getReferencedByAgentLabels = (agentId) => {
    const labels = [];
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      if (!section) continue;
      for (const agentKey of section.agentOrder || []) {
        const agent = section.agentsDict[agentKey];
        if (!agent) continue;
        // Check if this agent's reference_agent_ids contains our agentId
        if (agent.reference_agent_ids && agent.reference_agent_ids.includes(agentId)) {
          const sectionOrdering = section.section_ordering_id || '';
          const agentOrdering = agent.agent_ordering_id || 1;
          labels.push(`S.${sectionOrdering}. Agent ${agentOrdering}`);
        }
      }
    }
    return labels;
  };

  /**
   * Handle file upload for commented document
   */
  const handleCommentedDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'docx') {
      onError('Only DOCX files are supported for comment addressing');
      event.target.value = '';
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      onError('File size exceeds 50MB limit. Please upload a smaller document.');
      event.target.value = '';
      return;
    }

    // Process the file
    await processCommentedDocument(file);

    // Clear file input for re-upload
    event.target.value = '';
  };

  /**
   * Main function to process uploaded commented document
   */
  /**
 * Execute discard all comments
 */
  const executeDiscardAllComments = () => {
    // Clear all feedback
    setAgentFeedback({});

    // Clear agents with comments
    setAgentsWithComments(new Set());

    // Clear comment data
    setCommentFeedbackData({});

    // Clear comment feedback list
    setCommentFeedbackList([]);

    // Close modal
    setShowDiscardAllCommentsModal(false);
  };
  const processCommentedDocument = async (file) => {
    setIsProcessingComments(true);
    setCommentProcessingMessage('Uploading document...');
    onProcessingStart('Processing comments from document. This may take 2-3 minutes...');

    try {
      // Step 1: ask the API where this file goes, and get a signed URL for it
      const urlResponse = await axios.post(
        `${API_BASE_URL}/comments/upload-url`,
        {
          template_id: templateId,
          fabric_id: fabricId,
          file_name: file.name,
          file_size_mb: file.size / (1024 * 1024)
        },
        { headers }
      );

      if (urlResponse.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (urlResponse.data.status === 'failed') {
        onError(urlResponse.data.message || 'Failed to start the upload');
        return;
      }

      const { presigned_url: presignedUrl, s3_key: s3Key, content_type: contentType } = urlResponse.data.data;

      // Step 2: the document goes straight to S3, never through the API
      setCommentProcessingMessage('Uploading to cloud storage...');

      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': contentType }
      });

      setCommentProcessingMessage('Extracting comments and mapping to agents...');

      // Step 3: process the uploaded copy - the backend deletes it when it is done
      const response = await axios.post(
        `${API_BASE_URL}/comments/upload-and-process`,
        {
          template_id: templateId,
          fabric_id: fabricId,
          s3_key: s3Key
        },
        { headers }
      );

      // Handle authentication
      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      // Handle failure
      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to process comments');
        return;
      }

      setCommentProcessingMessage('Consolidating feedback using AI...');

      // Extract data
      const { agent_feedbacks, total_agents, total_comments, processing_time } = response.data.data;

      // Handle no comments case
      if (!agent_feedbacks || Object.keys(agent_feedbacks).length === 0) {
        onError('No comments found in the document. Please add comments using Word and try again.');
        return;
      }

      // Update state with feedback
      const newAgentFeedback = {};
      const newCommentData = {};
      const agentsSet = new Set();
      const feedbackList = [];

      Object.entries(agent_feedbacks).forEach(([agentId, data]) => {
        const agentIdInt = parseInt(agentId);
        newAgentFeedback[agentIdInt] = data.consolidated_feedback;
        newCommentData[agentIdInt] = data;
        agentsSet.add(agentIdInt);

        // Build feedback list for modal
        feedbackList.push({
          agentId: agentIdInt,
          sectionOrdering: getAgentSectionOrdering(agentIdInt),
          consolidatedFeedback: data.consolidated_feedback,
          originalComments: data.original_comments || [],
          commentCount: data.comment_count || 0
        });
      });

      // Merge with existing feedback (don't overwrite manual feedback)
      setAgentFeedback(prevFeedback => ({
        ...prevFeedback,
        ...newAgentFeedback
      }));

      setCommentFeedbackData(newCommentData);
      setAgentsWithComments(agentsSet);
      clearSelectionAttachments(Array.from(agentsSet));
      setCommentFeedbackList(feedbackList);
      setCommentPage(1);

      // Store stats for success modal
      setCommentProcessingStats({
        total_agents,
        total_comments,
        processing_time
      });

      // Show success modal
      setShowCommentSuccessModal(true);

    } catch (error) {
      console.error('Error processing commented document:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to process commented document';
      onError(errorMessage);
    } finally {
      setIsProcessingComments(false);
      setCommentProcessingMessage('');
      onProcessingEnd();
    }
  };

  /**
   * Scroll to the first agent with comments
   */
  const scrollToFirstCommentedAgent = (agentsSet) => {
    if (!agentsSet || agentsSet.size === 0) return;

    // Find first section and agent with comments
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      if (!section || !section.agentOrder) continue;

      for (const agentKey of section.agentOrder) {
        const agent = section.agentsDict[agentKey];
        if (agent && agentsSet.has(agent.id)) {
          // Expand section if collapsed
          setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: true
          }));

          const scrollToAgent = (attemptsLeft) => {
            const agentElement = document.querySelector(`[data-agent-id="${agent.id}"]`);
            if (agentElement) {
              agentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              return;
            }
            if (attemptsLeft > 0) setTimeout(() => scrollToAgent(attemptsLeft - 1), 100);
          };

          setTimeout(() => {
            const { keys, virtualizer } = virtualListRef.current;
            const index = keys.indexOf(sectionKey);
            if (index >= 0 && virtualizer) {
              virtualizer.scrollToIndex(index, { align: 'start' });
            }
            scrollToAgent(10);
          }, 300);

          return;
        }
      }
    }
  };

  /**
   * Check if an agent has comments
   */
  const hasComments = (sectionKey, agentKey) => {
    const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
    return agent && agentsWithComments.has(agent.id);
  };

  /**
   * Get comment data for an agent
   */
  const getCommentData = (sectionKey, agentKey) => {
    const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
    return agent ? commentFeedbackData[agent.id] : null;
  };

  /**
   * Clear all comments
   */
  const clearAllComments = () => {
    if (agentsWithComments.size === 0) return;

    const confirmClear = window.confirm(
      `This will clear feedback for ${agentsWithComments.size} agent(s). Are you sure?`
    );

    if (!confirmClear) return;

    // Clear agents with comments
    setAgentsWithComments(new Set());
    setCommentFeedbackData({});

    // Clear feedback for commented agents
    setAgentFeedback(prev => {
      const newFeedback = { ...prev };
      agentsWithComments.forEach(agentId => {
        delete newFeedback[agentId];
      });
      return newFeedback;
    });
  };

  /**
   * Trigger file input click programmatically
   */
  const triggerCommentUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   Helper to count selected agents that have feedback
   */
  const getSelectedAgentsWithFeedbackCount = () => {
    return Array.from(selectedAgentIds).filter(id => agentFeedback[id] && agentFeedback[id].trim()).length;
  };

  /**
   * Verify and address comments - handles both ALL and SELECTED
   */
  const handleVerifyAndAddressComments = async (selectedOnly = false) => {
    const agentsToRun = [];

    Object.entries(agentFeedback).forEach(([agentId, feedback]) => {
      const idInt = parseInt(agentId);
      if (feedback && feedback.trim()) {
        if (!selectedOnly || selectedAgentIds.has(idInt)) {
          const agent = getAgentById(idInt);
          // static-text agents never take feedback
          if (agent?.is_locked || agent?.is_static_text) return;
          agentsToRun.push({ agentId: idInt, feedback });
        }
      }
    });

    if (agentsToRun.length === 0) {
      onError(selectedOnly ? 'No selected agents have feedback to address' : 'No eligible agents with feedback found');
      return;
    }

    setVerifyCommentsMode(selectedOnly ? 'selected' : 'all');
    setVerifyCommentsCount(agentsToRun.length);
    setShowVerifyCommentsConfirmation(true);
  };

  /**
   * Execute verify and address comments after confirmation
   */
  const executeVerifyAndAddressComments = async () => {
    setShowVerifyCommentsConfirmation(false);

    const agentsToRun = [];
    Object.entries(agentFeedback).forEach(([agentId, feedback]) => {
      const idInt = parseInt(agentId);
      if (feedback && feedback.trim()) {
        if (verifyCommentsMode !== 'selected' || selectedAgentIds.has(idInt)) {
          const agent = getAgentById(idInt);
          // static-text agents never take feedback
          if (agent?.is_locked || agent?.is_static_text) return;
          agentsToRun.push({ agentId: idInt, feedback });
        }
      }
    });

    if (agentsToRun.length === 0) return;

    if (onProcessingStart) {
      onProcessingStart(`Addressing feedback for ${agentsToRun.length} agent(s)...`);
    }

    try {
      // Save preset if there are pending changes
      if (hasPendingChanges) {
        await performSaveTemplate();
      }

      // --- API CALL LOGIC ---
      // Backend expects: agents: [{ agent_id, feedback, preserve_response, amend_response, response_colour }]
      const agentsPayload = agentsToRun.map(a => {
        const finalFeedback = buildFinalFeedback(a.agentId, a.feedback);
        return {
          agent_id: a.agentId,
          feedback: finalFeedback && finalFeedback.trim() ? finalFeedback.trim() : null,
          ...getAgentPersistRunOptions(getAgentById(a.agentId))
        };
      });

      // Single API Call instead of sequential loops
      const response = await axios.post(
        `${API_BASE_URL}/run-template`,
        {
          template_id: templateId,
          fabric_id: fabricId,
          master_feedback: null,
          agents: agentsPayload
        },
        { headers }
      );
      statusFenceRef.current = Date.now();

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        onError(response.data.message || `Failed to address comments`);
        return;
      }

      snapshotRunInputs(agentsToRun.map(a => a.agentId));
      // Clear feedback ONLY for the agents that were successfully executed
      setAgentFeedback(prev => {
        const newFeedback = { ...prev };
        agentsToRun.forEach(a => delete newFeedback[a.agentId]);
        return newFeedback;
      });

      setAgentsWithComments(prev => {
        const newSet = new Set(prev);
        agentsToRun.forEach(a => newSet.delete(a.agentId));
        return newSet;
      });

      setCommentFeedbackData(prev => {
        const newData = { ...prev };
        agentsToRun.forEach(a => delete newData[a.agentId]);
        return newData;
      });

      clearSelectionAttachments(agentsToRun.map(a => a.agentId));

      // If we ran selected agents, deselect them after running
      if (verifyCommentsMode === 'selected') {
        setSelectedAgentIds(prev => {
          const newSet = new Set(prev);
          agentsToRun.forEach(a => newSet.delete(a.agentId));
          return newSet;
        });
      }

      await handleRefresh();
      if (onRefreshTemplateList) {
        onRefreshTemplateList();
      }

    } catch (error) {
      console.error('Error addressing comments:', error);
      onError(error.message || 'Failed to address comments');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  /**
   * Check if section has feedback (directly or in children)
   */
  // Section-tree rollups, computed once per formData/comment change.
  // These replace recursive helpers that ran per section during render:
  // isParentOfFeedbackSection looped every section and ran a recursive
  // sectionHasFeedback for each, which itself re-filtered sectionOrder at every
  // level — effectively O(sections^3) on every render of a large template.
  const sectionRollups = useMemo(() => {
    const sectionsDict = formData.sectionsDict || {};
    const sectionOrder = formData.sectionOrder || [];

    const childrenOf = {};
    sectionOrder.forEach(key => {
      const parent = sectionsDict[key]?.parent_section_id;
      if (parent) (childrenOf[parent] = childrenOf[parent] || []).push(key);
    });

    const ownFeedback = {};
    const ownRunning = {};
    sectionOrder.forEach(key => {
      const section = sectionsDict[key];
      if (!section) { ownFeedback[key] = false; ownRunning[key] = false; return; }
      const agentKeys = section.agentOrder || [];
      ownFeedback[key] = agentKeys.some(ak => {
        const agent = section.agentsDict?.[ak];
        return !!agent && agentsWithComments.has(agent.id);
      });
      ownRunning[key] = agentKeys.some(ak => {
        const agent = section.agentsDict?.[ak];
        return !!agent && agent.status === 'RUNNING';
      });
    });

    // Roll a per-section flag up through descendants in one pass.
    const rollUp = (own) => {
      const out = {};
      const visit = (key) => {
        if (out[key] !== undefined) return out[key];
        out[key] = false; // guards a malformed parent cycle
        let result = !!own[key];
        (childrenOf[key] || []).forEach(kid => { if (visit(kid)) result = true; });
        out[key] = result;
        return result;
      };
      sectionOrder.forEach(visit);
      return out;
    };

    const hasFeedback = rollUp(ownFeedback);
    const hasRunning = rollUp(ownRunning);

    // "Parent of a feedback section" = some strict descendant has feedback.
    // A child's rollup already covers everything below it, so checking direct
    // children is equivalent to the original full-descendant scan.
    const isParentOfFeedback = {};
    sectionOrder.forEach(key => {
      isParentOfFeedback[key] = (childrenOf[key] || []).some(kid => hasFeedback[kid]);
    });

    const hasContent = {};
    sectionOrder.forEach(key => {
      const section = sectionsDict[key];
      if (!section) { hasContent[key] = false; return; }
      hasContent[key] = (childrenOf[key] || []).length > 0
        || (Array.isArray(section.agentOrder) && section.agentOrder.length > 0);
    });

    return { hasFeedback, hasRunning, isParentOfFeedback, hasContent };
  }, [formData.sectionsDict, formData.sectionOrder, agentsWithComments]);

  // Both keep the original "unknown section is never highlighted" guard.
  const sectionHasFeedback = (sectionKey) =>
    !!formData.sectionsDict[sectionKey] && !!sectionRollups.hasFeedback[sectionKey];

  /**
   * Check if section is a parent of a section with feedback
   */
  const isParentOfFeedbackSection = (sectionKey) =>
    !!formData.sectionsDict[sectionKey] && !!sectionRollups.isParentOfFeedback[sectionKey];

  const convertSectionsToDict = (sectionsArray) => {
    const sectionsDict = {};
    const sectionOrder = [];

    sectionsArray.forEach(section => {
      const key = section.id || section.temp_id || `temp_${Date.now()}_${Math.random()}`;

      const agentsDict = {};
      const agentOrder = [];

      if (section.agents) {
        section.agents.forEach(agent => {
          const agentKey = agent.id || agent.temp_id || `agent_temp_${Date.now()}_${Math.random()}`;
          agentsDict[agentKey] = {
            ...agent,
            reference_agent_ids: agent.reference_agent_ids || [],
            _savedAgentContext: agent.agent_context || '',
            _savedAdditionalContext: agent.additional_context || '',
            _promptModified: false,
            _modifiedOutsidePrompt: false
          };
          if (!agent.isDeleted) {
            agentOrder.push(agentKey);
          }
        });
      }

      const oid = section.section_ordering_id || '';
      const isSPrefix = oid.startsWith('S') || oid.startsWith('s');

      sectionsDict[key] = {
        ...section,
        is_summary_section: section.is_summary_section || isSPrefix,
        agentsDict,
        agentOrder,
        vm_mappings: section.vm_mappings || []
      };

      if (!section.isDeleted) {
        sectionOrder.push(key);
      }
    });

    return { sectionsDict, sectionOrder };
  };

  const convertToStructuredSections = (sectionsDict, sectionOrder) => {
    const structured = {
      new: [],
      modified: [],
      deleted: []
    };
    // Collect deleted sections ONCE (outside the loop to avoid N duplicates)
    Object.values(sectionsDict).forEach(section => {
      if (section.isDeleted && section.id) {
        structured.deleted.push({
          id: section.id,
        });
      }
    });

    sectionOrder.forEach(sectionKey => {
      const section = sectionsDict[sectionKey];

      const agents = section.agentOrder.map(agentKey => {
        const agent = section.agentsDict[agentKey];
        const isDirty = !agent.id || agent.isModified === true;
        return {
          id: agent.id,
          temp_id: agent.temp_id,
          agent_name: agent.agent_name || '',
          agent_context: agent.agent_context || '',
          additional_context: agent.additional_context || null,
          is_locked: agent.is_locked || false,
          preserve_response: agent.preserve_response ?? false,
          amend_response: agent.amend_response ?? false,
          match_mapped_style: (agent.amend_response && agent.match_mapped_style) ?? false,
          amend_version: agent.amend_version === 2 ? 2 : 1, // AMEND 2 (temporary)
          response_colour: agent.response_colour ?? null,
          rich_formatting: agent.rich_formatting ?? false,
          agent_ordering_id: agent.agent_ordering_id || 1,
          document_mappings: agent.document_mappings || [],
          reference_agent_ids: agent.reference_agent_ids || [],
          par_style: agent.par_style ?? null,
          table_structure_id: agent.table_structure_id ?? null,
          is_static_text: agent.table_structure_id ? false : (agent.is_static_text ?? false),
          static_text: agent.static_text ?? null,
          is_dirty: isDirty,
          explicit_delete: false,
          tags: agent.tags || null,
          _editedResponse: agent.editedResponse || null
        };
      });

      Object.keys(section.agentsDict).forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent.isDeleted && agent.id) {
          agents.push({
            id: agent.id,
            agent_name: agent.agent_name || '',
            agent_context: agent.agent_context || '',
            additional_context: agent.additional_context || null,
            is_locked: agent.is_locked || false,
            agent_ordering_id: agent.agent_ordering_id || 1,
            document_mappings: agent.document_mappings || [],
            reference_agent_ids: agent.reference_agent_ids || [],
            par_style: agent.par_style ?? null,
            is_dirty: false,
            explicit_delete: true,
            tags: agent.tags || null
          });
        }
      });

      const sectionData = {
        id: section.id,
        temp_id: section.temp_id,
        section_name: section.section_name || '',
        parent_section_id: section.parent_section_id,
        section_ordering_id: section.section_ordering_id || '1',
        is_summary_section: section.is_summary_section || false,
        // null unless special: CHECK (is_summary_section OR special_section_config IS NULL)
        special_section_config: section.is_summary_section ? (section.special_section_config || null) : null,
        autogen_after: section.autogen_after || null,
        vm_mappings: section.vm_mappings || [],
        agents: agents
      };

      if (section.id) {
        structured.modified.push(sectionData);
      } else {
        structured.new.push(sectionData);
      }
    });

    return structured;
  };
  const buildTemplateFingerprint = (data) => {
    const structuredSections = convertToStructuredSections(data.sectionsDict, data.sectionOrder);
    const normalizeSections = (sections) => sections.map(section => ({
      ...section,
      agents: section.agents.map(({ is_dirty, ...agent }) => ({
        ...agent,
        document_mappings: (agent.document_mappings || [])
          .filter(mapping => mapping.selected !== false)
          .sort((a, b) => String(a.doc_id).localeCompare(String(b.doc_id)))
      }))
    }));

    return JSON.stringify({
      template_name: data.template_name || '',
      fabric_id: data.fabric_id ?? null,
      vm_config: !!data.vm_config,
      lang_id: data.lang_id || 1,
      global_prompt: data.global_prompt || '',
      format_json: data.format_json || null,
      sections: {
        new: normalizeSections(structuredSections.new),
        modified: normalizeSections(structuredSections.modified),
        deleted: structuredSections.deleted
      }
    });
  };
  const generateOrderingId = (parentKey, sectionsDict, sectionOrder) => {
    if (!parentKey) {
      // Exclude summary sections (S-prefix) from numeric ordering calculation
      const rootSections = sectionOrder.filter(key => {
        const section = sectionsDict[key];
        return section && !section.parent_section_id && !section.is_summary_section;
      });
      const maxNumber = rootSections.reduce((max, key) => {
        const section = sectionsDict[key];
        const orderingId = section?.section_ordering_id || '0';
        const num = parseInt(orderingId.split('.')[0]);
        return Math.max(max, isNaN(num) ? 0 : num);
      }, 0);
      const fmt = formData.format_json?.numberingFormat;
      const startVal = (fmt === "none" || fmt === undefined) ? (formData.format_json?.numberingStartValue ?? 1) : 1;
      return String(Math.max(maxNumber + 1, startVal));
    } else {
      const parentSection = sectionsDict[parentKey];
      if (!parentSection) return '1';

      const parentOrderingId = parentSection.section_ordering_id;
      const siblings = sectionOrder.filter(key => {
        const section = sectionsDict[key];
        return section && section.parent_section_id === parentKey;
      });

      const maxChildNumber = siblings.reduce((max, key) => {
        const section = sectionsDict[key];
        const orderingId = section?.section_ordering_id || `${parentOrderingId}.0`;
        const parts = orderingId.split('.');
        const childNum = parseInt(parts[parts.length - 1]);
        return Math.max(max, isNaN(childNum) ? 0 : childNum);
      }, 0);

      return `${parentOrderingId}.${maxChildNumber + 1}`;
    }
  };

  const findAllDescendants = (parentKey, sectionsDict, sectionOrder) => {
    const descendants = [];

    if (!sectionsDict[parentKey]) return descendants;

    const directChildren = sectionOrder.filter(key => {
      const section = sectionsDict[key];
      return section && section.parent_section_id === parentKey;
    });

    directChildren.forEach(childKey => {
      descendants.push(childKey);
      const grandChildren = findAllDescendants(childKey, sectionsDict, sectionOrder);
      descendants.push(...grandChildren);
    });

    return descendants;
  };

  const hasChildren = (sectionKey) => !!sectionRollups.hasContent[sectionKey];

  const hasAnyContent = (sectionKey) => {
    return hasChildren(sectionKey);
  };

  const toggleSectionExpansion = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const isSectionVisible = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section || !section.parent_section_id) return true;

    const parent = formData.sectionsDict[section.parent_section_id];
    if (!parent) return true;

    return expandedSections[section.parent_section_id] && isSectionVisible(section.parent_section_id);
  };

  const isAgentRunning = (agent) => {
    return agent.status === 'RUNNING';
  };

  const hasRunningAgents = (sectionKey) =>
    !!formData.sectionsDict[sectionKey] && !!sectionRollups.hasRunning[sectionKey];

  const handleRunConfirmation = (type) => {
    setSingleAgentExecution({ agentId: null, feedback: null });
    setRunType(type);
    setShowRunConfirmation(true);
  };


  /** Agents the writing rules can run on: generated (COMPLETED) and not locked. */
  const getWritingRulesEligibleIds = (agentIds) =>
    agentIds.filter(id => {
      const ag = getAgentById(id);
      return ag && ag.status === 'COMPLETED' && !ag.is_locked && !ag.is_static_text;
    });

  const handleApplyWritingRules = (agentIds) => {
    const eligible = getWritingRulesEligibleIds(agentIds);
    if (eligible.length === 0) {
      onError('Writing rules apply to generated responses only. Select at least one completed, unlocked agent.');
      return;
    }
    setWritingRulesAgentIds(eligible);
  };

  const executeWritingRules = async () => {
    const agentIds = writingRulesAgentIds || [];
    setWritingRulesAgentIds(null);
    if (agentIds.length === 0) return;

    if (onProcessingStart) {
      onProcessingStart(agentIds.length === 1
        ? 'Applying writing rules'
        : `Applying writing rules to ${agentIds.length} agents`);
    }
    try {
      if (hasPendingChanges) {
        await performSaveTemplate();
      }
      // Own endpoint, never /run-template: this must not start an agent run
      const payload = { template_id: templateId, fabric_id: fabricId, agent_ids: agentIds };
      const response = await axios.post(`${API_BASE_URL}/apply-writing-rules`, payload, { headers });
      statusFenceRef.current = Date.now();

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to apply writing rules');
        return;
      }
       const versionsAtApply = {};
      // Only agents the backend accepted; request_id is echoed back only on a real "no changes"
      const requestId = response.data.request_id;
      (response.data.agent_ids || agentIds).forEach(id => {
        versionsAtApply[id] = { version: getAgentById(id)?.versions?.[0]?.version ?? 0, requestId };
      });
      dispatch(setWritingRulesPending({ tabId: templateId, versions: versionsAtApply }));
      setSelectedAgentIds(prev => {
        const next = new Set(prev);
        agentIds.forEach(id => next.delete(id));
        return next;
      });
      clearAgentFeedbackState(agentIds);
      onRuntimeWake?.();
      await handleRefresh();
      if (onRefreshTemplateList) {
        onRefreshTemplateList();
      }
    } catch (error) {
      console.error('Error applying writing rules:', error);
      onError(error.message || 'Failed to apply writing rules');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };
  const postRunTemplate = async (payload, isSingleAgent) => {
    if (!isSingleAgent) {
      return await axios.post(`${API_BASE_URL}/run-template`, payload, { headers });
    }

    // Single-agent runs are streamed by the backend (heartbeat while the
    // LLM call is in flight, to survive gateway idle timeouts) — read with
    // fetch instead of axios, same pattern as handleTranslateAgent.
    const res = await fetch(`${API_BASE_URL}/run-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: headers.Authorization || headers.authorization
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line) continue;
        const parsed = JSON.parse(line);
        if (parsed.status === 'running') continue; // heartbeat only
        finalResult = parsed;
      }
    }

    return { data: finalResult };
  };

  const runInputSnapshotRef = useRef({});
  const snapshotRunInputs = (agentIds) => {
    const takenAt = Date.now();
    (agentIds || []).filter(Boolean).forEach(id => {
      runInputSnapshotRef.current[id] = {
        feedback: agentFeedback[id],
        comments: commentFeedbackData[id],
        hadComments: agentsWithComments.has(id),
        takenAt,
      };
    });
  };

  const dropFinishedRunSnapshots = (statusById, observedAt) => {
    statusById.forEach((status, id) => {
      const snap = runInputSnapshotRef.current[id];
      if (snap && status !== 'RUNNING' && observedAt > snap.takenAt) {
        delete runInputSnapshotRef.current[id];
      }
    });
  };
  const restoreRunInputs = (agentIds) => {
    const snaps = (agentIds || [])
      .map(id => [id, runInputSnapshotRef.current[id]])
      .filter(([, snap]) => snap);
    if (snaps.length === 0) return;
    snaps.forEach(([id]) => delete runInputSnapshotRef.current[id]);
    setAgentFeedback(prev => {
      const next = { ...prev };
      snaps.forEach(([id, snap]) => { if (snap.feedback && !next[id]) next[id] = snap.feedback; });
      return next;
    });
    setCommentFeedbackData(prev => {
      const next = { ...prev };
      snaps.forEach(([id, snap]) => { if (snap.comments && !next[id]) next[id] = snap.comments; });
      return next;
    });
    setAgentsWithComments(prev => {
      const next = new Set(prev);
      snaps.forEach(([id, snap]) => { if (snap.hadComments) next.add(id); });
      return next;
    });
  };

  const applyAgentStatuses = (statusById, stoppedIds = new Set(), debugSource = 'unknown') => {
    stopDebug('applyAgentStatuses called', { source: debugSource, incoming: Object.fromEntries(statusById), stoppedIds: [...stoppedIds] });
    if (statusById.size === 0) return;
    setFormData(prev => {
      const debugChanged = {};
      const debugUnmatched = new Set(statusById.keys());
      let sectionsDict = null;
      Object.entries(prev.sectionsDict || {}).forEach(([sectionKey, section]) => {
        let agentsDict = null;
        Object.entries(section.agentsDict || {}).forEach(([agentKey, agent]) => {
          if (agent.id) debugUnmatched.delete(agent.id);
          if (!agent.id || !statusById.has(agent.id) || agent.status === statusById.get(agent.id)) return;
          const status = statusById.get(agent.id);
          debugChanged[agent.id] = `${agent.status} -> ${status}`;
          const next = { ...agent, status };
          if (status === 'COMPLETED' && (!stoppedIds.has(agent.id) || !agent.response)) {
            next._responsePending = true;
          }
          agentsDict = agentsDict || { ...section.agentsDict };
          agentsDict[agentKey] = next;
        });
        if (agentsDict) {
          sectionsDict = sectionsDict || { ...prev.sectionsDict };
          sectionsDict[sectionKey] = { ...section, agentsDict };
        }
      });
      stopDebug('applyAgentStatuses result', { source: debugSource, changed: debugChanged, notInForm: [...debugUnmatched] });
      return sectionsDict ? { ...prev, sectionsDict } : prev;
    });
  };

  const closeStopNotice = () => {
    setStopNotice(null);
    onRuntimeWake?.();
    handleRefresh();
  };

  const stopAgentRun = async (agentIds) => {
    const ids = (agentIds || []).filter(Boolean);
    if (ids.length === 0) return null;
    setStoppingAgentIds(prev => new Set([...prev, ...ids]));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/stop-agent-run`,
        { template_id: templateId, fabric_id: fabricId, agent_ids: ids },
        { headers }
      );
      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return null;
      }
      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to stop the agent run');
        return null;
      }
      statusFenceRef.current = Date.now();
      stopDebug('stop-agent-run response', { requested: ids, data: response.data, fence: statusFenceRef.current });
      const reported = response.data.agents || [];
      applyAgentStatuses(
        new Map(reported.map(a => [a.agent_id, a.status])),
        new Set(reported.filter(a => a.stopped).map(a => a.agent_id)),
        'stop-response'
      );
      const summary = response.data.template;
      if (summary) {
        setTemplateData(prev => ({
          ...prev,
          agent_count: summary.agent_count,
          completed_agents: summary.completed_agents,
          running_agents: summary.running_agents,
          failed_agents: summary.failed_agents,
          pending_agents: summary.pending_agents,
          status: deriveTemplateStatus(summary),
        }));
      }
      restoreRunInputs(response.data.stopped_agent_ids);
      const alreadyFinished = reported.filter(a => !a.stopped && a.status !== 'RUNNING');
      if (alreadyFinished.length > 0) {
        const single = alreadyFinished.length === 1;
        const allCompleted = alreadyFinished.every(a => a.status === 'COMPLETED');
        setStopNotice(allCompleted
          ? {
            title: single ? 'Agent already completed' : `${alreadyFinished.length} agents already completed`,
            message: single
              ? 'This agent finished before it could be stopped. Close this message to load its latest response, or run it again.'
              : 'These agents finished before they could be stopped. Close this message to load their latest responses, or run them again.',
          }
          : {
            title: single ? 'Agent was not running' : `${alreadyFinished.length} agents were not running`,
            message: 'There was nothing to stop. Close this message to load the latest state.',
          });
      }
      onRuntimeWake?.();
      if (onRefreshTemplateList) {
        onRefreshTemplateList();
      }
      return response.data;
    } catch (error) {
      console.error('Error stopping agent run:', error);
      onError(error.message || 'Failed to stop the agent run');
      return null;
    } finally {
      setStoppingAgentIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const executeRunTemplate = async () => {
    setShowRunConfirmation(false);

    const isSelectiveRun = runType === 'selective_run';
    const runAgentId = singleAgentExecution.agentId;

    if (onProcessingStart) {
      onProcessingStart(
        runType === 'single_agent' ? 'Running agent' :
          isSelectiveRun ? `Running ${selectedAgentIds.size} selected agents` :
            'Running preset'
      );
    }

    try {
      if (hasPendingChanges) {
        await performSaveTemplate();
      }

      // Base payload config
      const payload = {
        template_id: templateId,
        fabric_id: fabricId,
        master_feedback: masterFeedback.trim() || null
      };

      // Handle specifics based on run type
      if (runType === 'single_agent') {
        payload.agent_id = singleAgentExecution.agentId || null;
        payload.feedback = singleAgentExecution.feedback || null;
        payload.enable_grounding_check =
          !!(singleAgentExecution.agentId && agentGrounding[singleAgentExecution.agentId]);
        payload.preserve_response = !!singleAgentExecution.preserve_response;
        payload.amend_response = !!singleAgentExecution.amend_response;
        payload.match_mapped_style =
          !!singleAgentExecution.amend_response && !!singleAgentExecution.match_mapped_style;
        payload.response_colour =
          (singleAgentExecution.preserve_response || singleAgentExecution.amend_response)
            ? (singleAgentExecution.response_colour || null)
            : null;
        payload.use_feedback_editor = !!singleAgentExecution.use_feedback_editor;
      } else if (isSelectiveRun) {
        // If all agents in template are selected, treat as full template run
        // (no agents array → backend routes to run_genbi_template)
        const allIds = getAllAgentIdsInTemplate();
        const isFullRun = selectedAgentIds.size === allIds.length &&
          allIds.every(id => selectedAgentIds.has(id));

        if (!isFullRun) {
          // Backend expects: agents: [{ agent_id, feedback, enable_grounding_check,
          // preserve_response, amend_response, response_colour, use_feedback_editor }]
          payload.agents = Array.from(selectedAgentIds).map(agentId => {
            // a static-text agent only runs its first generation - never with feedback
            const fb = getAgentById(agentId)?.is_static_text ? '' : buildFinalFeedback(agentId, agentFeedback[agentId]);
            return {
              agent_id: agentId,
              feedback: fb && fb.trim() ? fb.trim() : null,
              enable_grounding_check: !!agentGrounding[agentId],
              ...getAgentPersistRunOptions(getAgentById(agentId))
            };
          });
        }
      } else {
        const hasAnyGrounding = Object.values(agentGrounding).some(Boolean);
        if (hasAnyGrounding) {
          const allAgents = [];
          (formData.sectionOrder || []).forEach(sKey => {
            const section = formData.sectionsDict?.[sKey];
            (section?.agentOrder || []).forEach(aKey => {
              const agent = section.agentsDict?.[aKey];
              if (agent?.id) {
                const fb = buildFinalFeedback(agent.id, agentFeedback[agent.id]);
                allAgents.push({
                  agent_id: agent.id,
                  feedback: fb && fb.trim() ? fb.trim() : null,
                  enable_grounding_check: !!agentGrounding[agent.id],
                  ...getAgentPersistRunOptions(agent)
                });
              }
            });
          });
          payload.agents = allAgents;
        }
      }

      const response = await postRunTemplate(payload, runType === 'single_agent');
      statusFenceRef.current = Date.now();

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.code === 'QUICK_FEEDBACK_CREATION_UNSUPPORTED') {
        setQuickFeedbackNotice(response.data.message);
        onRuntimeWake?.();
        await fetchTemplateDetails({ background: true });
        return; // Preserve feedback and attachments so the user can switch to Thinking.
      }

      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to run preset');
        if (response.data.code === 'QUICK_FEEDBACK_INVALID_EDIT') {
          onRuntimeWake?.();
          await fetchTemplateDetails({ background: true });
        }
        return;
      }

      if (response.data.status === 'cancelled') {
        onRuntimeWake?.();
        await fetchTemplateDetails({ background: true });
        return;
      }

      snapshotRunInputs(
        runType === 'single_agent' ? [runAgentId]
          : isSelectiveRun ? Array.from(selectedAgentIds)
            : getAllAgentIdsInTemplate()
      );
      // --- Clear feedback ONLY for the agents that were executed ---
      if (isSelectiveRun) {
        setAgentFeedback(prev => {
          const newFeedback = { ...prev };
          Array.from(selectedAgentIds).forEach(agentId => delete newFeedback[agentId]);
          return newFeedback;
        });
        setAgentGrounding(prev => {
          const next = { ...prev };
          Array.from(selectedAgentIds).forEach(agentId => delete next[agentId]);
          return next;
        });
        clearSelectionAttachments(Array.from(selectedAgentIds));
        clearCommentState(Array.from(selectedAgentIds));
        setSelectedAgentIds(new Set()); // clear selection after run
      } else if (runType === 'single_agent') {
        setAgentFeedback(prev => {
          const newFeedback = { ...prev };
          if (singleAgentExecution.agentId) {
            delete newFeedback[singleAgentExecution.agentId];
          }
          return newFeedback;
        });
        setAgentGrounding(prev => {
          if (!singleAgentExecution.agentId) return prev;
          const next = { ...prev };
          delete next[singleAgentExecution.agentId];
          return next;
        });
        if (singleAgentExecution.agentId) {
          clearSelectionAttachments(singleAgentExecution.agentId);
          clearCommentState(singleAgentExecution.agentId);
        }
        setSingleAgentExecution({ agentId: null, feedback: null });
      } else {
        // If it's a full template run, clear everything
        setAgentFeedback({});
        setAgentGrounding({});
        setAgentSelectedText({});
        setAgentSelectedContext({});
        setAgentPreviewOpen({});
        setAgentsWithComments(new Set());
        setCommentFeedbackData({});
        setSingleAgentExecution({ agentId: null, feedback: null });
      }

      setMasterFeedback('');
      onRuntimeWake?.();
      await handleRefresh();
      if (onRefreshTemplateList) {
        onRefreshTemplateList();
      }

    } catch (error) {
      console.error('Error running template:', error);
      onError(error.message || 'Failed to run preset');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const performSaveTemplate = async () => {
    if (!hasPendingChanges) {
      // Still sync tab name even if no structural changes
      if (onTemplateNameSaved && formData.template_name) {
        onTemplateNameSaved(formData.template_name);
      }
      return
    }
    if (!formData.template_name.trim()) {
      throw new Error('Preset name is required');
    }

    if (!formData.fabric_id) {
      throw new Error('Please select a project');
    }

    if (formData.sectionOrder.length === 0) {
      throw new Error('At least one section is required');
    }

    const structuredSections = convertToStructuredSections(formData.sectionsDict, formData.sectionOrder);

    // Images cropped in the editor are still blobs in the browser: upload them now,
    // so update-template gets a stored file for each (nothing is stored before Save Preset).
    const preparedEdits = {};
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      for (const agentKey of (section?.agentOrder || [])) {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id && agent.editedResponse) {
          preparedEdits[agent.id] = await uploadCroppedImages(agent.editedResponse, {
            templateId, fabricId: formData.fabric_id, agentId: agent.id, authToken: headers.Authorization,
          });
        }
      }
    }

    const editedResponses = [];
    formData.sectionOrder.forEach(sectionKey => {
      const section = formData.sectionsDict[sectionKey];
      if (!section) return;
      section.agentOrder.forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id && agent.editedResponse) {
          const prepared = preparedEdits[agent.id] || agent.editedResponse;
          editedResponses.push({
            agent_id: agent.id,
            markdown: agent.editedResponse.markdown,
            tables: agent.editedResponse.tables || [],
            graphs: agent.editedResponse.graphs || [],
            images: agent.editedResponse.images || [],
            in_place_images: agent.editedResponse.in_place_images || [],
            // Pasted images: the backend copies each staged s3_key into the new version.
            ...(prepared.new_images?.length && {
              new_images: prepared.new_images.map(({ image_id, s3_key, index, subtitle, footer, attach_doc_id }) => ({ image_id, s3_key, index, subtitle, footer, attach_doc_id })),
            }),
            ...(prepared.new_in_place_images?.length && {
              new_in_place_images: prepared.new_in_place_images.map(({ in_place_image_id, index, s3_key, subtitle, footer, attach_doc_id }) => ({ in_place_image_id, index, s3_key, subtitle, footer, attach_doc_id })),
            }),
            // Cropped figures: the backend points each at its new file
            ...(prepared.replaced_images?.length && {
              replaced_images: prepared.replaced_images.map(({ image_id, s3_key }) => ({ image_id, s3_key })),
            }),
            ...(prepared.replaced_in_place_images?.length && {
              replaced_in_place_images: prepared.replaced_in_place_images.map(({ in_place_image_id, s3_key }) => ({ in_place_image_id, s3_key })),
            }),
            // Forward deletions so the backend can drop removed tables/figures.
            ...(agent.editedResponse.deleted_table_ids?.length && { deleted_table_ids: agent.editedResponse.deleted_table_ids }),
            ...(agent.editedResponse.deleted_graph_ids?.length && { deleted_graph_ids: agent.editedResponse.deleted_graph_ids }),
            ...(agent.editedResponse.deleted_image_ids?.length && { deleted_image_ids: agent.editedResponse.deleted_image_ids }),
            ...(agent.editedResponse.deleted_in_place_image_ids?.length && { deleted_in_place_image_ids: agent.editedResponse.deleted_in_place_image_ids }),
            ...(agent.editedResponse.deleted_in_place_graph_ids?.length && { deleted_in_place_graph_ids: agent.editedResponse.deleted_in_place_graph_ids })
          });
        }
      });
    });

    const cleanSections = {
      new: structuredSections.new.map(s => ({
        ...s,
        agents: s.agents.map(({ _editedResponse, ...rest }) => rest)
      })),
      modified: structuredSections.modified.map(s => ({
        ...s,
        agents: s.agents.map(({ _editedResponse, ...rest }) => rest)
      })),
      deleted: structuredSections.deleted
    };
    const requestData = {
      template_id: templateId,
      template_name: formData.template_name,
      fabric_id: formData.fabric_id,
      vm_config: formData.vm_config || false,
      lang_id: formData.lang_id || 1,
      sections: cleanSections,
      global_prompt: formData.global_prompt || null,
      format_json: withTocDefaults(formData.format_json) || null,
      ...(editedResponses.length > 0 && { edited_responses: editedResponses })
    };

    // /update-template streams NDJSON — read it and take the final envelope.
    const { data: result } = await postNdjson(
      `${API_BASE_URL}/update-template`,
      requestData,
      headers
    );

    if (result.status === 'NotAuthorized') {
      authNavigate('/login', 'iam', navigate);
      return;
    }

    if (result.status === 'failed') {
      throw new Error(result.message);
    }

    savedTemplateFingerprintRef.current = buildTemplateFingerprint(formData);
    setHasPendingChanges(false);
    deletedItemsByAgentRef.current = {};


    if (onTemplateNameSaved && formData.template_name) {
      onTemplateNameSaved(formData.template_name);
    }
  };

  // Helper function for natural sorting of ordering IDs
  // S-prefix sections (summary) always sort before any numeric section
  const naturalCompare = (a, b) => {
    const aIsS = a.startsWith('S');
    const bIsS = b.startsWith('S');
    if (aIsS && !bIsS) return -1;
    if (!aIsS && bIsS) return 1;

    const aClean = aIsS ? a.slice(1) : a;
    const bClean = bIsS ? b.slice(1) : b;
    const aParts = aClean.split('.').map(num => parseInt(num, 10));
    const bParts = bClean.split('.').map(num => parseInt(num, 10));

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aNum = aParts[i] || 0;
      const bNum = bParts[i] || 0;

      if (aNum !== bNum) {
        return aNum - bNum;
      }
    }
    return 0;
  };
  const recalculateSiblingOrderingIds = (parentKey, sectionsDict, sectionOrder) => {
    if (!sectionsDict || typeof sectionsDict !== 'object' || !Array.isArray(sectionOrder)) {
      return sectionsDict;
    }

    if (parentKey && !sectionsDict[parentKey]) {
      console.warn(`Parent section ${parentKey} not found during recalculation`);
      return sectionsDict;
    }

    // Sort siblings by their current ordering_id before recalculating
    const siblings = sectionOrder.filter(key => {
      const section = sectionsDict[key];
      if (!section || section.isDeleted) return false;

      return (parentKey ? section.parent_section_id === parentKey : !section.parent_section_id);
    }).sort((a, b) => {
      const orderingA = sectionsDict[a]?.section_ordering_id || '0';
      const orderingB = sectionsDict[b]?.section_ordering_id || '0';
      return naturalCompare(orderingA, orderingB);
    });

    const updatedSectionsDict = { ...sectionsDict };

    // Determine if parent is a summary section — children of summary sections use S-prefix ordering
    const parentIsSummary = parentKey && sectionsDict[parentKey]?.is_summary_section;

    // Filter out root-level summary sections from numeric ordering (they have their own S-prefix system)
    // But if parent is a summary section, include its children (they get S-prefix sub-ordering)
    const numericSiblings = parentIsSummary
      ? siblings
      : siblings.filter(key => !sectionsDict[key]?.is_summary_section);

    numericSiblings.forEach((siblingKey, index) => {
      const section = updatedSectionsDict[siblingKey];

      if (!section) return;

      const newOrderingId = parentKey
        ? `${updatedSectionsDict[parentKey]?.section_ordering_id || '0'}.${index + 1}`
        : (() => {
          const fmt = formData.format_json?.numberingFormat;
          const startVal = (fmt === "none" || fmt === undefined) ? (formData.format_json?.numberingStartValue ?? 1) : 1;
          return String(startVal + index);
        })();
      if (section.section_ordering_id !== newOrderingId) {
        updatedSectionsDict[siblingKey] = {
          ...section,
          section_ordering_id: newOrderingId,
          isModified: section.id ? true : undefined
        };

        const children = sectionOrder.filter(key => {
          const childSection = sectionsDict[key];
          return childSection && !childSection.isDeleted && childSection.parent_section_id === siblingKey;
        });

        if (children.length > 0) {
          try {
            const childUpdates = recalculateSiblingOrderingIds(siblingKey, updatedSectionsDict, sectionOrder);
            if (childUpdates && typeof childUpdates === 'object') {
              Object.assign(updatedSectionsDict, childUpdates);
            }
          } catch (error) {
            console.error('Error updating children ordering:', error);
          }
        }
      }
    });

    return updatedSectionsDict;
  };

  const recalculateSummaryOrderingIds = (sectionsDict, sectionOrder) => {
    const updatedSectionsDict = { ...sectionsDict };

    const isRootSummary = (sec) => {
      if (!sec || !sec.is_summary_section || sec.isDeleted) return false;
      if (sec.parent_section_id) return false;
      const oid = sec.section_ordering_id || '';
      return !oid.includes('.');
    };

    const isChildSummary = (sec, parentKey) => {
      if (!sec || !sec.is_summary_section || sec.isDeleted) return false;
      if (sec.parent_section_id === parentKey) return true;
      const oid = sec.section_ordering_id || '';
      const parentOid = updatedSectionsDict[parentKey]?.section_ordering_id || '';
      return oid.startsWith(parentOid + '.') && oid.split('.').length === parentOid.split('.').length + 1;
    };

    const rootSummarySections = sectionOrder.filter(key => isRootSummary(updatedSectionsDict[key]))
      .sort((a, b) => {
        const orderingA = updatedSectionsDict[a]?.section_ordering_id || 'S0';
        const orderingB = updatedSectionsDict[b]?.section_ordering_id || 'S0';
        return naturalCompare(orderingA, orderingB);
      });

    rootSummarySections.forEach((key, index) => {
      const newOrderingId = `S${index + 1}`;
      const section = updatedSectionsDict[key];
      if (section.section_ordering_id !== newOrderingId) {
        updatedSectionsDict[key] = {
          ...section,
          section_ordering_id: newOrderingId,
          isModified: section.id ? true : undefined
        };
      }

      const children = sectionOrder.filter(childKey => isChildSummary(updatedSectionsDict[childKey], key))
        .sort((a, b) => {
          const orderingA = updatedSectionsDict[a]?.section_ordering_id || 'S0.0';
          const orderingB = updatedSectionsDict[b]?.section_ordering_id || 'S0.0';
          return naturalCompare(orderingA, orderingB);
        });

      children.forEach((childKey, childIndex) => {
        const childOrderingId = `${newOrderingId}.${childIndex + 1}`;
        const child = updatedSectionsDict[childKey];
        updatedSectionsDict[childKey] = {
          ...child,
          section_ordering_id: childOrderingId,
          parent_section_id: key,
          isModified: child.id ? true : undefined
        };
      });
    });

    return updatedSectionsDict;
  };

  const findInsertionPosition = (parentKey, sectionOrder, sectionsDict) => {
    if (!parentKey) {
      return sectionOrder.length;
    }

    const parentIndex = sectionOrder.indexOf(parentKey);
    if (parentIndex === -1) return sectionOrder.length;

    let insertIndex = parentIndex + 1;
    const parentSection = sectionsDict[parentKey];
    if (!parentSection) return sectionOrder.length;

    const parentOrderingId = parentSection.section_ordering_id;

    for (let i = parentIndex + 1; i < sectionOrder.length; i++) {
      const sectionKey = sectionOrder[i];
      const section = sectionsDict[sectionKey];
      if (!section) continue;

      const orderingId = section.section_ordering_id;

      if (orderingId && orderingId.startsWith(parentOrderingId + '.')) {
        insertIndex = i + 1;
      } else {
        break;
      }
    }

    return insertIndex;
  };

  const handleShowVersion = async (sectionKey, agentKey, agentId, version, source) => {
    onProcessingStart('Loading version...');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-agent-response-version`,
        { agent_id: agentId, version: version, fabric_id: fabricId },
        { headers }
      );

      if (response.data.status === 'success') {
        setFormData(prev => {
          const newSectionsDict = { ...prev.sectionsDict };
          const newAgentsDict = { ...newSectionsDict[sectionKey].agentsDict };

          newAgentsDict[agentKey] = {
            ...newAgentsDict[agentKey],
            response: {
              ...response.data.data,
              ...(source ? { source } : {}),
            } // Override the current response with the historical one
          };

          newSectionsDict[sectionKey] = {
            ...newSectionsDict[sectionKey],
            agentsDict: newAgentsDict
          };

          return { ...prev, sectionsDict: newSectionsDict };
        });

        // Update the state that tracks which version number is displayed
        setSelectedVersions(prev => ({ ...prev, [agentId]: version }));
      } else {
        onError(response.data.message || 'Failed to load version');
      }
    } catch (err) {
      onError(err.message || 'An error occurred while fetching the version.');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
    }
  };

  const handleCompareVersions = (agentId, vA, vB) => {
    if (vA === vB) return;
    const versionA = Math.min(vA, vB);
    const versionB = Math.max(vA, vB);
    setComparePanelState({ agentId, versionA, versionB });
  };

  const handleRestoreVersion = async (agentId, version) => {
    if (!window.confirm(`Are you sure you want to restore to version ${version}? This will delete all newer versions.`)) {
      return;
    }

    onProcessingStart('Restoring version...');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/restore-agent-version`,
        { agent_id: agentId, version: version },
        { headers }
      );

      if (response.data.status === 'success') {
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to restore version');
      }
    } catch (err) {
      onError(err.message || 'Failed to restore version');
    } finally {
      onProcessingEnd();
      setOpenVersionDropdown(null);
    }
  };

  const promptRestoreVersion = (agentId, version, versionName) => {
    let agent = null;
    for (const sKey of Object.keys(formData.sectionsDict || {})) {
      for (const aKey of Object.keys(formData.sectionsDict[sKey]?.agentsDict || {})) {
        const a = formData.sectionsDict[sKey].agentsDict[aKey];
        if (a.id === agentId) { agent = a; break; }
      }
      if (agent) break;
    }

    const currentDocIds = (agent?.document_mappings || []).filter(d => d.selected).map(d => d.doc_id);
    const versionObj = (agent?.versions || []).find(v => v.version === version);
    const versionDocIds = Array.isArray(versionObj?.last_run_doc_ids) ? versionObj.last_run_doc_ids : [];

    const cSet = new Set(currentDocIds);
    const vSet = new Set(versionDocIds);
    const docsChanged = versionDocIds.length > 0 && (
      currentDocIds.length !== versionDocIds.length
      || currentDocIds.some(id => !vSet.has(id))
      || versionDocIds.some(id => !cSet.has(id))
    );

    const docNameMap = {};
    const vDocNames = agent?.version_doc_names || {};
    Object.entries(vDocNames).forEach(([id, name]) => {
      if (name) docNameMap[Number(id)] = name;
    });
    (agent?.document_mappings || []).forEach(d => {
      if (d.doc_name) docNameMap[d.doc_id] = d.doc_name;
    });

    const latestVersion = (agent?.versions || [])[0]?.version;
    const currentVersionName = latestVersion != null ? `Version ${latestVersion}` : 'Current';

    setRestoreVersionInfo({
      isOpen: true, agentId, version, versionName, fabricId,
      docsChanged, restoreDocs: docsChanged, versionDocIds, currentDocIds, docNameMap, currentVersionName
    });
  };

  const executeRestoreVersion = async () => {
    const { agentId, version, fabricId, restoreDocs } = restoreVersionInfo;
    setRestoreVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '', docsChanged: false, restoreDocs: false, versionDocIds: [], currentDocIds: [], docNameMap: {} });
    if (onProcessingStart) onProcessingStart('Restoring version...');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/restore-agent-version`,
        { agent_id: agentId, version: version, fabric_id: fabricId, restore_docs: restoreDocs },
        { headers }
      );

      if (response.data.status === 'success') {
        // Refresh the template to show the restored state
        setSelectedVersions(prev => {
          const newState = { ...prev };
          delete newState[agentId];
          return newState;
        });
        setAgentFeedback(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentQuickEdit(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentsWithComments(prev => { const next = new Set(prev); next.delete(agentId); return next; });
        setAgentSelectedText(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentSelectedContext(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentPreviewOpen(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to restore version.');
      }
    } catch (err) {
      onError(err.message || 'An error occurred while restoring the version.');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
    }
  };
  const openConfigureModal = (sectionKey, agentKey) => {
    setCurrentAgentConfig({ sectionKey, agentKey });
    setConfigureModalOpen(true);
  };

  // ── Languages fetch ──
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/langs`, { headers });
        if (response.data.status === 'success' && response.data.langs) {
          setAllLanguages(Object.entries(response.data.langs).map(([id, name]) => ({ id: parseInt(id), name })));
        }
      } catch (e) { console.error('Failed to fetch languages:', e); }
    };
    fetchLanguages();
  }, []);

  // Fetch Table Structures on mount + on refresh — shared across all agents to avoid N calls
  useEffect(() => {
    // Structures depend on the project's country — wait until the project id is known
    if (!fabricId) return;
    const fetchTableStructures = async () => {
      setTableStructuresLoading(true);
      try {
        const resp = await axios.post(`${API_BASE_URL}/list-table-structures`, { fabric_id: fabricId }, { headers });
        if (resp.data.status === 'success' && resp.data.table_structures) {
          setTableStructures(resp.data.table_structures);
        }
      } catch (err) {
        console.error('Failed to fetch table structures:', err);
      } finally {
        setTableStructuresLoading(false);
      }
    };
    fetchTableStructures();
  }, [refreshKey, fabricId]);

  // ── Prompt context translation ──
  const handleTranslateAgentContext = async (sectionKey, agentKey, targetLangId) => {
    if (onProcessingStart) onProcessingStart('Translating agent prompt...');
    setTranslateDropdown(null);
    try {
      const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
      if (!agent || !agent.id) { onError('Agent not found'); return; }
      const response = await axios.post(`${API_BASE_URL}/translate-agent-context`,
        { agent_id: agent.id, fabric_id: fabricId, target_lang_id: targetLangId }, { headers });
      if (response.data.status === 'NotAuthorized') { authNavigate('/login', 'iam', navigate); return; }
      if (response.data.status === 'success') { handleRefresh(); }
      else { onError(response.data.message || 'Failed to translate agent context'); }
    } catch (err) { onError(err.message || 'An error occurred while translating'); }
    finally { if (onProcessingEnd) onProcessingEnd(); }
  };

  // ── Prompt version: show ──
  const handleShowPromptVersion = (agentId, version, latestVersion) => {
    _doShowPromptVersion(agentId, version, version === latestVersion);
  };

  const _doShowPromptVersion = async (agentId, version, isLatest = false) => {
    if (onProcessingStart) onProcessingStart('Loading prompt version...');
    try {
      const response = await axios.post(`${API_BASE_URL}/get-agent-prompt-version`,
        { agent_id: agentId, version, fabric_id: fabricId }, { headers });
      if (response.data.status === 'success') {
        const { agent_context, additional_context, context_lang_id } = response.data.data;
        setFormData(prev => {
          const newSectionsDict = { ...prev.sectionsDict };
          for (const sKey of Object.keys(newSectionsDict)) {
            const agentsDict = newSectionsDict[sKey].agentsDict;
            if (agentsDict) {
              for (const aKey of Object.keys(agentsDict)) {
                if (agentsDict[aKey].id === agentId) {
                  newSectionsDict[sKey] = {
                    ...newSectionsDict[sKey],
                    agentsDict: { ...agentsDict, [aKey]: { ...agentsDict[aKey], agent_context, additional_context, context_lang_id } }
                  };
                }
              }
            }
          }
          return { ...prev, sectionsDict: newSectionsDict };
        });
        setSelectedPromptVersions(prev => ({ ...prev, [agentId]: version }));
        if (isLatest) {
          setPromptVersionPreviewAgents(prev => { const n = new Set(prev); n.delete(agentId); return n; });
        } else {
          setPromptVersionPreviewAgents(prev => new Set([...prev, agentId]));
        }
      } else { onError(response.data.message || 'Failed to load prompt version'); }
    } catch (err) { onError(err.message || 'Error fetching prompt version.'); }
    finally { if (onProcessingEnd) onProcessingEnd(); }
  };

  // ── Prompt version: restore ──
  const promptRestorePromptVersion = (agentId, version, versionName) => {
    setRestorePromptVersionInfo({ isOpen: true, agentId, version, versionName, fabricId });
  };

  const executeRestorePromptVersion = async () => {
    const { agentId, version, fabricId } = restorePromptVersionInfo;
    setRestorePromptVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '' });
    if (onProcessingStart) onProcessingStart('Restoring prompt version...');
    try {
      const response = await axios.post(`${API_BASE_URL}/restore-agent-prompt-version`,
        { agent_id: agentId, version, fabric_id: fabricId }, { headers });
      if (response.data.status === 'success') {
        setAgentQuickEdit(prev => ({ ...prev, [agentId]: false }));
        setSelectedPromptVersions(prev => { const n = { ...prev }; delete n[agentId]; return n; });
        setPromptVersionPreviewAgents(prev => { const n = new Set(prev); n.delete(agentId); return n; });
        handleRefresh();
      } else { onError(response.data.message || 'Failed to restore prompt version.'); }
    } catch (err) { onError(err.message || 'Error restoring prompt version.'); }
    finally { if (onProcessingEnd) onProcessingEnd(); }
  };
  // const handleTranslateAgent = async (agentId, targetLangId) => {
  //   if (onProcessingStart) {
  //     onProcessingStart('Translating agent response...');
  //   }

  //   try {
  //     const response = await axios.post(
  //       `${API_BASE_URL}/translate-agent-response`,
  //       {
  //         agent_id: agentId,
  //         fabric_id: fabricId,
  //         target_lang_id: targetLangId
  //       },
  //       { headers }
  //     );

  //     if (response.data.status === 'NotAuthorized') {
  //       authNavigate('/login', 'iam', navigate);
  //       return;
  //     }


  //     if (response.data.status === 'success') {
  //       handleRefresh();
  //     } else {
  //       onError(response.data.message || 'Failed to translate agent response');
  //     }
  //   } catch (err) {
  //     console.error('Error translating agent response:', err);
  //     onError(err.message || 'An error occurred while translating');
  //   } finally {
  //     if (onProcessingEnd) {
  //       onProcessingEnd();
  //     }
  //   }
  // };
  const handleTranslateAgent = async (agentId, targetLangId) => {
    if (onProcessingStart) {
      onProcessingStart('Translating agent response...');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/translate-agent-response`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': headers.Authorization || headers.authorization
          },
          body: JSON.stringify({
            agent_id: agentId,
            fabric_id: fabricId,
            target_lang_id: targetLangId
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalResult = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);

            if (data.status === 'accepted') {
              // First chunk - translation started, connection alive
              console.log('Translation in progress:', data.message);
            } else {
              // Final result
              finalResult = data;
            }
          } catch (e) {
            // Not JSON, might be partial chunk - ignore
            console.log('Partial chunk:', line);
          }
        }
      }

      statusFenceRef.current = Date.now();
      // Handle final result
      if (finalResult) {
        if (finalResult.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
          return;
        }

        if (finalResult.status === 'success') {
          clearAgentFeedbackState(agentId);
          onRuntimeWake?.();
          handleRefresh();
        } else if (finalResult.status === 'cancelled') {
          onRuntimeWake?.();
          fetchTemplateDetails({ background: true });
        } else {
          onError(finalResult.message || 'Failed to translate agent response');
        }
      }

    } catch (err) {
      console.error('Error translating agent response:', err);
      onError(err.message || 'An error occurred while translating');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };
  useEffect(() => {
    setExpandedSections(prev => {
      // Return prev untouched when every section is already tracked, so this
      // does not hand React a fresh object (and a re-render) on every run.
      const missing = formData.sectionOrder.filter(sectionKey => !(sectionKey in prev));
      if (missing.length === 0) return prev;
      const newExpanded = { ...prev };
      missing.forEach(sectionKey => { newExpanded[sectionKey] = false; });
      return newExpanded;
    });
  }, [formData.sectionOrder, setExpandedSections]);

  useEffect(() => {
    if (templateDataCache[templateId]) {
      const cachedData = templateDataCache[templateId];
      stopDebug('loaded from CACHE (no server call)', { refreshKey, agent_statuses: debugStatusesFromSections(cachedData.sections) });
      setCapabilities(cachedData.capabilities || {});
      let { sectionsDict, sectionOrder } = convertSectionsToDict(cachedData.sections || []);
      sectionsDict = recalculateSummaryOrderingIds(sectionsDict, sectionOrder);
      sectionsDict = applyPersistedDeletions(sectionsDict);

      const nextFormData = {
        template_name: cachedData.template_name,
        fabric_id: cachedData.fabric_id,
        fabric_name: cachedData.fabric_name,
        vm_config: cachedData.vm_config,
        lang_id: cachedData.lang_id,
        sectionsDict,
        sectionOrder,
        global_prompt: cachedData.global_prompt || '',
        format_json: cachedData.format_json || null
      };

      savedTemplateFingerprintRef.current = buildTemplateFingerprint(nextFormData);
      setFormData(nextFormData);
      pruneAgentStateToTemplate(sectionsDict);

      setTemplateData({
        agent_count: cachedData.agent_count || 0,
        completed_agents: cachedData.completed_agents || 0,
        running_agents: cachedData.running_agents || 0,
        failed_agents: cachedData.failed_agents || 0,
        pending_agents: cachedData.pending_agents || 0,
        status: deriveTemplateStatus(cachedData),
        report_gen_status: cachedData.report_gen_status || null,
        context_mapping_status: cachedData.context_mapping_status || ''
      });
      setIsInitialLoading(false);
      setHasPendingChanges(false);

      if (cachedData.fabric_id) {
        loadFabricDocuments(cachedData.fabric_id);
      }
    } else {
      setIsInitialLoading(true);
      fetchTemplateDetails();
    }
  }, [templateId, refreshKey]);


  useEffect(() => {
    if (isUserEditRef.current) {
      const savedFingerprint = savedTemplateFingerprintRef.current;
      setHasPendingChanges(
        savedFingerprint === null || buildTemplateFingerprint(formData) !== savedFingerprint
      );
    }
    isUserEditRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.sectionsDict, formData.sectionOrder]);

  const onCloseGenerateReport = (closed_due_to) => {
    setIsGenReportModalVisible(false);
    if (closed_due_to === 'gen') {
      onRuntimeWake?.('report');
      handleRefresh();
    }
  }
  const detailsInFlightRef = useRef(false);
  const detailsControllerRef = useRef(null);
  useEffect(() => () => detailsControllerRef.current?.abort(), []);
  const runtimeStatusRef = useRef(runtimeStatus);
  runtimeStatusRef.current = runtimeStatus;
  const deferAutoRefreshRef = useRef(false);
  deferAutoRefreshRef.current = hasPendingChanges || isUserEditRef.current || Object.keys(editingAgents).length > 0
    || Object.keys(selectedVersions).length > 0 || promptVersionPreviewAgents.size > 0;
  const runtimeMergeGuardRef = useRef({ editingAgents, selectedVersions });
  runtimeMergeGuardRef.current = { editingAgents, selectedVersions };

  // Run output owned by the server. Prompts, settings and mappings the user may
  // be editing are left alone, so a deferred refresh can still finish agents.
  const RUNTIME_AGENT_FIELDS = ['status', 'updated_at', 'versions', 'prompt_versions',
    'last_run_doc_ids', 'version_doc_names'];
  const mergeServerRuntime = (serverSections) => {
    const serverAgents = new Map();
    (serverSections || []).forEach(section => (section.agents || []).forEach(agent => {
      if (agent.id) serverAgents.set(agent.id, agent);
    }));
    const { editingAgents: editing, selectedVersions: selected } = runtimeMergeGuardRef.current;
    setFormData(prev => {
      let sectionsDict = null;
      Object.entries(prev.sectionsDict || {}).forEach(([sectionKey, section]) => {
        let agentsDict = null;
        Object.entries(section.agentsDict || {}).forEach(([agentKey, agent]) => {
          const server = agent.id && serverAgents.get(agent.id);
          if (!server) return;
          const next = { ...agent, _responsePending: false };
          RUNTIME_AGENT_FIELDS.forEach(field => { next[field] = server[field]; });
          // Keep a response the user is editing or viewing as an older version.
          const keepResponse = `${sectionKey}::${agentKey}` in editing || agent.editedResponse
            || selected[agent.id] !== undefined;
          if (!keepResponse && 'response' in server) next.response = server.response;
          agentsDict = agentsDict || { ...section.agentsDict };
          agentsDict[agentKey] = next;
        });
        if (agentsDict) {
          sectionsDict = sectionsDict || { ...prev.sectionsDict };
          sectionsDict[sectionKey] = { ...section, agentsDict };
        }
      });
      return sectionsDict ? { ...prev, sectionsDict } : prev;
    });
  };

  useEffect(() => {
    if (!runtimeStatus) return;
    if (runtimeStatus.requested_at && runtimeStatus.requested_at < statusFenceRef.current) return;
    setTemplateData(prev => ({
      ...prev,
      agent_count: runtimeStatus.agent_count ?? prev.agent_count,
      completed_agents: runtimeStatus.completed_agents ?? prev.completed_agents,
      running_agents: runtimeStatus.running_agents ?? prev.running_agents,
      failed_agents: runtimeStatus.failed_agents ?? prev.failed_agents,
      pending_agents: runtimeStatus.pending_agents ?? prev.pending_agents,
      status: runtimeStatus.status ? deriveTemplateStatus(runtimeStatus) : prev.status,
      report_gen_status: runtimeStatus.report_gen_status,
      context_mapping_status: runtimeStatus.context_mapping_status
    }));
  }, [runtimeStatus]);

  useEffect(() => {
    const statuses = runtimeStatus?.agent_statuses;
    stopDebug('runtime poll received', {
      has_agent_statuses: !!statuses, requested_at: runtimeStatus?.requested_at, fence: statusFenceRef.current,
      template_status: runtimeStatus?.status, has_active_work: runtimeStatus?.has_active_work, agent_statuses: statuses,
    });
    if (!statuses || (runtimeStatus.requested_at || 0) < statusFenceRef.current) {
      stopDebug('runtime poll statuses NOT applied', !statuses ? 'no agent_statuses in response' : 'older than fence');
      return;
    }
    const statusById = new Map(Object.entries(statuses).map(([id, status]) => [Number(id), status]));
    dropFinishedRunSnapshots(statusById, runtimeStatus.requested_at || 0);
    applyAgentStatuses(statusById, new Set(), 'runtime-poll');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeStatus]);

  useEffect(() => {
    stopDebug('formData agent statuses now', debugStatusesFromDict(formData.sectionsDict));
  }, [formData.sectionsDict]);

  const hasResponsePending = Object.values(formData.sectionsDict || {})
    .some(section => Object.values(section.agentsDict || {}).some(agent => agent._responsePending));
  useEffect(() => {
    if (hasResponsePending) onRuntimeStale?.();
  }, [hasResponsePending, onRuntimeStale]);

  // Keep the callback current without restarting the shared polling loop.
  useEffect(() => registerRuntimeRefresh?.(signal => fetchTemplateDetails({ background: true, signal })));

  const fetchTemplateDetails = async ({ background = false, signal } = {}) => {
    if (background && detailsInFlightRef.current) {
      stopDebug('get-template-details SKIPPED (another one in flight)', { background, fromPoller: !!signal });
      return false;
    }
    const startedAt = Date.now();
    stopDebug('get-template-details START', { background, fromPoller: !!signal, startedAt });
    detailsControllerRef.current?.abort();
    const controller = new AbortController();
    detailsControllerRef.current = controller;
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    detailsInFlightRef.current = true;
    if (!background) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-template-details`,
        {
          template_id: templateId,
          fabric_id: fabricId,
          include_agent_responses: true
        },
        { headers, signal: controller.signal }
      );

      if (controller.signal.aborted) {
        stopDebug('get-template-details response IGNORED (aborted)', { startedAt });
        return false;
      }
      stopDebug('get-template-details response', {
        startedAt, background, status: response.data.status,
        deferMerge: background && deferAutoRefreshRef.current,
        agent_statuses: debugStatusesFromSections(response.data.data?.sections),
      });

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        if (response.data.code === 'ACCESS_DENIED' && onAccessLost) {
          onAccessLost(response.data.message);
          return;
        }
        if (background) throw new Error(response.data.message || 'Failed to load Preset details');
        setError(response.data.message || 'Failed to load Preset details');
        onError(response.data.message || 'Failed to load Preset details');
        return;
      }

      const data = response.data.data;
      dropFinishedRunSnapshots(
        new Map((data.sections || []).flatMap(s => (s.agents || []).map(a => [a.id, a.status]))),
        startedAt
      );
      setCapabilities(data.capabilities || {});
      const nextTemplateData = {
        agent_count: data.agent_count || 0,
        completed_agents: data.completed_agents || 0,
        running_agents: data.running_agents || 0,
        failed_agents: data.failed_agents || 0,
        pending_agents: data.pending_agents || 0,
        status: deriveTemplateStatus(data),
        report_gen_status: runtimeStatusRef.current?.report_gen_status ?? data.report_gen_status,
        context_mapping_status: data.context_mapping_status || ''
      };

      // Unsaved edits: replacing the form would lose them, so only merge the
      // run output (agent status, responses, versions) into it.
      if (background && deferAutoRefreshRef.current) {
        mergeServerRuntime(data.sections);
        setTemplateData(nextTemplateData);
        setTemplateDataCache(prev => ({ ...prev, [templateId]: data }));
        return true;
      }

      let { sectionsDict, sectionOrder } = convertSectionsToDict(data.sections || []);
      sectionsDict = recalculateSummaryOrderingIds(sectionsDict, sectionOrder);
      sectionsDict = applyPersistedDeletions(sectionsDict);

      const nextFormData = {
        template_name: data.template_name,
        fabric_id: data.fabric_id,
        fabric_name: data.fabric_name,
        vm_config: data.vm_config,
        lang_id: data.lang_id,
        sectionsDict,
        sectionOrder,
        global_prompt: data.global_prompt || '',
        format_json: data.format_json || null,
        heading_mapping_id: data.heading_mapping_id || null
      };

      savedTemplateFingerprintRef.current = buildTemplateFingerprint(nextFormData);
      setFormData(nextFormData);
      pruneAgentStateToTemplate(sectionsDict);

      // Writing rules: an agent that left RUNNING without a newer version had no changes.
      // Checked only on fresh server data. Redux only, so a reload or closing the tab clears it.
      const pendingRules = writingRulesPending || {};
      const pendingIds = Object.keys(pendingRules);
      if (pendingIds.length > 0) {
        const freshAgents = {};
        Object.values(sectionsDict || {}).forEach(sec => {
          Object.values(sec?.agentsDict || {}).forEach(ag => { if (ag?.id != null) freshAgents[ag.id] = ag; });
        });
        const done = [];
        const noChange = {};
        pendingIds.forEach(id => {
          const ag = freshAgents[id];
          if (!ag) { done.push(id); return; }
          if (ag.status === 'RUNNING') return;
          const latest = ag.versions?.[0]?.version ?? 0;
          done.push(id);
          const p = pendingRules[id];
          if (ag.status === 'COMPLETED' && latest === p?.version
              && p?.requestId && ag.response?.execution_metadata?.writing_rules_unchanged === p.requestId) {
            noChange[id] = latest;
          }
        });
        if (done.length) {
          dispatch(resolveWritingRules({ tabId: templateId, done, noChange }));
        }
      }

      setTemplateData(nextTemplateData);

      // Sync tab name on load
      if (onTemplateNameSaved && data.template_name) {
        onTemplateNameSaved(data.template_name);
      }

      setTemplateDataCache(prev => ({
        ...prev,
        [templateId]: data
      }));

      if (data.fabric_id && !background) {
        await loadFabricDocuments(data.fabric_id);
      }

      setHasPendingChanges(false);

      if (!background) restoreCurrentState();
      return true;

    } catch (error) {
      stopDebug('get-template-details ERROR', { startedAt, aborted: controller.signal.aborted, message: error?.message });
      if (controller.signal.aborted || axios.isCancel(error)) return false;
      if (background) throw error;
      console.error('Error fetching Preset details:', error);
      const errorMessage = error.message || 'Failed to load Preset details';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      signal?.removeEventListener('abort', abort);
      if (detailsControllerRef.current === controller) {
        detailsInFlightRef.current = false;
        if (!background && !controller.signal.aborted) {
          setIsLoading(false);
          setIsInitialLoading(false);
        }
      }
    }
  };

  const loadFabricDocuments = async (fabricId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/fabric_unstructured_data_tree`,
        {
          fabric_id: fabricId,
          include_documents: true,
          page: 1,
          size: 25
        },
        { headers }
      );

      if (response.data.status === 'success') {
        setFabricTreeData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading project documents:', error);
    }
  };

  // Sync Docs: fetch diff from backend and open modal
  const handleSyncDocs = async () => {
    setSyncDocsLoading(true);
    setSyncDocsModalOpen(true);
    setSyncDocsChanges([]);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/reconfigure-doc-mappings`,
        { template_id: templateId, fabric_id: fabricId },
        { headers }
      );
      if (response.data.status === 'success') {
        setSyncDocsChanges(response.data.changes || []);
      }
    } catch (err) {
      console.error('Error fetching doc sync diff:', err);
    } finally {
      setSyncDocsLoading(false);
    }
  };

  // Apply approved sync changes to formData
  const handleApplySyncChanges = (approvedChanges) => {
    // Build a lookup: agent_id -> change
    const changeMap = {};
    for (const change of approvedChanges) {
      changeMap[change.agent_id] = change;
    }

    setFormData(prev => {
      const newSectionsDict = {};

      for (const sectionKey of Object.keys(prev.sectionsDict)) {
        const section = prev.sectionsDict[sectionKey];
        const newAgentsDict = {};
        let sectionChanged = false;

        for (const agentKey of Object.keys(section.agentsDict || {})) {
          const agent = section.agentsDict[agentKey];
          const change = changeMap[agent.id];

          if (!change) {
            newAgentsDict[agentKey] = agent;
            continue;
          }

          // Build new mappings array immutably
          const existingMappings = agent.document_mappings || [];
          const currentDocIds = new Set(existingMappings.filter(d => d.selected).map(d => d.doc_id));
          const removedIds = new Set(change.removed_docs.map(d => d.doc_id));

          // Keep non-removed existing mappings
          const kept = existingMappings.filter(m => !removedIds.has(m.doc_id));

          // Add new docs that don't already exist
          const toAdd = change.added_docs
            .filter(doc => !currentDocIds.has(doc.doc_id))
            .map(doc => ({ doc_id: doc.doc_id, doc_name: doc.doc_name, selected: true }));

          newAgentsDict[agentKey] = {
            ...agent,
            document_mappings: [...kept, ...toAdd],
            isModified: true
          };
          sectionChanged = true;
        }

        newSectionsDict[sectionKey] = sectionChanged
          ? { ...section, agentsDict: newAgentsDict }
          : section;
      }

      return { ...prev, sectionsDict: newSectionsDict };
    });

    setSyncDocsModalOpen(false);
    isUserEditRef.current = true;
  };

  const handleRefresh = () => {
    dispatch(clearWritingRulesNoChange(templateId));
    saveCurrentState();
    setSelectedVersions({});
    setSelectedPromptVersions({});
    setPromptVersionPreviewAgents(new Set());
    setSelectedAgentIds(new Set()); // Reset selections on refresh
    setTranslateDropdown(null);
    deletedItemsByAgentRef.current = {};
    setMasterFeedback('');
    setTemplateDataCache(prev => {
      const newCache = { ...prev };
      delete newCache[templateId];
      return newCache;
    });
    setRefreshKey(prev => prev + 1);
  };

  const addSection = (parentKey = null) => {
    isUserEditRef.current = true;
    const newKey = `temp_${Date.now()}_${Math.random()}`;
    const newOrderingId = generateOrderingId(parentKey, formData.sectionsDict, formData.sectionOrder);
    const insertPosition = findInsertionPosition(parentKey, formData.sectionOrder, formData.sectionsDict);

    const parentSection = parentKey ? formData.sectionsDict[parentKey] : null;
    const inheritSummary = parentSection?.is_summary_section || false;

    const newSection = {
      temp_id: newKey,
      section_name: '',
      parent_section_id: parentKey,
      section_ordering_id: newOrderingId,
      is_summary_section: inheritSummary,
      autogen_after: null,
      agentsDict: {},
      agentOrder: [],
      isNew: true
    };

    setFormData(prev => {
      const newSectionOrder = [...prev.sectionOrder];
      newSectionOrder.splice(insertPosition, 0, newKey);

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [newKey]: newSection
        },
        sectionOrder: newSectionOrder
      };
    });

    if (parentKey) {
      setExpandedSections(prev => ({
        ...prev,
        [parentKey]: true
      }));
    }
  };

  const addSummarySection = () => {
    isUserEditRef.current = true;
    const newKey = `temp_${Date.now()}_${Math.random()}`;

    setFormData(prev => {
      const rootSummarySections = prev.sectionOrder.filter(key => {
        const sec = prev.sectionsDict[key];
        return sec?.is_summary_section && !sec.parent_section_id;
      });
      const newOrderingId = `S${rootSummarySections.length + 1}`;

      const newSection = {
        temp_id: newKey,
        section_name: 'Special Section',
        parent_section_id: null,
        section_ordering_id: newOrderingId,
        is_summary_section: true,
        agentsDict: {},
        agentOrder: [],
        isNew: true
      };

      // Insert after the last S-series section (and its children), before regular sections
      let insertIndex = 0;
      for (let i = 0; i < prev.sectionOrder.length; i++) {
        const sec = prev.sectionsDict[prev.sectionOrder[i]];
        if (sec?.is_summary_section) {
          insertIndex = i + 1;
        } else {
          break;
        }
      }

      const newSectionOrder = [...prev.sectionOrder];
      newSectionOrder.splice(insertIndex, 0, newKey);

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [newKey]: newSection
        },
        sectionOrder: newSectionOrder
      };
    });
  };

  const showGenReportModal = () => {
    setIsGenReportModalVisible(true);
  }

  const addAgent = (sectionKey) => {
    isUserEditRef.current = true;
    const section = formData.sectionsDict[sectionKey];
    if (!section) return;

    const newAgentKey = `agent_temp_${Date.now()}_${Math.random()}`;
    const maxOrderingId = section.agentOrder.length > 0
      ? Math.max(...section.agentOrder.map(key =>
        section.agentsDict[key]?.agent_ordering_id || 0
      ))
      : 0;

    const allDocIds = [];
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        thread.documents?.forEach(doc => {
          allDocIds.push(doc.id);
        });
      });
    });

    const newAgent = {
      temp_id: newAgentKey,
      agent_name: '',
      agent_context: '',
      additional_context: null,
      agent_ordering_id: maxOrderingId + 1,
      document_mappings: allDocIds.map(docId => ({
        doc_id: docId,
        selected: true
      })),
      par_style: null,
      rich_formatting: false,
      table_structure_id: null,
      preserve_response: false,
      amend_response: false,
      match_mapped_style: false,
      response_colour: null,
      tags: null,
      isNew: true
    };

    setFormData(prev => ({
      ...prev,
      sectionsDict: {
        ...prev.sectionsDict,
        [sectionKey]: {
          ...prev.sectionsDict[sectionKey],
          agentsDict: {
            ...prev.sectionsDict[sectionKey].agentsDict,
            [newAgentKey]: newAgent
          },
          agentOrder: [...prev.sectionsDict[sectionKey].agentOrder, newAgentKey],
          hasModifiedAgents: true
        }
      }
    }));
    // Auto-expand section so new agent is immediately visible
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: true
    }));
  };

  const deleteSection = (sectionKey) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      let updatedSectionsDict = { ...prev.sectionsDict };
      let updatedSectionOrder = [...prev.sectionOrder];

      const allDescendants = findAllDescendants(sectionKey, updatedSectionsDict, updatedSectionOrder);

      const sectionsToRemove = [sectionKey, ...allDescendants];
      sectionsToRemove.forEach(keyToRemove => {
        const sectionToDelete = updatedSectionsDict[keyToRemove];
        if (sectionToDelete) {
          if (sectionToDelete.id) {
            updatedSectionsDict[keyToRemove] = {
              ...sectionToDelete,
              isDeleted: true
            };
          } else {
            delete updatedSectionsDict[keyToRemove];
          }
        }
      });

      updatedSectionOrder = updatedSectionOrder.filter(key =>
        !sectionsToRemove.includes(key)
      );

      const parentKey = section.parent_section_id;
      try {
        updatedSectionsDict = recalculateSiblingOrderingIds(parentKey, updatedSectionsDict, updatedSectionOrder);
      } catch (error) {
        console.error('Error recalculating sibling ordering:', error);
      }

      if (section.is_summary_section) {
        try {
          updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
        } catch (error) {
          console.error('Error recalculating summary ordering:', error);
        }
      }

      return {
        ...prev,
        sectionsDict: updatedSectionsDict,
        sectionOrder: updatedSectionOrder
      };
    });
  };

  const deleteAgent = (sectionKey, agentKey) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      const agent = section.agentsDict[agentKey];
      if (!agent || isAgentRunning(agent)) return prev;

      isUserEditRef.current = true;

      let updatedAgentsDict = { ...section.agentsDict };
      let updatedAgentOrder = [...section.agentOrder];

      if (agent.id) {
        updatedAgentsDict[agentKey] = {
          ...agent,
          isDeleted: true
        };
      } else {
        delete updatedAgentsDict[agentKey];
      }

      updatedAgentOrder = updatedAgentOrder.filter(key => key !== agentKey);

      updatedAgentOrder.forEach((agentKey, index) => {
        const agent = updatedAgentsDict[agentKey];
        if (agent && agent.agent_ordering_id !== index + 1) {
          updatedAgentsDict[agentKey] = {
            ...agent,
            agent_ordering_id: index + 1,
            isModified: agent.id ? true : undefined
          };
        }
      });

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [sectionKey]: {
            ...section,
            agentsDict: updatedAgentsDict,
            agentOrder: updatedAgentOrder,
            hasModifiedAgents: true
          }
        }
      };
    });
  };

  const updateSectionField = (sectionKey, field, value) => {
    isUserEditRef.current = true;
    setFormData(prev => ({
      ...prev,
      sectionsDict: {
        ...prev.sectionsDict,
        [sectionKey]: {
          ...prev.sectionsDict[sectionKey],
          [field]: value,
          isModified: prev.sectionsDict[sectionKey].id ? true : undefined
        }
      }
    }));
  };

  const updateSpecialSectionConfig = (sectionKey, configKey, value) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      const currentConfig = section.special_section_config || {};
      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [sectionKey]: {
            ...section,
            special_section_config: { ...currentConfig, [configKey]: value },
            isModified: section.id ? true : undefined
          }
        }
      };
    });
  };

  const updateAgentField = (sectionKey, agentKey, field, value) => {
    isUserEditRef.current = true;
    if (field === 'agent_context' || field === 'additional_context') {
      const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
      if (agentId) {
        setAgentQuickEdit(prev => ({ ...prev, [agentId]: false }));
        setPromptVersionPreviewAgents(prev => { const n = new Set(prev); n.delete(agentId); return n; });
      }
    }
    if (field === 'document_mappings') {
      const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
      if (agentId) {
        setAgentFeedback(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentQuickEdit(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentsWithComments(prev => { const next = new Set(prev); next.delete(agentId); return next; });
        setAgentSelectedText(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentSelectedContext(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentPreviewOpen(prev => { const next = { ...prev }; delete next[agentId]; return next; });
      }
    }
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      const agent = section.agentsDict[agentKey];
      const isPromptField = field === 'agent_context' || field === 'additional_context';
      const nextAgentContext = field === 'agent_context' ? value : (agent.agent_context || '');
      const nextAdditionalContext = field === 'additional_context' ? value : (agent.additional_context || '');
      const promptModified = isPromptField
        ? nextAgentContext !== (agent._savedAgentContext || '') ||
        nextAdditionalContext !== (agent._savedAdditionalContext || '')
        : !!agent._promptModified;
      const modifiedOutsidePrompt = !!agent._modifiedOutsidePrompt ||
        (agent.isModified === true && !agent._promptModified);
      const nextModifiedOutsidePrompt = isPromptField ? modifiedOutsidePrompt : true;

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [sectionKey]: {
            ...section,
            agentsDict: {
              ...section.agentsDict,
              [agentKey]: {
                ...agent,
                [field]: value,
                _promptModified: promptModified,
                _modifiedOutsidePrompt: nextModifiedOutsidePrompt,
                isModified: agent.id
                  ? nextModifiedOutsidePrompt || promptModified
                  : undefined
              }
            },
            hasModifiedAgents: true
          }
        }
      };
    });
  };

  // AMEND 2 (temporary): amendVersion (1 or 2) picks which amend colouring an Amend turned on here runs.
  const applyGlobalAgentSetting = (setting, value, amendVersion) => {
    Object.entries(formData.sectionsDict || {}).forEach(([sectionKey, section]) => {
      (section?.agentOrder || []).forEach((agentKey) => {
        const agent = section.agentsDict?.[agentKey];
        // Locked, rich-formatted and static-text agents are skipped — their per-agent Extend/Amend
        // toggles are disabled too. Static matters most here: the user never picked that agent, but
        // without the skip a template-wide Extend reaches it and the next save raises on it.
        if (!agent || agent.is_locked || agent.rich_formatting || agent.is_static_text) return;
        if (setting === 'preserve_response' || setting === 'amend_response') {
          if (value && agent.status !== 'COMPLETED') return;
          const other = setting === 'preserve_response' ? 'amend_response' : 'preserve_response';
          updateAgentField(sectionKey, agentKey, setting, value);
          if (setting === 'amend_response' && value) {
            updateAgentField(sectionKey, agentKey, 'amend_version', amendVersion === 2 ? 2 : 1); // AMEND 2 (temporary)
          }
          if (value) {
            updateAgentField(sectionKey, agentKey, other, false);
          } else {
            updateAgentField(sectionKey, agentKey, 'response_colour', null);
          }
          // Match Mapped Style only lives under Amend: drop it whenever Amend ends up off.
          if (setting === 'preserve_response' ? value : !value) {
            updateAgentField(sectionKey, agentKey, 'match_mapped_style', false);
          }
        } else if (setting === 'match_mapped_style') {
          if (agent.amend_response) updateAgentField(sectionKey, agentKey, 'match_mapped_style', value);
        } else if (setting === 'response_colour') {
          updateAgentField(sectionKey, agentKey, 'response_colour', value);
        }
      });
    });
  };

  const openDocumentSelector = (sectionKey, agentKey) => {
    setCurrentAgentIndex({ sectionKey, agentKey });
    setDocumentSelectorOpen(true);
  };

  const handleDocumentSelection = (selectedDocIds) => {
    const { sectionKey, agentKey } = currentAgentIndex;
    const documentMappings = selectedDocIds.map(docId => ({
      doc_id: docId,
      selected: true
    }));

    updateAgentField(sectionKey, agentKey, 'document_mappings', documentMappings);
    setDocumentSelectorOpen(false);
  };

  const moveSectionOrder = (sectionKey, direction) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      const sectionIsSummary = section.is_summary_section;
      const parentKey = section.parent_section_id;

      // Get siblings at same level — same type only (S-series with S-series, ordinary with ordinary)
      const siblings = prev.sectionOrder.filter(key => {
        const siblingSection = prev.sectionsDict[key];
        if (!siblingSection) return false;
        if (!!siblingSection.is_summary_section !== !!sectionIsSummary) return false;
        if (parentKey) return siblingSection.parent_section_id === parentKey;
        return !siblingSection.parent_section_id;
      }).sort((a, b) => {
        const orderingA = prev.sectionsDict[a]?.section_ordering_id || '0';
        const orderingB = prev.sectionsDict[b]?.section_ordering_id || '0';
        return naturalCompare(orderingA, orderingB);
      });

      const currentSiblingIndex = siblings.indexOf(sectionKey);
      const targetSiblingIndex = direction === 'up' ? currentSiblingIndex - 1 : currentSiblingIndex + 1;

      if (targetSiblingIndex < 0 || targetSiblingIndex >= siblings.length) {
        return prev;
      }

      // Swap the ordering_ids
      const targetSectionKey = siblings[targetSiblingIndex];

      const currentOrderingId = prev.sectionsDict[sectionKey].section_ordering_id;
      const targetOrderingId = prev.sectionsDict[targetSectionKey].section_ordering_id;

      let updatedSectionsDict = { ...prev.sectionsDict };

      // Swap ordering IDs
      updatedSectionsDict[sectionKey] = {
        ...updatedSectionsDict[sectionKey],
        section_ordering_id: targetOrderingId,
        isModified: updatedSectionsDict[sectionKey].id ? true : undefined
      };

      updatedSectionsDict[targetSectionKey] = {
        ...updatedSectionsDict[targetSectionKey],
        section_ordering_id: currentOrderingId,
        isModified: updatedSectionsDict[targetSectionKey].id ? true : undefined
      };

      // Update children ordering IDs for both sections
      const currentDescendants = findAllDescendants(sectionKey, updatedSectionsDict, prev.sectionOrder);
      const targetDescendants = findAllDescendants(targetSectionKey, updatedSectionsDict, prev.sectionOrder);

      // Recalculate children of current section
      currentDescendants.forEach(childKey => {
        const child = updatedSectionsDict[childKey];
        if (child && child.section_ordering_id) {
          const childParts = child.section_ordering_id.split('.');
          childParts[childParts.length - (childParts.length - currentOrderingId.split('.').length)] = targetOrderingId.split('.').pop();
          const newChildOrderingId = child.section_ordering_id.replace(currentOrderingId, targetOrderingId);
          updatedSectionsDict[childKey] = {
            ...child,
            section_ordering_id: newChildOrderingId,
            isModified: child.id ? true : undefined
          };
        }
      });

      // Recalculate children of target section
      targetDescendants.forEach(childKey => {
        const child = updatedSectionsDict[childKey];
        if (child && child.section_ordering_id) {
          const newChildOrderingId = child.section_ordering_id.replace(targetOrderingId, currentOrderingId);
          updatedSectionsDict[childKey] = {
            ...child,
            section_ordering_id: newChildOrderingId,
            isModified: child.id ? true : undefined
          };
        }
      });

      return {
        ...prev,
        sectionsDict: updatedSectionsDict
      };
    });
  };
  const moveAgentOrder = (sectionKey, agentKey, direction) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      const currentIndex = section.agentOrder.indexOf(agentKey);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex >= 0 && targetIndex < section.agentOrder.length) {
        const newAgentOrder = [...section.agentOrder];
        [newAgentOrder[currentIndex], newAgentOrder[targetIndex]] =
          [newAgentOrder[targetIndex], newAgentOrder[currentIndex]];

        const updatedAgentsDict = { ...section.agentsDict };
        newAgentOrder.forEach((agentKey, index) => {
          const agent = updatedAgentsDict[agentKey];
          if (agent) {
            updatedAgentsDict[agentKey] = {
              ...agent,
              agent_ordering_id: index + 1,
              isModified: agent.id ? true : undefined
            };
          }
        });

        return {
          ...prev,
          sectionsDict: {
            ...prev.sectionsDict,
            [sectionKey]: {
              ...section,
              agentsDict: updatedAgentsDict,
              agentOrder: newAgentOrder,
              hasModifiedAgents: true
            }
          }
        };
      }

      return prev;
    });
  };

  const canMoveSectionUp = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    const sectionIsSummary = section.is_summary_section;
    const parentKey = section.parent_section_id;
    const siblings = formData.sectionOrder.filter(key => {
      const siblingSection = formData.sectionsDict[key];
      if (!siblingSection) return false;
      if (!!siblingSection.is_summary_section !== !!sectionIsSummary) return false;
      return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
    }).sort((a, b) => {
      const orderingA = formData.sectionsDict[a]?.section_ordering_id || '0';
      const orderingB = formData.sectionsDict[b]?.section_ordering_id || '0';
      return naturalCompare(orderingA, orderingB);
    });

    return siblings.indexOf(sectionKey) > 0;
  };

  const canMoveSectionDown = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    const sectionIsSummary = section.is_summary_section;
    const parentKey = section.parent_section_id;
    const siblings = formData.sectionOrder.filter(key => {
      const siblingSection = formData.sectionsDict[key];
      if (!siblingSection) return false;
      if (!!siblingSection.is_summary_section !== !!sectionIsSummary) return false;
      return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
    }).sort((a, b) => {
      const orderingA = formData.sectionsDict[a]?.section_ordering_id || '0';
      const orderingB = formData.sectionsDict[b]?.section_ordering_id || '0';
      return naturalCompare(orderingA, orderingB);
    });

    return siblings.indexOf(sectionKey) < siblings.length - 1;
  };

  const handleSaveAgentResponse = (agentId, editedData) => {
    isUserEditRef.current = true;

    // Remember deletions outside of formData so they survive a full refetch
    // (e.g. after Save Preset triggers handleRefresh -> fetchTemplateDetails),
    // even if the backend doesn't echo them back.
    if (editedData?.deleted_table_ids?.length || editedData?.deleted_graph_ids?.length || editedData?.deleted_image_ids?.length) {
      const prior = deletedItemsByAgentRef.current[agentId] || {};
      deletedItemsByAgentRef.current = {
        ...deletedItemsByAgentRef.current,
        [agentId]: {
          deleted_table_ids: Array.from(new Set([...(prior.deleted_table_ids || []), ...(editedData.deleted_table_ids || [])])),
          deleted_graph_ids: Array.from(new Set([...(prior.deleted_graph_ids || []), ...(editedData.deleted_graph_ids || [])])),
          deleted_image_ids: Array.from(new Set([...(prior.deleted_image_ids || []), ...(editedData.deleted_image_ids || [])])),
        }
      };
    }

    setFormData(prev => {
      const newSectionsDict = { ...prev.sectionsDict };
      for (const sKey of Object.keys(newSectionsDict)) {
        const agentsDict = newSectionsDict[sKey].agentsDict;
        if (!agentsDict) continue;
        for (const aKey of Object.keys(agentsDict)) {
          if (agentsDict[aKey].id === agentId) {
            // The editor only sends tables changed in the current session, so carry
            // forward table edits from an earlier unsaved session that this payload
            // doesn't include (unless the table was deleted now).
            const priorTables = agentsDict[aKey].editedResponse?.tables || [];
            const newTableIds = new Set((editedData?.tables || []).map(t => t.table_id));
            const deletedTableIds = new Set(editedData?.deleted_table_ids || []);
            const mergedEditedData = {
              ...editedData,
              tables: [
                ...priorTables.filter(t => !newTableIds.has(t.table_id) && !deletedTableIds.has(t.table_id)),
                ...(editedData?.tables || []),
              ],
            };
            newSectionsDict[sKey] = {
              ...newSectionsDict[sKey],
              agentsDict: {
                ...agentsDict,
                [aKey]: {
                  ...agentsDict[aKey],
                  editedResponse: mergedEditedData,
                  isModified: true
                }
              },
              hasModifiedAgents: true
            };
            return { ...prev, sectionsDict: newSectionsDict };
          }
        }
      }
      return prev;
    });
  };

  // This index is handed to every agent, so a fresh object on each render breaks
  // memoisation across the whole template. formData's identity churns on every
  // keystroke and toggle, but the index only reads each agent's id, ordering and
  // response/editedResponse — so the previous result is reused whenever those
  // specific inputs are unchanged (editing a prompt, feedback, reference ids or
  // paragraph style cannot affect it).
  const referenceIndexCacheRef = useRef(null);

  const referenceIndex = useMemo(() => {
    const sectionOrder = formData?.sectionOrder || [];
    const sectionsDict = formData?.sectionsDict || {};

    const signature = [];
    sectionOrder.forEach(sk => {
      const sec = sectionsDict[sk];
      if (!sec || sec.isDeleted) return;
      (sec.agentOrder || []).forEach(ak => {
        const ag = sec.agentsDict?.[ak];
        if (!ag || ag.isDeleted) return;
        signature.push(ag.id, ag.agent_ordering_id, ag.response, ag.editedResponse);
      });
    });

    const cached = referenceIndexCacheRef.current;
    if (cached
      && cached.signature.length === signature.length
      && cached.signature.every((v, i) => v === signature[i])) {
      return cached.value;
    }

    const idToNumber = {};
    const idToArtifact = {};
    const idToReferencingAgents = {};

    const addRef = (id, agentId, agentLabel) => {
      if (!id) return;
      if (!idToReferencingAgents[id]) idToReferencingAgents[id] = [];
      if (!idToReferencingAgents[id].some(a => a.agentId === agentId)) {
        idToReferencingAgents[id].push({ agentId, agentLabel });
      }
    };

    sectionOrder.forEach(sk => {
      const sec = sectionsDict[sk];
      if (!sec || sec.isDeleted) return;
      (sec.agentOrder || []).forEach(ak => {
        const ag = sec.agentsDict?.[ak];
        if (!ag || ag.isDeleted) return;
        const resp = ag.response || {};
        const editedTablesById = (ag.editedResponse?.tables || []).reduce((acc, table) => {
          if (table?.table_id) acc[table.table_id] = table;
          return acc;
        }, {});
        const editedGraphsById = (ag.editedResponse?.graphs || []).reduce((acc, graph) => {
          if (graph?.graph_id) acc[graph.graph_id] = graph;
          return acc;
        }, {});
        const editedImagesById = (ag.editedResponse?.images || []).reduce((acc, image) => {
          if (image?.image_id) acc[image.image_id] = image;
          return acc;
        }, {});

        (resp.graphs || []).forEach((g, idx) => {
          if (!g.graph_id) return;
          const figureNumber = g.graph_number ?? (idx + 1);
          const override = editedGraphsById[g.graph_id] || {};
          idToNumber[g.graph_id] = { kind: 'figure', number: figureNumber };
          idToArtifact[g.graph_id] = {
            kind: 'graph',
            data: {
              ...g,
              ...('subtitle' in override && { subtitle: override.subtitle }),
              ...('title' in override && { title: override.title }),
              figureNumber,
            },
          };
        });
        (resp.images || []).forEach((im, idx) => {
          if (!im.image_id) return;
          const figureNumber = im.img_num ?? (idx + 1);
          const override = editedImagesById[im.image_id] || {};
          idToNumber[im.image_id] = { kind: 'figure', number: figureNumber };
          idToArtifact[im.image_id] = {
            kind: 'image',
            data: {
              ...im,
              ...('subtitle' in override && { subtitle: override.subtitle }),
              ...('footer' in override && { footer: override.footer }),
              figureNumber,
            },
          };
        });
        (resp.tables || []).forEach((t, idx) => {
          if (!t.table_id) return;
          const tableNumber = t.table_number ?? (idx + 1);
          const override = editedTablesById[t.table_id] || {};
          idToNumber[t.table_id] = { kind: 'table', number: tableNumber };
          idToArtifact[t.table_id] = {
            kind: 'table',
            data: {
              ...t,
              html: override.html ?? t.html,
              ...('footnote' in override && { footnote: override.footnote }),
              tableNumber,
            },
          };
        });

        const agentLabel = `Agent ${ag.agent_ordering_id ?? ag.id}`;
        const searchable = [
          ag.editedResponse?.markdown ?? resp.markdown_raw ?? resp.markdown ?? '',
          ...(resp.tables || []).flatMap(t => [t.html || '', t.footnote || '']),
          ...(resp.graphs || []).flatMap(g => [g.subtitle || '', g.title || '']),
          ...(resp.images || []).flatMap(im => [im.subtitle || '', im.footer || '']),
        ].join('\n');
        const matches = searchable.match(/\[(agent_[^\]]+)\]/g) || [];
        matches.forEach(m => addRef(m.slice(1, -1), ag.id, agentLabel));
      });
    });

    const value = { idToNumber, idToArtifact, idToReferencingAgents };
    referenceIndexCacheRef.current = { signature, value };
    return value;
  }, [formData]);

  const handleSaveEdits = async () => {
    if (!formData.template_name.trim()) {
      onError('Preset name is required');
      return;
    }

    if (!formData.fabric_id) {
      onError('Please select a project');
      return;
    }

    if (formData.sectionOrder.length === 0) {
      onError('At least one section is required');
      return;
    }

    saveCurrentState();
    setIsLoading(true);

    if (onProcessingStart) {
      onProcessingStart('Saving Preset changes');
    }

    try {
      await performSaveTemplate();
      handleRefresh();
      console.log('Preset updated successfully');
    } catch (error) {
      console.error('Error saving Preset:', error);
      onError(error.message || 'Failed to save Preset');
    } finally {
      setIsLoading(false);
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const getRunModalBodyContent = () => {
    const isSingleAgent = runType === 'single_agent';
    const isSelectiveRun = runType === 'selective_run';

    // Get agents that reference (depend on) the current agent
    const referencedByLabels = isSingleAgent && singleAgentExecution.agentId
      ? getReferencedByAgentLabels(singleAgentExecution.agentId)
      : [];

    return (
      <div>
        <>
          {hasPendingChanges && (
            <div className="ev-alert-card-warning mb-3">
              <i className="bi bi-info-circle me-2"></i>
              You have unsaved changes. They will be saved before running. To discard, click Refresh first.
            </div>
          )}
          <div className="ev-alert-card-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Important:</strong> This action will {
              isSelectiveRun ? `run ${selectedAgentIds.size} selected agent(s)` :
                isSingleAgent ? 'run this agent only' :
                  'run all agents'
            }.
          </div>
          {/* Master Feedback block temporarily hidden
            <div 
              className="ev-card-3 ev-radius-small mb-3 overflow-hidden"
              style={{ border: masterFeedback.trim() ? 'var(--px-1) solid var(--color2)' : 'var(--px-1) solid var(--color17)', backgroundColor: '#fff' }}
            >
              <div 
                className="d-flex align-items-center justify-content-between ev-padding-xsmall cursor-pointer"
                onClick={() => document.getElementById('master-feedback-textarea')?.focus()}
              >
                <span className="ev-fs-12" style={{ color: '#333' }}>
                  <i className="bi bi-chat-square-text me-1"></i>
                  Master Feedback
                  <span className="ev-fs-11 ms-1" style={{ opacity: 0.6 }}>(optional)</span>
                </span>
                {masterFeedback.trim() && (
                  <span className="badge ev-btn-xsmall ev-fs-10" style={{ backgroundColor: 'var(--color2)', color: 'var(--color3)' }}>
                    Active
                  </span>
                )}
              </div>
              <textarea
                id="master-feedback-textarea"
                className="w-100 ev-fs-12 border-0 px-2 pb-2"
                rows={2}
                placeholder="Instructions for all agents — e.g., Use recent data only, keep paragraphs concise..."
                value={masterFeedback}
                onChange={(e) => setMasterFeedback(e.target.value)}
                style={{ 
                  outline: 'none', 
                  resize: 'vertical',
                  backgroundColor: 'transparent',
                  color: '#333'
                }}
              />
            </div>
            */}
        </>
        <div className="mb-2">
          <strong>Preset:</strong> {formData.template_name}
        </div>
        <div className="mb-2">
          <strong>Project:</strong> {formData.fabric_name}
        </div>
        <div className="mb-2">
          {isSelectiveRun ? (
            <>
              <strong>Action:</strong> Run {selectedAgentIds.size} selected agent(s)
            </>
          ) : isSingleAgent ? (
            <>
              <strong>Action:</strong> Run single agent only
              {singleAgentExecution.feedback && (() => {
                const runAgentId = singleAgentExecution.agentId;
                const reportTexts = getReportAnnotationTexts(runAgentId);
                const manualText = (agentSelectedText[runAgentId] || '').trim();
                const manualContext = agentSelectedContext[runAgentId];
                const passages = reportTexts.length > 0 ? reportTexts : (manualText ? [manualText] : []);
                if (passages.length === 0) {
                  return (
                    <div className="mt-2">
                      <strong>Feedback:</strong> {singleAgentExecution.feedback}
                    </div>
                  );
                }
                const regardingLabel = (idx) => {
                  if (reportTexts.length === 0) return manualContext ? `Regarding this text from a ${manualContext}` : 'Regarding this text';
                  return `Regarding this text${reportTexts.length > 1 ? ` (${idx + 1})` : ''}`;
                };
                return (
                  <div className="mt-2">
                    <strong>Feedback:</strong>
                    <div className="scrollable-element mt-1 vh-15">
                      {passages.map((text, idx) => (
                        <div key={idx} className="original-comment-list-item">
                          <strong>{regardingLabel(idx)}:</strong> "{text}"
                        </div>
                      ))}
                    </div>
                    <div className="mt-2">
                      <strong>Change requested:</strong> {agentFeedback[singleAgentExecution.agentId] || ''}
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <>
              <strong>Action:</strong> Run all agents
            </>
          )}
        </div>
        {isSingleAgent && referencedByLabels.length > 0 && (
          <div className="ev-alert-card-warning mt-3 warning-list-scroll">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> The Table/Figure/Graph references of the following cross referred agents will get Unlinked, please re-execute them to link it.
            <ul className="mb-0 mt-1">
              {referencedByLabels.map((label, idx) => (
                <li key={idx}>{label}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  // Check if there are any agents with feedback
  // Called several times per render from the toolbar; compute once.
  const anyAgentHasFeedback = useMemo(
    () => Object.values(agentFeedback).some(feedback => feedback && feedback.trim()),
    [agentFeedback]
  );
  const hasAgentsWithFeedback = () => anyAgentHasFeedback;

  const hasAnyQuickFeedbackAgent = () => {
    return Object.entries(agentFeedback).some(([agentId, feedback]) =>
      feedback && feedback.trim() && agentQuickEdit[agentId]
    );
  };

  const hasSelectedQuickFeedbackAgent = () => {
    return Array.from(selectedAgentIds).some(id => agentQuickEdit[id]);
  };

  // Get section highlight class
  const getSectionHighlightClass = (sectionKey) => {
    const hasDirectFeedback = sectionHasFeedback(sectionKey);
    const isParent = isParentOfFeedbackSection(sectionKey);

    if (hasDirectFeedback) {
      return 'section-with-feedback-direct';
    } else if (isParent) {
      return 'section-with-feedback-parent';
    }
    return '';
  };


  const AUTOGEN_SECTION_TYPES = [
    { value: '', label: 'Autogen' },
    { value: 'TABLE_OF_CONTENTS', label: 'Table of Contents' },
    { value: 'LIST_OF_FIGURES', label: 'List of Figures' },
    { value: 'LIST_OF_TABLES', label: 'List of Tables' },
    { value: 'ABBREVIATIONS', label: 'Abbreviations' },
  ];

  const AUTOGEN_TYPE_LABELS = {
    TABLE_OF_CONTENTS: 'Table of Contents',
    LIST_OF_FIGURES: 'List of Figures',
    LIST_OF_TABLES: 'List of Tables',
    ABBREVIATIONS: 'Abbreviations',
  };

  // Returns the sectionKey already holding this special type, if any.
  const getAutogenSectionKeyUsing = (autogenType) => {
    if (!autogenType) return null;
    return Object.keys(formData.sectionsDict).find((key) => {
      const sec = formData.sectionsDict[key];
      if (!sec) return false;
      return sec.autogen_after === autogenType;
    }) || null;
  };
  // Stable identities for everything handed to the memoised AgentResponseSlot.
  // Without these the memo is defeated on every parent render.
  const stableTranslateAgent = useStableCallback(handleTranslateAgent);
  const stableSaveAgentResponse = useStableCallback(handleSaveAgentResponse);
  const stableUpdateAgentField = useStableCallback(updateAgentField);
  const stableToggleParagraphDropdown = useStableCallback(toggleParagraphDropdown);
  const stableCloseParagraphDropdown = useStableCallback(closeParagraphDropdown);
  const stableApplyWritingRules = useStableCallback(handleApplyWritingRules);

  const renderSection = (sectionKey) => {
    // Only render if visible by filter AND expanded logic
    if (!isSectionVisibleUnderFilter(sectionKey) || !isSectionVisible(sectionKey)) return null;
    const section = formData.sectionsDict[sectionKey];
    if (!section) return null;

    const level = (section.section_ordering_id?.split('.').length || 1) - 1;
    const effectiveAutogenAfter = section.autogen_after || null;
    const indentPx = level * 10;

    const baseIndentWidth = 10;
    const indentWidthPercent = baseIndentWidth + (level * 0.75);
    const sectionWidthPercent = 100;

    const indentStyle = {
      marginLeft: `${indentPx}px`,
      minWidth: `${indentWidthPercent}%`,
      textAlign: "right"
    };

    const sectionHasChildren = hasChildren(sectionKey);
    const isExpanded = expandedSections[sectionKey];
    const sectionHasRunning = hasRunningAgents(sectionKey);
    const sectionHasContent = hasAnyContent(sectionKey);
    const highlightClass = getSectionHighlightClass(sectionKey);

    // Section checkbox state calculations based on current filter
    const visibleIdsInSection = getVisibleAgentIdsInSection(sectionKey);
    const isSectionChecked = visibleIdsInSection.length > 0 && visibleIdsInSection.every(id => selectedAgentIds.has(id));
    const isSectionIndeterminate = visibleIdsInSection.length > 0 && visibleIdsInSection.some(id => selectedAgentIds.has(id)) && !isSectionChecked;

    return (
      <div key={sectionKey}>
        <div className={`d-flex align-items-center mb-2 ${highlightClass}`}>
          <div className="d-flex justify-content-end" style={indentStyle}>
            <span className="ev-message-card2 me-1 ms-1 mt-2 template-section-order-card d-flex align-items-center" >
              <button
                type="button"
                className="btn p-0 me-1 ev-small-p-blue border-0 fs-11 bg-transparent"
                onClick={() => toggleSectionExpansion(sectionKey)}
                disabled={!hasAnyContent(sectionKey)}
              >
                <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
              </button>
              {/* Checkbox for Section */}
              <input
                type="checkbox"
                className="form-check-input  me-1 mb-1"
                checked={isSectionChecked}
                ref={el => { if (el) el.indeterminate = isSectionIndeterminate; }}
                onChange={(e) => handleSectionCheckbox(sectionKey, e.target.checked)}
                disabled={visibleIdsInSection.length === 0}
                title="Select/Deselect visible agents in this section"
              />
              {section.is_summary_section
                ? <><i className="bi bi-journal-text me-1"></i>{section.section_ordering_id || 'S'}</>
                : section.section_ordering_id}
            </span>
          </div>
          <div className="template-viewer-section-name-col ev-radius-xsmall mt-2" title={section.section_name || undefined}>
            <input
              type="text"
              className="formField2 ev-btn-small ev-fs-12 ev-radius-xsmall template-viewer-sec-name-input"
              placeholder="Section Name"
              value={section.section_name}
              onChange={(e) => updateSectionField(sectionKey, 'section_name', e.target.value)}
              disabled={!hasSectionManagementAccess}
            />
            <div className="text-end template-viewer-sec-buttons">
              {vmConfig && hasSectionManagementAccess && (
                <button
                  type="button"
                  className="ev-message-card2 ev-message-card5 ev-btn-xsmall ev-bold-reg ev-fs-11 ms-1"
                  onClick={() => openVMSelector(sectionKey)}
                >
                  <i className="bi bi-chat-dots-fill"></i> Link Value Messages ({getSelectedVMCount(sectionKey)})
                </button>
              )}
              {section.is_summary_section && hasSectionManagementAccess && (
                <div
                  ref={openSpecialSectionConfig === sectionKey ? specialSectionConfigRef : null}
                  className="position-relative d-inline-block ms-1"
                >
                  <button
                    type="button"
                    className="no-wrap ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg border-0 d-flex align-items-center"
                    onClick={() => setOpenSpecialSectionConfig(prev => prev === sectionKey ? null : sectionKey)}
                  >
                    Config
                    <i className="bi bi-chevron-down ms-1" />
                  </button>

                  {openSpecialSectionConfig === sectionKey && (() => {
                    const ssc = section.special_section_config || {};
                    return (
                      <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2" style={{ minWidth: '220px' }}>
                        <span className="ev-small-p-blue ev-bold-med px-2 pt-1 pb-1 text-start text-nowrap">Section Config</span>

                        {/* Outside Border */}
                        <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25">
                          <span className="d-flex align-items-center gap-2 text-nowrap">
                            <i className="bi bi-box-arrow-up-right" />
                            <span>Outside Border</span>
                          </span>
                          <div
                            className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${ssc.is_outside_border ? 'agent-lock-toggle--on' : ''}`}
                            onClick={() => updateSpecialSectionConfig(sectionKey, 'is_outside_border', !ssc.is_outside_border)}
                          >
                            <div className="agent-lock-toggle-knob" />
                          </div>
                        </div>

                        {/* Heading BG Color */}
                        <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-0 rounded-2 border border-secondary border-opacity-25" style={{ minHeight: 0, ...(ssc.is_outside_border && { opacity: 0.4, pointerEvents: 'none' }) }}>
                          <span className="d-flex align-items-center gap-2 text-nowrap">
                            <i className="bi bi-paint-bucket" />
                            <span>Heading BG</span>
                          </span>
                          <span className="d-flex align-items-center" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                          <ColorPickerButton
                            value={ssc.heading_bg_color || '#FFFFFF'}
                            onChange={(color) => updateSpecialSectionConfig(sectionKey, 'heading_bg_color', color)}
                            icon={<i className="bi bi-square-fill" />}
                          />
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {!section.is_summary_section && (hasSectionManagementAccess || effectiveAutogenAfter) && (
                <div
                  className="position-relative d-inline-block ms-1"
                  ref={openSectionTypeDropdown === sectionKey ? sectionTypeDropdownRef : null}
                >
                  <button
                    type="button"
                    className={`${effectiveAutogenAfter ? 'ev-message-card7' : 'ev-btn-blue-outline-round'} ev-btn-xsmall ev-bold-reg ev-fs-11 avatar-tooltip-bottom`}
                    onClick={() => toggleSectionTypeDropdown(sectionKey)}
                    disabled={!hasSectionManagementAccess}
                    data-tooltip="Select/Change the position of TOC, LOF, LOT, Abbreviation."
                  >
                    {AUTOGEN_TYPE_LABELS[effectiveAutogenAfter] || 'Autogen'}
                    <i className="bi bi-chevron-down ms-1 ev-fs-10"></i>
                  </button>
                  {openSectionTypeDropdown === sectionKey && (
                    <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2 section-type-dropdown-panel">
                      <span className="ev-small-p-blue ev-bold-med px-1 mb-1 text-start">TOC Position</span>
                      {AUTOGEN_SECTION_TYPES.map((opt) => {
                        const usedByKey = opt.value ? getAutogenSectionKeyUsing(opt.value) : null;
                        const isTakenElsewhere = !!usedByKey && usedByKey !== sectionKey;
                        const usedByOrdering = isTakenElsewhere ? formData.sectionsDict[usedByKey]?.section_ordering_id : null;
                        const isCurrent = (effectiveAutogenAfter || '') === opt.value;
                        return (
                          <button
                            key={opt.value || 'ai-response'}
                            type="button"
                            className={`par-style-item d-flex align-items-center ev-fs-12 py-2 px-2 ${isCurrent ? 'active' : ''} ${isTakenElsewhere ? 'opacity-50 avatar-tooltip' : ''}`}
                            disabled={isTakenElsewhere}
                            data-tooltip={isTakenElsewhere ? `Already used in S.${usedByOrdering}` : ''}
                            onClick={() => {
                              updateSectionField(sectionKey, 'autogen_after', opt.value || null);
                              closeSectionTypeDropdown();
                            }}
                          >
                            <i className={`bi ${isCurrent ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {canSectionManagement && hasSectionManagementAccess && (
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg ev-fs-11 ms-1"
                  onClick={() => addSection(sectionKey)}
                >
                  <i className="bi bi-plus-circle-fill"></i> Section
                </button>
              )}
              {hasSectionManagementAccess && (
                <>
                  <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg ev-fs-11 ms-1"
                    onClick={() => addAgent(sectionKey)}
                  >
                    <i className="bi bi-plus-circle-fill"></i> Add AI Agent
                  </button>
                  <button
                    type="button"
                    className={`ev-btn-red-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10 ms-1 me-1 avatar-tooltip-bottom`}
                    onClick={() => deleteSection(sectionKey)}
                    disabled={sectionHasRunning}
                    data-tooltip={sectionHasRunning ? "Wait for my agents to complete" : "Delete section"}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </>
              )}
            </div>
          </div>
          {hasSectionManagementAccess && (
          <div className="col-1">
            <div className="ev-message-card2 me-1 ms-2 template-caret-card margin-top-10" >
              <button
                type="button"
                className="template-ordering-btn"
                onClick={() => moveSectionOrder(sectionKey, 'up')}
                disabled={!canMoveSectionUp(sectionKey)}
              >
                <i className="bi bi-caret-up-square"></i>
              </button>
              <button
                type="button"
                className="template-ordering-btn ms-1"
                onClick={() => moveSectionOrder(sectionKey, 'down')}
                disabled={!canMoveSectionDown(sectionKey)}
              >
                <i className="bi bi-caret-down-square"></i>
              </button>
            </div>
          </div>
          )}
        </div>


        {isExpanded && section.agentOrder?.map((agentKey, agentIndex) => {
          const agent = section.agentsDict[agentKey];
          if (!agent) return null;
          // Hide if not matching filter logic
          if (!agent || !isAgentVisible(agent)) return null;

          const agentRunning = isAgentRunning(agent);
          const agentHasComments = hasComments(sectionKey, agentKey);
          const commentData = getCommentData(sectionKey, agentKey);
          const isLocked = agent.is_locked || false;
          // Static-text agent: the section prose is authored on the template and the agent only
          // fills its placeholders. Its prompt still runs, so prompt editing stays open; what is
          // gated below is everything that contradicts static mode — Extend/Amend (the backend
          // rejects the combination) and the table structure (which silently wins over it).
          const isStatic = agent.is_static_text || false;
          const staticTooltip = 'Not available on a static-text agent';
          const staticCollapseKey = `${sectionKey}:${agentKey}`;
          const isStaticCollapsed = isStatic && !!collapsedStaticAgents[staticCollapseKey];
          // Meta tags already on this agent, lower-cased to match against templateTags.
          const agentTagKeys = new Set((agent.tags || []).map(t => (t || '').trim().toLowerCase()));
          // Pills to show in Agent Settings: the agent's own tags first — always
          // removable, even one no Data Folder carries any more (imported template) —
          // then the rest of the project's vocabulary, addable.
          const pillTags = [...(agent.tags || []), ...templateTags].filter((tag, i, all) =>
            tag && all.findIndex(o => (o || '').trim().toLowerCase() === tag.trim().toLowerCase()) === i
          );

          // Docs changed since last successful run — only a warning; feedback
          // stays allowed, but references in the response may be stale.
          const lastRunDocIds = Array.isArray(agent.last_run_doc_ids) ? agent.last_run_doc_ids : [];
          const currentDocIds = (agent.document_mappings || []).filter(d => d.selected).map(d => d.doc_id);
          const lastRunSet = new Set(lastRunDocIds);
          const currentSet = new Set(currentDocIds);
          const docsStale =
            agent.status === 'COMPLETED'
            && agent.last_run_doc_ids != null
            && (
              currentDocIds.length !== lastRunDocIds.length
              || currentDocIds.some(id => !lastRunSet.has(id))
              || lastRunDocIds.some(id => !currentSet.has(id))
            );

          const showNoDocumentsNotice = currentDocIds.length === 0 && (agent.reference_agent_ids || []).length === 0;
          // Writing rules ran on this exact version and produced no new one
          const showWritingRulesNoChange =
            writingRulesNoChange?.[agent.id] !== undefined
            && writingRulesNoChange[agent.id] === agent.versions?.[0]?.version;

          // Changed documents and edited / quick-feedback versions only show a red note;
          // neither blocks feedback.
          const isSourceManual = isLatestManualResponse(agent);
          const isQuickFeedbackMode = !!agentQuickEdit[agent.id];
          const showEditedWarning = isSourceManual && !isQuickFeedbackMode;
          const showDocsChangedWarning = docsStale;
          const showFeedbackWarning = showEditedWarning || showDocsChangedWarning;
          const canGiveFeedback = canRunTemplate && !isLocked && !isStatic;
          const showFeedbackSwitch = canGiveFeedback && !agent.is_static_text;
          const feedbackPlaceholder = isStatic
            ? 'Feedback is not available on a static text agent'
            : isLocked
            ? 'Unlock agent to provide feedback'
            : (isQuickFeedbackMode
                ? 'Describe changes to existing text, tables, or figures (Required)'
                : 'Refine past result — thorough (Optional)');

          return (
            <div key={agentKey} data-agent-id={agent.id}>
              <div className="d-flex mt-2 mb-2">
                <div style={indentStyle}>
                  {/* Empty space for alignment */}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ev-card-8 ev-radius-small ev-padding-small" >
                    <AgentHeaderRow className="row margin-bottom-1">
                    {({ versionCompact, settingsCompact, configureCompact }) => (
                    <>
                      <div className="col-6 pt-1 d-flex align-items-center">
                        {/* Agent Selection Checkbox */}
                        <input
                          type="checkbox"
                          className="form-check-input ms-1 me-2"
                          checked={agent.id ? selectedAgentIds.has(agent.id) : false}
                          onChange={() => agent.id && toggleAgentSelection(agent.id)}
                          disabled={!agent.id}
                          title={!agent.id ? "Save Preset first to select this agent" : "Select this agent"}
                        />
                        <span className={`agent-tag-popover-anchor ${agent.tags?.length ? '' : 'd-inline-flex'}`}>
                          <span className="ev-message-card3 ev-btn-small ev-fs-11 text-nowrap">
                            <i className="bi bi-robot"></i> S.{section.section_ordering_id}. Agent {agent.agent_ordering_id}
                          </span>
                          {agent.tags?.length > 0 && (
                            <div className="agent-tag-popover ev-radius-med">
                              <div className="fw-bold ev-fs-13 mb-1 text-white">
                                <i className="bi bi-tags-fill me-1"></i>Tags
                              </div>
                              <div className="agent-tag-popover-scroll d-flex flex-wrap gap-1">
                                {agent.tags.map((tag, idx) => (
                                  <span key={idx} className="agent-tag-pill ev-fs-11">{tag}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </span>

                        {/* --- USE THE NEW COMPONENT HERE --- */}
                        <VersionSelector
                          versions={agent.versions}
                          compact={versionCompact}
                          selectedVersionNumber={selectedVersions[agent.id]}
                          onShow={(version, versionName, source) => handleShowVersion(sectionKey, agentKey, agent.id, version, source)}
                          onRestore={(version, versionName) => promptRestoreVersion(agent.id, version, versionName)}
                          onCompare={(vA, vB) => handleCompareVersions(agent.id, vA, vB)}
                          canRestore={canHere(PERMISSIONS.AGENT_RESTORE_VERSION)}
                          disabled={agentRunning || isLocked}
                          disabledTooltip={agentRunning ? 'Agent is running' : isLocked ? 'Unlock agent to change version' : undefined}
                        />

                      </div>
                      <div className="col-6 text-end d-flex align-items-center justify-content-end gap-1" >
                        {/* Agent Settings Dropdown — Lock · Rich Format · Extend */}
                        {hasSectionManagementAccess && (
                        <div
                          ref={openAgentSettingsDropdown === agentKey ? settingsDropdownRef : null}
                          className={`position-relative d-inline-block${agentRunning ? ' avatar-tooltip' : ''}`}
                          data-tooltip={agentRunning ? 'Agent is running' : undefined}
                        >
                          <button
                            type="button"
                            className={`no-wrap ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg border-0 d-flex align-items-center${settingsCompact ? ' ev-radius-xsmall' : ''}${settingsCompact && !agentRunning ? ' avatar-tooltip' : ''}`}
                            onClick={() => { setOpenAgentTagPopover(null); setOpenAgentSettingsDropdown(prev => prev === agentKey ? null : agentKey); }}
                            disabled={agentRunning}
                            data-tooltip={settingsCompact && !agentRunning ? 'Agent Settings' : undefined}
                          >
                            <i className={`bi ${isLocked ? 'bi-lock-fill' : 'bi-sliders'}`} />
                            {!settingsCompact && (
                              <span data-fit="settings-label" className="d-inline-flex align-items-center ms-1">
                                Settings<i className="bi bi-chevron-down ms-1" />
                              </span>
                            )}
                            <span data-fit="settings-full" className="d-inline-flex align-items-center ms-1 position-absolute invisible text-nowrap pe-none" aria-hidden="true">
                              Settings<i className="bi bi-chevron-down ms-1" />
                            </span>
                          </button>

                          {openAgentSettingsDropdown === agentKey && (
                            <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2 min-w-200">
                              <span className="ev-small-p-blue ev-bold-med px-2 pt-1 pb-1 text-start text-nowrap">Agent Settings</span>

                              {/* Lock */}
                              <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25">
                                <span className="d-flex align-items-center gap-2 text-nowrap">
                                  <i className={`bi ${isLocked ? 'bi-lock-fill' : 'bi-unlock'}`} />
                                  <span>Lock</span>
                                </span>
                                <div
                                  className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${isLocked ? 'agent-lock-toggle--on' : ''}`}
                                  onClick={() => updateAgentField(sectionKey, agentKey, 'is_locked', !isLocked)}
                                >
                                  <div className="agent-lock-toggle-knob" />
                                </div>
                              </div>

                              {/* Rich Formatting */}
                              <div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked || !!agent.preserve_response || !!agent.amend_response ? 'opacity-50' : ''}`}>
                                <span className="d-flex align-items-center gap-2 text-nowrap">
                                  <i className="bi bi-stars" />
                                  <span>
                                    Color & Highlight
                                  </span>
                                </span>
                                <div
                                  className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${!!agent.rich_formatting ? 'agent-lock-toggle--on' : ''}`}
                                  onClick={() => { if (isLocked || !!agent.preserve_response || !!agent.amend_response) return; updateAgentField(sectionKey, agentKey, 'rich_formatting', !agent.rich_formatting); }}
                                >
                                  <div className="agent-lock-toggle-knob" />
                                </div>
                              </div>

                              {/* Extend — only when COMPLETED, and not on projects that hide Extend/Amend.
                                  Blocked on a static agent: the backend raises on the combination, which
                                  would fail the whole template save, not just this agent. */}
                              {showBulkExtendAmend && agent.status === 'COMPLETED' && (
                                <div
                                  className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked || isStatic || !!agent.rich_formatting ? 'opacity-50' : ''}`}
                                  title={isStatic ? staticTooltip : undefined}
                                >
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-layout-text-sidebar" />
                                    <span>Extend</span>
                                  </span>
                                  <div
                                    className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${!!agent.preserve_response ? 'agent-lock-toggle--on' : ''}`}
                                    onClick={() => {
                                      if (isLocked || isStatic || !!agent.rich_formatting) return;
                                      const next = !agent.preserve_response;
                                      updateAgentField(sectionKey, agentKey, 'preserve_response', next);
                                      if (next) {
                                        updateAgentField(sectionKey, agentKey, 'amend_response', false);
                                        updateAgentField(sectionKey, agentKey, 'match_mapped_style', false);
                                      } else {
                                        updateAgentField(sectionKey, agentKey, 'response_colour', null);
                                      }
                                    }}
                                  >
                                    <div className="agent-lock-toggle-knob" />
                                  </div>
                                </div>
                              )}

                              {/* Amend 1 / Amend 2 — same exclusion as Extend above.
                                  AMEND 2 (temporary): two amend colourings side by side, one on at a
                                  time; Amend 1 is the default. Revert to the single Amend toggle. */}
                              {showBulkExtendAmend && agent.status === 'COMPLETED' && [1, 2].map((amendVersion) => {
                                const amendOn = !!agent.amend_response && (agent.amend_version === 2 ? 2 : 1) === amendVersion;
                                return (
                                <div
                                  key={`amend-${amendVersion}`}
                                  className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked || isStatic || !!agent.rich_formatting ? 'opacity-50' : ''}`}
                                  title={isStatic ? staticTooltip : undefined}
                                >
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-pencil-square" />
                                    <span>{`Amend ${amendVersion}`}</span>
                                  </span>
                                  <div
                                    className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${amendOn ? 'agent-lock-toggle--on' : ''}`}
                                    onClick={() => {
                                      if (isLocked || isStatic || !!agent.rich_formatting) return;
                                      const next = !amendOn;
                                      updateAgentField(sectionKey, agentKey, 'amend_response', next);
                                      if (next) {
                                        updateAgentField(sectionKey, agentKey, 'amend_version', amendVersion);
                                        updateAgentField(sectionKey, agentKey, 'preserve_response', false);
                                      } else {
                                        updateAgentField(sectionKey, agentKey, 'response_colour', null);
                                        updateAgentField(sectionKey, agentKey, 'match_mapped_style', false);
                                      }
                                    }}
                                  >
                                    <div className="agent-lock-toggle-knob" />
                                  </div>
                                </div>
                                );
                              })}

                              {/* Match Mapped Style — an Amend sub-option, shown only while Amend is on.
                                  Adds a style-conformity rule to the amend prompt so new content
                                  follows the existing (mapped) response's writing style. */}
                              {showBulkExtendAmend && agent.status === 'COMPLETED' && !!agent.amend_response && (
                                <label
                                  className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 mb-0 cursor-pointer ${isLocked ? 'opacity-50' : ''}`}
                                  title="New content follows the same style as the mapped content"
                                >
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-type" />
                                    <span>Match Mapped Style</span>
                                  </span>
                                  <input
                                    type="checkbox"
                                    className="form-check-input m-0 cursor-pointer shadow-none flex-shrink-0"
                                    checked={!!agent.match_mapped_style}
                                    disabled={isLocked}
                                    onChange={(e) => {
                                      if (isLocked) return;
                                      updateAgentField(sectionKey, agentKey, 'match_mapped_style', e.target.checked);
                                    }}
                                  />
                                </label>
                              )}

                              {/* Color picker — when Extend or Amend is on */}
                              {agent.status === 'COMPLETED' && (!!agent.preserve_response || !!agent.amend_response) && (<div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked ? 'opacity-50' : ''}`}>
                                <span className="d-flex align-items-center gap-2 text-nowrap">
                                  <i className="bi bi-palette" />
                                  <span>Text Color</span>
                                </span>
                                <span className="d-flex align-items-center" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                                    <ColorPickerButton
                                      value={agent.response_colour || '#000000'}
                                      onChange={(colour) => { if (isLocked) return; updateAgentField(sectionKey, agentKey, 'response_colour', colour); }}
                                      icon={<i className="bi bi-alphabet-uppercase" />}
                                    />
                                  </span>
                              </div>
                              )}

                              {/* Meta Tags — the row opens a popover shaped like the Text Color
                                  one, so the dropdown keeps its width however long the project's
                                  tag vocabulary is. Click a pill to add it to this agent or
                                  remove it. Tags only; document mappings are Sync Docs' job. */}
                              {canSectionManagement && pillTags.length > 0 && (
                                <div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked ? 'opacity-50' : ''}`}>
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-tags" />
                                    <span>Meta Tags</span>
                                  </span>
                                  <div
                                    ref={openAgentTagPopover === agentKey ? tagPopoverRef : null}
                                    className="position-relative d-inline-block"
                                  >
                                    <button
                                      type="button"
                                      className="ev-btn-small ev-message-card2 ev-card-3 ev-btn-border-square d-inline-flex align-items-center gap-1 ev-fs-11"
                                      onClick={() => { if (isLocked) return; setOpenAgentTagPopover(prev => prev === agentKey ? null : agentKey); }}
                                    >
                                      <span>{agentTagKeys.size || 'None'}</span>
                                      <i className="bi bi-chevron-down" />
                                    </button>

                                    {openAgentTagPopover === agentKey && (
                                      <div
                                        className="ev-message-card2 ev-card-3 position-absolute top-100 z-1000 min-w-300 p-2 rounded-2"
                                        style={{ right: 'var(--px-0)' }}
                                      >
                                        <p className="ev-fs-10 fw-bold mb-1 text-start">Meta Tags</p>
                                        <div className="d-flex flex-wrap gap-1 agent-tag-settings-list">
                                          {pillTags.map((tag) => {
                                            const on = agentTagKeys.has(tag.trim().toLowerCase());
                                            return (
                                              <button
                                                key={tag}
                                                type="button"
                                                className={`sync-docs-agent-tag-pill ev-fs-11 d-inline-flex align-items-center gap-1 ${on ? 'is-on' : 'is-off'}`}
                                                title={on ? `Remove "${tag}"` : `Add "${tag}"`}
                                                onClick={() => {
                                                  if (isLocked) return;
                                                  const key = tag.trim().toLowerCase();
                                                  const next = on
                                                    ? (agent.tags || []).filter(t => (t || '').trim().toLowerCase() !== key)
                                                    : [...(agent.tags || []), tag];
                                                  updateAgentField(sectionKey, agentKey, 'tags', next.length ? next : null);
                                                }}
                                              >
                                                {tag}
                                                <span className="agent-tag-state">
                                                  <i className={`bi ${on ? 'bi-x-lg' : 'bi-plus-lg'}`} />
                                                </span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        )}

                        <span className=""> <StatusBadgeSm status={agent.status} /></span>
                        {isStatic && (
                          <span
                            className="ev-message-card3-green ev-btn-xsmall ev-fs-11 text-nowrap"
                            title="This agent fills placeholders in authored static text. Editing the text is not available yet."
                          >
                            <i className="bi bi-file-earmark-text"></i> Static
                          </span>
                        )}

                        {hasSectionManagementAccess && (
                        <>
                        <button
                          type="button"
                          className={`ev-btn-blue-fill-round ev-bold-reg ev-btn-small avatar-tooltip text-nowrap${configureCompact ? ' ev-radius-xsmall' : ''}`}
                          onClick={() => openConfigureModal(sectionKey, agentKey)}
                          disabled={isLocked || agentRunning}
                          data-tooltip={agentRunning ? 'Agent is running' : isLocked ? 'Unlock agent to configure' : configureCompact ? 'Configure' : ''}
                        >
                          {/* configureCompact comes from AgentHeaderRow: last step when the header is
                              still too narrow after the version and Settings labels are shortened. */}
                          <i className="bi bi-gear-wide"></i>
                          {!configureCompact && <span data-fit="configure-label" className="ms-1">Configure</span>}
                          <span data-fit="configure-full" className="ms-1 position-absolute invisible text-nowrap pe-none" aria-hidden="true">Configure</span>
                        </button>
                        {/* Disabled on a static agent: a table structure wins over static text, so
                            picking one here would quietly turn the agent back into a normal one and
                            the authored text would stop being used, with nothing to show for it. */}
                        <div
                          className={`position-relative d-inline-block${isStatic ? ' avatar-tooltip' : ''}`}
                          data-tooltip={isStatic ? staticTooltip : undefined}
                        >
                          <TableStructureSelector
                            sectionKey={sectionKey}
                            agentKey={agentKey}
                            selectedId={agent.table_structure_id}
                            onSelect={(id) => updateAgentField(sectionKey, agentKey, 'table_structure_id', id)}
                            disabled={isLocked || isStatic}
                            structures={tableStructures}
                            loading={tableStructuresLoading}
                            isStatic={!!agent.is_static_text}
                          />
                        </div>
                        <button
                          type="button"
                          className={`ev-btn-red-fill-round ev-fs-10 ev-radius-xsmall ev-icon-btn-padding-small avatar-tooltip-bottom`}
                          onClick={() => deleteAgent(sectionKey, agentKey)}
                          disabled={isLocked || agentRunning}
                          data-tooltip={agentRunning ? "Agent is in running state" : isLocked ? "Unlock agent to delete" : "Delete agent"}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                        </>
                        )}
                      </div>
                    </>
                    )}
                    </AgentHeaderRow>
                    {/* <input
                      type="text"
                      className="formField4 mb-2 ev-btn-reg"
                      placeholder="Name"
                      value={agent.agent_name}
                      onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_name', e.target.value)}
                    /> */}
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
                      <div className="d-flex align-items-center gap-1">
                        <div
                          className="btn-group custom-btn-group-outer mb-1 "
                          role="group"
                        >
                          <button
                            type="button"
                            className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${!activePromptTab[agentKey] || activePromptTab[agentKey] === "prompt" ? "ev-btn-blue-fill-round" : "ev-btn-lightblue-fill-round"}`}
                            onClick={() =>
                              setActivePromptTab((prev) => ({
                                ...prev,
                                [agentKey]: "prompt",
                              }))
                            }
                          >
                            Prompt
                          </button>
                          <button
                            type="button"
                            className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${activePromptTab[agentKey] === "additional_context" ? "ev-btn-blue-fill-round" : "ev-btn-lightblue-fill-round"}`}
                            onClick={() =>
                              setActivePromptTab((prev) => ({
                                ...prev,
                                [agentKey]: "additional_context",
                              }))
                            }
                          >
                            Additional Context
                          </button>
                        </div>

                        {hasSectionManagementAccess && agent.prompt_versions && agent.prompt_versions.length > 0 && (
                          <PromptVersionSelector
                            promptVersions={agent.prompt_versions}
                            selectedPromptVersion={selectedPromptVersions[agent.id]}
                            onShow={(version) => handleShowPromptVersion(agent.id, version, agent.prompt_versions[0]?.version)}
                            onRestore={(version, versionName) => promptRestorePromptVersion(agent.id, version, versionName)}
                            isLocked={isLocked || agentRunning}
                            isRunning={agentRunning}
                            canRestore={canHere(PERMISSIONS.AGENT_RESTORE_PROMPT_VERSION)}
                          />
                        )}
                      </div>

                      {/* Prompt Translate Pill */}
                      <div className="position-relative">
                        <div className="ev-message-card1 ev-btn-xsmall d-flex align-items-center pe-1 ev-radius-large prompt-translate-pill">
                          <i className="bi bi-globe2 me-1"></i>
                          <span>{allLanguages.find(l => l.id === (agent.context_lang_id ?? 1))?.name ?? 'English'}</span>
                          {canHere(PERMISSIONS.AGENT_TRANSLATE_CONTEXT) && (
                          <button
                            type="button"
                            className="ev-btn-blue-fill-round ev-btn-xsmall ms-2 avatar-tooltip"
                            disabled={isLoading || isLocked || agentRunning || promptVersionPreviewAgents.has(agent.id)}
                            data-tooltip={agentRunning ? 'Agent is running' : isLocked ? 'Unlock agent to translate' : promptVersionPreviewAgents.has(agent.id) ? 'Restore prompt version to enable translate' : 'Translate prompt'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTranslateDropdown(
                                translateDropdown?.sectionKey === sectionKey && translateDropdown?.agentKey === agentKey
                                  ? null : { sectionKey, agentKey }
                              );
                            }}
                          >
                            <i className="bi bi-translate"></i> Translate
                          </button>
                          )}
                        </div>
                        {translateDropdown?.sectionKey === sectionKey && translateDropdown?.agentKey === agentKey && (
                          <AgentTranslationModal
                            currentLanguageId={agent.context_lang_id ?? 1}
                            onClose={() => setTranslateDropdown(null)}
                            onTranslate={(targetLangId) => handleTranslateAgentContext(sectionKey, agentKey, targetLangId)}
                          />
                        )}
                      </div>

                    </div>
                    <div className="mb-0">
                      {(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? (
                        <DeferredTextControl
                          className="formField3 ev-padding-xsmall ev-fs-12"
                          placeholder="Provide context here"
                          rows="4"
                          value={agent.agent_context || ''}
                          onCommit={(next) => updateAgentField(sectionKey, agentKey, 'agent_context', next)}
                          disabled={isLocked || promptVersionPreviewAgents.has(agent.id) || !hasSectionManagementAccess}
                        />
                      ) : (
                        <DeferredTextControl
                          className="formField3 ev-padding-xsmall ev-fs-12"
                          placeholder="Provide additional context here (optional)"
                          rows="4"
                          value={agent.additional_context || ''}
                          onCommit={(next) => updateAgentField(sectionKey, agentKey, 'additional_context', next)}
                          disabled={isLocked || promptVersionPreviewAgents.has(agent.id) || !hasSectionManagementAccess}
                        />
                      )}
                    </div>

                    {promptVersionPreviewAgents.has(agent.id) && (
                      <div className="d-flex align-items-center ev-card-10 ev-radius-xsmall px-2 py-1 mt-1">
                        <i className="bi bi-lock-fill ev-small-p-blue me-1"></i>
                        <span className="ev-fs-10 ev-small-p-dark">Viewing an old version — editing is disabled. Restore to make it active.</span>
                      </div>
                    )}

                    <div className="mt-1">
                      {agent.status === 'COMPLETED' && (
                        <div className="mb-2">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <div className="d-flex align-items-center gap-2">
                              <label className="ev-small-p-blue ev-bold-med mb-0">
                                &nbsp;Feedback
                              </label>
                              {showFeedbackSwitch && (
                                <div
                                  className={`feedback-switch-wrap${agent._promptModified ? ' avatar-tooltip' : ''}`}
                                  data-tooltip={agent._promptModified
                                    ? 'Prompt changed. Save and regenerate to enable Quick Feedback.'
                                    : undefined}
                                >
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!isQuickFeedbackMode}
                                    aria-label="Toggle thinking mode"
                                    className={`feedback-switch ${!isQuickFeedbackMode ? 'on' : 'off'}${showFeedbackWarning ? ' feedback-blocked' : ''}`}
                                    onClick={() => {
                                      if (agent._promptModified) return;
                                      setAgentQuickEdit(prev => ({ ...prev, [agent.id]: !prev[agent.id] }));
                                    }}
                                    disabled={!!agent._promptModified}
                                  >
                                    <span className="feedback-switch-knob"></span>
                                  </button>
                                  <span className={`feedback-switch-label ${isQuickFeedbackMode ? 'quick' : 'think'}${showFeedbackWarning ? ' feedback-blocked' : ''}`}>
                                    <i className={`bi ${isQuickFeedbackMode ? 'bi-lightning-charge-fill' : 'bi-stars'} me-1`}></i>
                                    {isQuickFeedbackMode ? 'Quick feedback' : 'Thinking'}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-1">
                            {showWritingRulesNoChange && (
                              <span
                                className="ev-alert-card-warning ev-btn-yellow-outline-round genbi-no-doc-notice ev-bold-reg d-inline-flex align-items-center"
                                style={{ whiteSpace: 'nowrap' }}
                                title={`${writingRulesLabel || 'Writing'} rules were applied; no changes were made`}
                              >
                                <i className="bi bi-spellcheck me-1"></i>
                                No changes made
                              </span>
                            )}
                            {agentHasComments && commentData && (
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="comment-count-badge"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openOriginalCommentsModal(sectionKey, agentKey);
                                  }}
                                >
                                  <i className="bi bi-chat-left-text-fill"></i>
                                  {commentData.comment_count} comment{commentData.comment_count !== 1 ? 's' : ''}
                                </button>
                                {hasSectionManagementAccess && (
                                  <button
                                    type="button"
                                    className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openDiscardCommentModal(sectionKey, agentKey);
                                    }}
                                  >
                                    <i className="bi bi-trash"> </i> <span></span>
                                    Discard Comment
                                  </button>
                                )}
                              </div>
                            )}
                            </div>
                          </div>
                          <div className="feedback-generate-wrapper">
                            <div className="feedback-input-container">
                              {agentSelectedText[agent.id] && (
                                <button
                                  type="button"
                                  className={`feedback-chevron-btn ${agentPreviewOpen[agent.id] ? 'open' : ''}`}
                                  onClick={() => togglePreviewOpen(agent.id)}
                                  title={agentPreviewOpen[agent.id] ? 'Hide attached text' : 'Show attached text'}
                                  disabled={!canGiveFeedback}
                                >
                                  <i className="bi bi-chevron-up"></i>
                                  {!agentPreviewOpen[agent.id] && <span className="feedback-chevron-dot" />}
                                </button>
                              )}
                              <DeferredTextControl
                                className={`feedback-field formField4 ${agentHasComments ? 'feedback-with-comments' : ''} ${isQuickFeedbackMode ? 'feedback-field-quick' : ''} ${agentSelectedText[agent.id] ? 'feedback-with-attachment' : ''} ${agentPreviewOpen[agent.id] ? 'feedback-preview-mode' : ''} ${showFeedbackWarning ? 'feedback-edited' : ''}`}
                                placeholder={feedbackPlaceholder}
                                rows="1"
                                value={agentPreviewOpen[agent.id]
                                  ? `"${agentSelectedText[agent.id] || ''}"`
                                  : (agentHasComments && isQuickFeedbackMode)
                                    ? ''
                                    : getAgentFeedback(sectionKey, agentKey)}
                                onCommit={(next) => {
                                  if (agentPreviewOpen[agent.id]) return;
                                  updateAgentFeedback(sectionKey, agentKey, next);
                                }}
                                readOnly={!!agentPreviewOpen[agent.id]}
                                disabled={!canGiveFeedback}
                              />

                              {agentSelectedText[agent.id] && agentPreviewOpen[agent.id] && (
                                <button
                                  type="button"
                                  className="feedback-remove-attachment"
                                  onClick={() => removeSelectionAttachment(agent.id)}
                                  title="Remove attached text"
                                  disabled={!canGiveFeedback}
                                >
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              )}
                            </div>
                            {canHere(PERMISSIONS.TEMPLATE_RUN) && (
                              <div className="feedback-generate-btn">
                                <button
                                  type="button"
                                  className="ev-btn-gradient-fill-round ev-bold-reg"
                                  onClick={() => handleRunSingleAgent(sectionKey, agentKey)}
                                  disabled={agentRunning || isLocked}
                                >
                                  <i className="bi bi-play-fill me-1"></i>
                                  Generate
                                </button>
                              </div>
                            )}
                          </div>
                          {showFeedbackWarning && (() => {
                            const warningItems = [
                              showEditedWarning && {
                                key: 'edited',
                                icon: 'bi-pencil-square',
                                title: 'Edited response',
                                text: 'Thinking may overwrite your edits.',
                              },
                              showDocsChangedWarning && {
                                key: 'docs',
                                icon: 'bi-file-earmark-diff',
                                title: 'Documents changed',
                                text: 'References may be outdated. Generate to refresh.',
                              },
                            ].filter(Boolean);
                            const warningOpen = !agentWarningCollapsed[agent.id];
                            return (
                              <div className="mt-2 py-1 ev-fs-12 rounded-3 border border-danger border-opacity-25 bg-danger bg-opacity-10 text-danger" style={{ width: 'fit-content', maxWidth: '100%' }} role="note">
                                <button
                                  type="button"
                                  className="d-flex align-items-center gap-2 w-100 px-3 border-0 bg-transparent text-start ev-bold-med"
                                  style={{ color: 'var(--color21)' }}
                                  onClick={() => setAgentWarningCollapsed(prev => ({ ...prev, [agent.id]: !prev[agent.id] }))}
                                  aria-expanded={warningOpen}
                                >
                                  <i className="bi bi-exclamation-triangle-fill"></i>
                                  <span className="flex-grow-1">
                                    Check before you refine
                                    {!warningOpen && <span className="ms-1 fw-normal">({warningItems.length})</span>}
                                  </span>
                                  <i className={`bi ${warningOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                </button>
                                {warningOpen && (
                                  <div className="mt-1">
                                    {warningItems.map((item, idx) => (
                                      <div
                                        key={item.key}
                                        className={`d-flex align-items-center gap-2 px-3${idx > 0 ? ' mt-1 pt-1 border-top border-danger border-opacity-25' : ''}`}
                                      >
                                        <i className={`bi ${item.icon} flex-shrink-0`}></i>
                                        <span>
                                          <span className="ev-bold-med">{item.title}:</span> {item.text}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {agent.status !== 'COMPLETED' && canHere(PERMISSIONS.TEMPLATE_RUN) && (
                        agent.id ? (
                          <div className={`single-agent-run-alignment`}>
                            <button
                              type="button"
                              className="ev-btn-gradient-fill-round ev-bold-reg"
                              onClick={() => handleRunSingleAgent(sectionKey, agentKey)}
                              disabled={agentRunning || isLocked || !!agentPreviewOpen[agent.id]}
                              title={agentRunning
                                ? 'Agent is running. Select it and use Stop to stop the run'
                                : agentPreviewOpen[agent.id] ? 'Close the attached-text preview to run' : undefined}
                            >
                              <i className="bi bi-play-fill me-1"></i>
                              Generate
                            </button>
                          </div>
                        ) : (
                          <div className="ev-alert-card-warning mb-0">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            Almost ready! Click <b>Save Preset </b>above to save your agent, then you can run it
                          </div>
                        )
                      )}

                    </div>
                    {isStatic && (
                      <button
                        type="button"
                        className="genbi-static-response-collapsed"
                        onClick={() => setCollapsedStaticAgents(prev => ({ ...prev, [staticCollapseKey]: !prev[staticCollapseKey] }))}
                        aria-expanded={!isStaticCollapsed}
                      >
                        <i className={`bi ${isStaticCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        <span>{isStaticCollapsed ? 'Agent Response hidden' : 'Agent Response'}</span>
                        <span className="ms-auto">{isStaticCollapsed ? 'Show' : 'Hide'}</span>
                      </button>
                    )}
                    {!isStaticCollapsed && (
                    <div
                      className="genbi-response-wrapper"
                      ref={(el) => { if (agent.id) responseWrapperRefs.current[agent.id] = el; }}
                      onMouseUp={agent.id && canGiveFeedback ? handleResponseMouseUp(agent.id) : undefined}
                    >
                      <ErrorBoundary label={`agent:${agent.id}`} resetKey={`${agent.id}:${agent.status}`}>
                      <AgentResponseSlot
                          agent={agent}
                          sectionKey={sectionKey}
                          agentKey={agentKey}
                          onTranslate={stableTranslateAgent}
                          canTranslate={canHere(PERMISSIONS.AGENT_TRANSLATE_RESPONSE)}
                          canEditResponse={canHere(PERMISSIONS.TEMPLATE_UPDATE)}
                          isLocked={isLocked}
                          canCreateReference={canHere(PERMISSIONS.CREATE_MANUAL_REFERENCE)}
                          onSaveResponse={stableSaveAgentResponse}
                          onUpdateAgentField={stableUpdateAgentField}
                          onToggleParagraphDropdown={stableToggleParagraphDropdown}
                          onCloseParagraphDropdown={stableCloseParagraphDropdown}
                          referenceIndex={referenceIndex}
                          formatJson={formData.format_json || null}
                          fabricId={fabricId}
                          templateId={templateId}
                          docIdToNameMap={docIdToNameMap}
                          docIdToThreadMap={docIdToThreadMap}
                          showNoDocumentsNotice={showNoDocumentsNotice}
                          citationsHidden={citationsHiddenFor(formData.format_json, section)}

                          isSummarySection={!!section.is_summary_section}
                          paragraphStyleOptions={paragraphStyleOptions}
                          openParagraphDropdown={openParagraphDropdown}
                          dropdownRef={dropdownRef}
                          hasSectionManagementAccess={hasSectionManagementAccess}
                          onEditingChange={handleAgentEditingChange}
                          onApplyWritingRules={canRunTemplate ? stableApplyWritingRules : null}
                          writingRulesLabel={writingRulesLabel}
                          isStopping={stoppingAgentIds.has(agent.id)}
                        />
                        </ErrorBoundary>
                        {selectionFloatBtn
                          && selectionFloatBtn.agentId === agent.id
                          && agent.status === 'COMPLETED'
                          && canGiveFeedback && (
                            <button
                              type="button"
                              className="selection-attach-btn"
                              style={{ left: `${selectionFloatBtn.x}px`, top: `${selectionFloatBtn.y}px` }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={attachSelectionToFeedback}
                            >
                              <i className="bi bi-quote me-1"></i>
                              Add in feedback
                            </button>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                {hasSectionManagementAccess && (
                <div className="col-1">
                  <div className="ev-message-card2 me-1 ms-2 template-caret-card " >
                    <button
                      type="button"
                      className="template-ordering-btn"
                      onClick={() => moveAgentOrder(sectionKey, agentKey, 'up')}
                      disabled={agentIndex === 0}
                    >
                      <i className="bi bi-caret-up-square"></i>
                    </button>
                    <button
                      type="button"
                      className="template-ordering-btn ms-1"
                      onClick={() => moveAgentOrder(sectionKey, agentKey, 'down')}
                      disabled={agentIndex === section.agentOrder.length - 1}
                    >
                      <i className="bi bi-caret-down-square"></i>
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Virtualised section list
  const visibleSectionKeys = useMemo(() => {
    return (formData.sectionOrder || [])
      .filter(key => formData.sectionsDict[key]
        && isSectionVisibleUnderFilter(key)
        && isSectionVisible(key))
      .sort((a, b) => naturalCompare(
        formData.sectionsDict[a]?.section_ordering_id || '0',
        formData.sectionsDict[b]?.section_ordering_id || '0'
      ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.sectionOrder, formData.sectionsDict, expandedSections, agentFilter]);

  // Indices of sections with an agent in edit mode: always rendered, wherever the scroll is.
  const pinnedSectionIndices = useMemo(() => {
    const editingSections = new Set(Object.values(editingAgents));
    if (!editingSections.size) return [];
    return visibleSectionKeys.reduce((acc, key, i) => {
      if (editingSections.has(key)) acc.push(i);
      return acc;
    }, []);
  }, [editingAgents, visibleSectionKeys]);

  const sectionRangeExtractor = useCallback((range) => {
    const indices = defaultRangeExtractor(range);
    if (!pinnedSectionIndices.length) return indices;
    return Array.from(new Set([...indices, ...pinnedSectionIndices])).sort((a, b) => a - b);
  }, [pinnedSectionIndices]);

  const sectionVirtualizer = useVirtualizer({
    count: visibleSectionKeys.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 240,
    overscan: 3,
    rangeExtractor: sectionRangeExtractor,
    getItemKey: (index) => visibleSectionKeys[index],
    // Measure on the next frame: resizing a section from inside the
    // ResizeObserver callback re-lays-out the list in the same frame, which
    // raised "ResizeObserver loop completed with undelivered notifications".
    useAnimationFrameWithResizeObserver: true,
  });

  useEffect(() => {
    virtualListRef.current = { keys: visibleSectionKeys, virtualizer: sectionVirtualizer };
  });

  if (isInitialLoading) {
    return <ProcessingPopup message={`Loading ${templateName || 'preset'} details`} />;
  }
  // Pagination for comment modal
  const totalCommentPages = Math.ceil(commentFeedbackList.length / COMMENTS_PER_PAGE);
  const paginatedComments = commentFeedbackList.slice(
    (commentPage - 1) * COMMENTS_PER_PAGE,
    commentPage * COMMENTS_PER_PAGE
  );
  return (
    <div >
      <LoadingOverlay isLoading={isLoading} />
      {/* Hidden file input for comment upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={handleCommentedDocumentUpload}
      />

      <div className="row mb-2 ev-card-5 w-100 m-0 ev-padding-xsmall ev-radius-large flex-nowrap">
        <div className="col-auto p-0">
          <div className="ev-message-card3 ev-btn-small">
            <span className="ev-small-p-blue avatar-tooltip" data-tooltip={formData.template_name}>
              <i className="bi bi-kanban me-1"></i>
              <TrimContent text={formData.template_name} maxLength={20} />
            </span>
          </div>
        </div>
        <div className="col pe-0 d-flex justify-content-end align-items-center">
          {canHere(PERMISSIONS.TEMPLATE_RECONFIGURE_DOC_MAPPINGS) && (
            <button
              type="button"
              className="ev-btn-lightblue-fill-round d-flex align-items-center gap-1 me-1 ev-btn-small avatar-tooltip text-nowrap"
              onClick={handleSyncDocs}
              data-tooltip="Re-match documents to agents by tags"
            >
              <i className="bi bi-arrow-repeat"></i>
              Sync Tags
            </button>
          )}
          <span className="me-1"> <StatusBadgeSm status={templateData.status} /></span>
          <div className="ev-message-card5 m-0 ev-padding-xsmall2 d-flex align-items-center ms-1">
            {/* --- HOVERABLE AGENT STATUS (INLINE) --- */}
            {templateData && (
              <div
                className="d-inline-block position-relative"
                onMouseEnter={() => setIsStatusDropdownOpen(true)}
                onMouseLeave={() => setIsStatusDropdownOpen(false)}
              >
                <div className="ev-message-card2 rounded-pill ev-btn-xsmall ev-bold-reg text-nowrap d-inline-flex align-items-center cursor-pointer">
                  <b>{templateData.agent_count} Agents</b>
                  <i
                    className={`bi ${isStatusDropdownOpen ? "bi-arrow-down" : "bi-arrow-right"} ms-1 opacity-30`}
                  ></i>
                </div>

                {isStatusDropdownOpen && (
                  <div className="position-absolute top-100 start-50 translate-middle-x mt-2 z-1000">
                    {/* Modified Square-ish Design Wrapper */}
                    <div className="ev-message-card5 m-0 ev-padding-xsmall2 ps-0 d-flex align-items-center ms-1 shadow-lg">
                      <div className="d-flex gap-2 align-items-center">
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Done: {templateData.completed_agents || 0}</b>
                        </div>
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Running: {templateData.running_agents || 0}</b>
                        </div>
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Pending: {templateData.pending_agents || 0}</b>
                        </div>
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Failed: {templateData.failed_agents || 0}</b>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {canHere(PERMISSIONS.REPORT_GENERATE) && (
            <button
              className='ev-btn-blue-fill-round ev-bold-reg ms-1 avatar-tooltip text-nowrap ev-btn-small'
              disabled={runtimeStatus === null || templateData.running_agents > 0 || templateData.report_gen_status === 'RUNNING'}
              onClick={showGenReportModal}
              data-tooltip={
                templateData.report_gen_status === 'RUNNING'
                  ? "Report generation is already in progress"
                  : templateData.running_agents > 0
                    ? "Wait for agents to complete before generating report"
                    : "Generate report in Word format"
              }
            >
              <i className="bi bi-file-text-fill icon-lg"></i> Generate Report
            </button>
          )}
          {!canHere(PERMISSIONS.REPORT_GENERATE) && canHere(PERMISSIONS.ABBREV_GET) && (
            <button
              className='ev-btn-lightblue-fill-round ev-bold-reg ms-2 avatar-tooltip text-nowrap ev-btn-small'
              onClick={() => setIsAbbrevModalVisible(true)}
              data-tooltip="View abbreviations"
            >
              <i className="bi bi-card-list icon-lg"></i> Abbreviations
            </button>
          )}
          <button
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-2 ev-btn-small text-nowrap d-flex align-items-center text-nowrap"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <i className="bi bi-arrow-repeat me-1"></i> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {!error && (
        <div>

          {canSectionManagement && hasSectionManagementAccess && formData.sectionOrder.length === 0 && (
            <div className="ev-alert-card-warning mt-2 mb-2">
              <p className="mb-0">
                <i className="bi bi-info-circle me-1"></i>
                &nbsp;
                Click "Super Section" to add your first section
              </p>
            </div>
          )}

          <div className="ev-card-3 mb-0 mt-1 ev-padding-small">
            <div className="d-flex justify-content-between align-items-center mb-2 text-nowrap">
              <div className="d-flex align-items-center gap-1">
                {canSectionManagement && hasSectionManagementAccess && (
                  <button
                    type="button"
                    className="ev-btn-blue-fill-round ev-bold-reg ev-btn-small"
                    onClick={() => addSection()}
                  >
                    <i className="bi bi-plus-circle"></i> Super Section
                  </button>
                )}
                {canSectionManagement && hasSectionManagementAccess && (
                  <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-1 avatar-tooltip"
                    onClick={addSummarySection}
                    data-tooltip="Add a Special Section that will be placed on starting of document"
                  >
                    <i className="bi bi-journal-text"></i> Special Section
                  </button>
                )}
                <button
                  type="button"
                  className="ev-btn-light-fill ev-bold-reg ms-2 avatar-tooltip ev-btn-small border"
                  onClick={handleToggleExpandCollapseAll}
                  disabled={getExpandableSectionKeys().length === 0}
                  data-tooltip={areAllExpandableExpanded() ? "Collapse all sections" : "Expand all sections"}
                >
                  <i className={`bi ${areAllExpandableExpanded() ? "bi-arrows-collapse" : "bi-arrows-expand"} ${hasAgentsWithFeedback() ? "" : "me-1"}`}></i>
                  {!hasAgentsWithFeedback() && <span>{areAllExpandableExpanded() ? "Collapse All" : "Expand All"}</span>}
                </button>

                {staticAgentCollapseKeys.length > 0 && (
                  <button
                    type="button"
                    className={`genbi-static-all-toggle ms-1 avatar-tooltip${areAllStaticAgentsCollapsed ? ' is-collapsed' : ''}`}
                    onClick={handleToggleAllStaticAgents}
                    aria-pressed={areAllStaticAgentsCollapsed}
                    data-tooltip={areAllStaticAgentsCollapsed
                      ? `Show responses of all static text agents (${staticAgentCollapseKeys.length})`
                      : `Hide responses of all static text agents (${staticAgentCollapseKeys.length})`}
                  >
                    <i className="bi bi-file-earmark-text"></i>
                    {!hasAgentsWithFeedback() && (
                      // Both labels share one grid cell so the button keeps the wider width either way.
                      <span className="genbi-static-all-toggle-label">
                        <span className={areAllStaticAgentsCollapsed ? '' : 'invisible'}>Show Static</span>
                        <span className={areAllStaticAgentsCollapsed ? 'invisible' : ''}>Hide Static</span>
                      </span>
                    )}
                    <i className="bi bi-chevron-down genbi-static-all-toggle-chevron"></i>
                  </button>
                )}

                {/* Filter & Select UI — single button directly opens the picker modal */}
                <div className="d-flex align-items-center ms-1 p-0 ev-card-7 rounded-pill">
                  <button
                    type="button"
                    className="genbi-custom-btn-group-inner ev-btn-blue-outline-round d-flex align-items-center gap-1"
                    onClick={() => setFilterPickerOpen(true)}
                    data-tooltip="Modify Agents"
                  >
                    <i className="bi bi-funnel-fill"></i>
                    {!hasAgentsWithFeedback() && <span>Modify Agents</span>}
                    {agentFilter.length > 0 && !agentFilter.includes('ALL') && (
                      <span className="ev-fs-10">({agentFilter.length})</span>
                    )}
                  </button>

                  {(() => {
                    const allVisibleAgentIds = formData.sectionOrder
                      .filter(sectionKey => !formData.sectionsDict[sectionKey]?.parent_section_id)
                      .flatMap(sectionKey => getVisibleAgentIdsInSection(sectionKey));
                    const allSelected = allVisibleAgentIds.length > 0 && allVisibleAgentIds.every(id => selectedAgentIds.has(id));
                    return (
                      <button
                        type="button"
                        className="ev-message-card4 ms-2 rounded-pill px-2 py-1 ev-fs-10 border-0"
                        onClick={() => allSelected ? deselectAllVisible() : selectAllVisible()}
                      >
                        {allSelected
                          ? 'Deselect All'
                          : selectedAgentIds.size > 0
                            ? `${selectedAgentIds.size} Selected`
                            : 'Select All'}
                      </button>
                    );
                  })()}
                </div>

                {canHere(PERMISSIONS.TEMPLATE_UPDATE) && hasPendingChanges && promptVersionPreviewAgents.size === 0 && (
                  <button
                    className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                    onClick={handleSaveEdits}
                    data-tooltip={
                      templateData.report_gen_status === 'RUNNING'
                        ? "Report generation is in progress, please wait"
                        : templateData.running_agents > 0
                          ? "Wait for agents to complete"
                          : undefined
                    }
                    disabled={isLoading || templateData.report_gen_status === 'RUNNING' || templateData.running_agents > 0}
                  >
                    <i className="bi bi-check-circle me-1"></i>
                    Save Preset
                  </button>
                )}
                {canHere(PERMISSIONS.TEMPLATE_RUN) && hasAgentsWithFeedback() && (
                  <>
                    {!hasAnyQuickFeedbackAgent() && (
                      getSelectedAgentsWithFeedbackCount() > 0 ? (
                        <button
                          className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                          onClick={() => handleVerifyAndAddressComments(true)}
                          disabled={isLoading}
                          data-tooltip="Run only selected agents with feedback"
                        >
                          <i className="bi bi-check2-square me-1"></i>
                          Address ({getSelectedAgentsWithFeedbackCount()})
                        </button>
                      ) : (
                        <button
                          className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                          onClick={() => handleVerifyAndAddressComments(false)}
                          disabled={isLoading}
                          data-tooltip="Run all agents with feedback to address comments"
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Address All
                        </button>
                      )
                    )}

                    <button
                      className="ev-btn-red-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                      onClick={() => setShowDiscardAllCommentsModal(true)}
                      disabled={isLoading}
                      data-tooltip="Remove all comments and feedback"
                    >
                      <i className="bi bi-trash me-1"></i>
                      Discard All
                    </button>
                  </>
                )}
              </div>
              <div className="d-flex align-items-center">
                {/* A status, not an action — plain text so it doesn't read as a button. */}
                {!hasSectionManagementAccess && !canRunTemplate && (
                  <span
                    className="ev-xsmall-p-blue d-flex align-items-center me-2 avatar-tooltip avatar-tooltip-end"
                    style={{ cursor: 'default' }}
                    data-tooltip="You can view this preset, but can't edit it or run agents"
                  >
                    <i className="bi bi-eye me-1"></i> View only
                  </span>
                )}
                {canHere(PERMISSIONS.TEMPLATE_RUN) && (
                  <div className="btn-group custom-btn-group-outer ms-2">
                    {(() => {
                      const runningSelectedIds = Array.from(selectedAgentIds)
                        .filter(id => getAgentById(id)?.status === 'RUNNING');
                      const stopping = runningSelectedIds.some(id => stoppingAgentIds.has(id));
                      return (
                        <button
                          className={`btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round genbi-stop-btn avatar-tooltip me-1 ${stopping ? 'is-stopping' : ''}`}
                          data-tooltip={stopping
                            ? undefined
                            : runningSelectedIds.length > 0
                              ? `Stop ${runningSelectedIds.length} running agent(s), each keeps its previous version`
                              : 'Select running agents to stop'}
                          disabled={runningSelectedIds.length === 0 || stopping}
                          onClick={() => setStopConfirmIds(runningSelectedIds)}
                        >
                          <span className="genbi-stop-btn-label">Stop</span>
                          {stopping && (
                            <span className="spinner-border spinner-border-sm genbi-stop-btn-spinner" role="status" aria-label="Stopping"></span>
                          )}
                        </button>
                      );
                    })()}
                    {writingRulesCountry && (() => {
                      // Apply Writing Rules to the selected agents (generated, unlocked ones only)
                      const rulesCount = getWritingRulesEligibleIds(Array.from(selectedAgentIds)).length;
                      return (
                        <button
                          className="btn genbi-custom-btn-group-inner ev-btn-lightblue-fill-round avatar-tooltip me-1"
                          data-tooltip={rulesCount > 0
                            ? `Apply ${writingRulesLabel} writing rules to ${rulesCount} selected agent(s)`
                            : 'Select generated agents to apply writing rules'}
                          disabled={isLoading || rulesCount === 0}
                          onClick={() => handleApplyWritingRules(Array.from(selectedAgentIds))}
                        >
                          <i className="bi bi-spellcheck"></i>
                        </button>
                      );
                    })()}
                    <button
                      className={`btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round avatar-tooltip`}
                      data-tooltip={
                        hasSelectedQuickFeedbackAgent()
                          ? 'Use generate button for quick feedback'
                          : selectedAgentIds.size > 0
                            ? `Run ${selectedAgentIds.size} selected agents`
                            : 'Select at least one agent to run'
                      }
                      disabled={isLoading || hasSelectedQuickFeedbackAgent()}
                      onClick={() => {
                        if (selectedAgentIds.size === 0) {
                          onError('Please select at least one agent to run.');
                          return;
                        }
                        handleRunConfirmation('selective_run');
                      }}
                    >
                      <i className="bi bi-play-fill"></i> Run
                    </button>
                  </div>
                )}


                <ToggleMenu
                  isLoading={isLoading}
                  templateData={templateData}
                  setTemplateData={setTemplateData}
                  fabricId={formData.fabric_id}
                  templateId={templateId}
                  handleRefresh={handleRefresh}
                  onUploadCommentedReport={triggerCommentUpload}
                  canImportAgentContext={SHOW_IMPORT_AGENT_CONTEXT && canHere(PERMISSIONS.AGENT_MAP_CONTEXT)}
                  hasPendingChanges={hasPendingChanges}
                  canUploadCommentedReport={canHere(PERMISSIONS.COMMENTS_UPLOAD)}
                />
              </div>
            </div>

            <div
              ref={scrollContainerRef}
              className="scrollable-element mb-0 vh-60"
              style={{
                overflowX: 'hidden',
                paddingRight: 'var(--px-8)'
              }}
            >
              {formData.sectionOrder.some(key => isSectionVisibleUnderFilter(key)) ? (
                <div
                  style={{
                    height: `${sectionVirtualizer.getTotalSize()}px`,
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  {sectionVirtualizer.getVirtualItems().map((virtualRow) => (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      ref={sectionVirtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: `${virtualRow.start}px`,
                        left: 0,
                        width: '100%',
                      }}
                    >
                      {renderSection(visibleSectionKeys[virtualRow.index])}
                    </div>
                  ))}
                </div>
              ) : (
                formData.sectionOrder.length > 0 && (
                  /* CHANGE: simple warning card instead of custom empty state block */
                  <div className="ev-alert-card-warning mt-2 mb-2">
                    <p className="mb-0">
                      <i className="bi bi-info-circle me-1"></i>
                      &nbsp;No agents available with the selected filter{agentFilter.length > 1 ? 's' : ''}: <b>{agentFilter.join(', ')}</b>
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Discard Comment Modal */}
      {showDiscardCommentModal && discardCommentInfo && (
        <div className="comment-success-modal-overlay" onClick={() => setShowDiscardCommentModal(false)}>
          <div className="comment-success-modal ev-message-card2">
            <div className="comment-success-modal-header" onClick={(e) => e.stopPropagation()}>

              <p className="mb-0 ev-med-p-blue comment-success-modal-title">
                Discard Comment
              </p>
              <button
                type="button"
                className="ev-btn-text-only-fill ev-fs-20"
                onClick={() => setShowDiscardCommentModal(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="comment-success-modal-body scrollable-element">
              <div className="ev-alert-card-warning mb-2">
                <i className="bi bi-exclamation-triangle-fill"></i>

                <strong>Warning:</strong> This action cannot be undone. The feedback and all original comments for this agent will be permanently removed.

              </div>

              <div className="discard-comment-info">
                <div className="discard-comment-info-item">
                  <div className="discard-comment-info-label">Section & Agent:</div>
                  <div className="discard-comment-info-value">
                    Section {discardCommentInfo.sectionOrdering}: {discardCommentInfo.sectionName} -
                    Agent {discardCommentInfo.agentOrdering}: {discardCommentInfo.agentName}
                  </div>
                </div>

                <div className="discard-comment-info-item">
                  <div className="discard-comment-info-label">Consolidated Feedback:</div>
                  <div className="discard-comment-info-value">
                    {discardCommentInfo.consolidatedFeedback}
                  </div>
                </div>

                {discardCommentInfo.originalComments && discardCommentInfo.originalComments.length > 0 && (
                  <div className="discard-comment-info-item">
                    <div className="discard-comment-info-label">
                      Original Comments ({discardCommentInfo.commentCount}):
                    </div>
                    <div className="discard-comment-info-value">
                      {discardCommentInfo.originalComments.map((comment, idx) => (
                        <div key={idx} className="original-comment-list-item">
                          {idx + 1}. "{comment}"
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
            <div className="comment-success-modal-footer">
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-bold-reg me-2"
                onClick={() => setShowDiscardCommentModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ev-btn-red-fill-round ev-bold-reg"
                onClick={executeDiscardComment}
              >
                <i className="bi bi-trash me-1"></i>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>

      )}

      {/* Comment Success Modal with Navigation */}
      {showCommentSuccessModal && commentFeedbackList.length > 0 && createPortal(
        <div className="comment-success-modal-overlay">
          <div className="comment-success-modal ev-message-card2">
            <div className="comment-success-modal-header">
              <h5 className="comment-success-modal-title">
                <i className="bi bi-check-circle-fill text-success"></i>
                Comments Processed Successfully!
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowCommentSuccessModal(false);
                  scrollToFirstCommentedAgent(agentsWithComments);
                }}
              ></button>
            </div>
            <div className="comment-success-modal-body scrollable-element">
              <div className="comment-success-intro mb-3">
                <p className="ev-small-p-blue mb-0">
                  Your feedback has been consolidated and is ready for review. Navigate through the sections below:
                </p>
                {(() => {
                  const totalComments = commentFeedbackList.reduce((sum, item) => sum + (item.commentCount || 0), 0);
                  return (
                    <span className="comment-count-badge-sm comment-success-total">
                      {totalComments} comment{totalComments !== 1 ? 's' : ''}
                    </span>
                  );
                })()}
              </div>

              {/* Comment Navigation Structure */}
              <div className="comment-nav-container scrollable-element">
                {Object.values(buildCommentNavigationStructure())
                  .sort((a, b) => naturalCompare(a.sectionOrdering, b.sectionOrdering))
                  .map(sectionData => (
                    <div key={sectionData.sectionKey} className="comment-nav-section">
                      <div
                        className={`comment-nav-section-header ${commentNavExpanded[sectionData.sectionKey] ? 'expanded' : ''}`}
                        onClick={() => toggleCommentNavSection(sectionData.sectionKey)}
                      >
                        <i className="bi bi-chevron-right chevron"></i>
                        <span className="comment-nav-section-title">
                          Section {sectionData.sectionOrdering}: {sectionData.sectionName}
                        </span>
                        <span className="comment-nav-section-badge">
                          {(() => {
                            const agentCount = countAgentsInSection(sectionData);
                            return `${agentCount} Agent${agentCount !== 1 ? 's' : ''}`;
                          })()}
                        </span>
                      </div>

                      {commentNavExpanded[sectionData.sectionKey] && (
                        <div className="comment-nav-subsection">
                          {sectionData.agents.map(renderCommentNavAgent)}
                          {sectionData.subsections.map(renderCommentNavSubsection)}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
            <div className="comment-success-modal-footer">
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg"
                onClick={() => {
                  setShowCommentSuccessModal(false);
                  scrollToFirstCommentedAgent(agentsWithComments);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Original Comments Modal */}
      {originalCommentsModalOpen && selectedAgentForComments && (
        <OriginalCommentsModal
          key={`${selectedAgentForComments.sectionKey}-${selectedAgentForComments.agentKey}`}
          commentData={getCommentData(selectedAgentForComments.sectionKey, selectedAgentForComments.agentKey)}
          onClose={() => setOriginalCommentsModalOpen(false)}
        />
      )}
      {quickFeedbackNotice && (
        <WarningPopup
          title="Quick Feedback"
          message={quickFeedbackNotice}
          onClose={() => setQuickFeedbackNotice(null)}
        />
      )}
      {stopNotice && (
        <WarningPopup
          title={stopNotice.title}
          message={stopNotice.message}
          onClose={closeStopNotice}
        />
      )}

      <ConfirmationModal
        show={!!stopConfirmIds}
        onClose={() => setStopConfirmIds(null)}
        onConfirm={() => {
          const ids = stopConfirmIds;
          setStopConfirmIds(null);
          stopAgentRun(ids);
        }}
        title="Stop Running Agents"
        message={`This will stop ${stopConfirmIds?.length || 0} running agent(s). Each agent keeps its previous version, and agents that depend on them continue with that version. Continue?`}
        confirmButtonText="Yes, Stop"
        confirmButtonClass="btn-danger"
      />

      {/* Verify Comments Confirmation Modal */}
      {/* Discard All Comments Confirmation Modal */}
      <ConfirmationModal
        show={showDiscardAllCommentsModal}
        onClose={() => setShowDiscardAllCommentsModal(false)}
        onConfirm={executeDiscardAllComments}
        title="Discard All Comments"
        message="This will remove all comments and feedback from all agents. You will need to upload the commented document again to restore them. Do you wish to continue?"
        confirmButtonText="Yes, Discard All"
        confirmButtonClass="btn-danger"
      />

      <ConfirmationModal
        show={showVerifyCommentsConfirmation}
        onClose={() => setShowVerifyCommentsConfirmation(false)}
        onConfirm={executeVerifyAndAddressComments}
        title={verifyCommentsMode === 'selected' ? "Address Selected Feedback" : "Verify and Address All Feedback"}
        message={`This will re-run ${verifyCommentsCount} agent(s) with the provided feedback. Continue?`}
        confirmButtonText={verifyCommentsMode === 'selected' ? "Yes, Address Selected" : "Yes, Address All"}
        confirmButtonClass="btn-primary"
      />

      <ConfirmationModal
        show={restoreVersionInfo.isOpen}
        onClose={() => setRestoreVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '', docsChanged: false, restoreDocs: false, versionDocIds: [], currentDocIds: [], docNameMap: {} })}
        onConfirm={executeRestoreVersion}
        title="Confirm Restore Version"
        message={`This is a destructive action. Are you sure you want to restore to ${restoreVersionInfo.versionName}? All newer versions will be permanently deleted.`}
        confirmButtonText="Yes, Restore"
        confirmButtonClass="btn-warning"
        bodyContent={restoreVersionInfo.docsChanged ? (
          <div className="mt-2">
            <div className="alert alert-warning d-flex align-items-start py-2 ev-fs-12 mb-2">
              <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
              <span>This version was generated with different documents than currently configured.</span>
            </div>
            <div className="form-check p-2 rounded" style={{ background: 'rgba(13,110,253,0.06)' }}>
              <input
                className="form-check-input ms-0 me-2"
                type="checkbox"
                id="restoreDocsCheck"
                checked={restoreVersionInfo.restoreDocs}
                onChange={(e) => setRestoreVersionInfo(prev => ({ ...prev, restoreDocs: e.target.checked }))}
              />
              <label className="form-check-label ev-fs-12 fw-semibold" htmlFor="restoreDocsCheck">
                Also restore document configuration
                <div className="fw-normal text-muted ev-fs-11">Selects the documents this version was generated with</div>
              </label>
            </div>
          </div>
        ) : null}
      />

      <ConfirmationModal
        show={restorePromptVersionInfo.isOpen}
        onClose={() => setRestorePromptVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '' })}
        onConfirm={executeRestorePromptVersion}
        title="Confirm Restore Prompt Version"
        message={`This is a destructive action. Are you sure you want to restore to prompt ${restorePromptVersionInfo.versionName}? All newer prompt versions will be permanently deleted.`}
        confirmButtonText="Yes, Restore"
        confirmButtonClass="btn-warning"
      />

      <ConfirmationModal
        show={showPromptVersionWarning}
        onClose={() => { setShowPromptVersionWarning(false); setPendingPromptVersionShow(null); }}
        onConfirm={async () => {
          setShowPromptVersionWarning(false);
          if (pendingPromptVersionShow) {
            const { sectionKey, agentKey } = pendingPromptVersionShow;
            const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
            if (agent) {
              const feedback = getAgentFeedback(sectionKey, agentKey);
              const finalFeedback = buildFinalFeedback(agent.id, feedback);
              if (onProcessingStart) onProcessingStart('Running agent');
              try {
                const response = await axios.post(`${API_BASE_URL}/run-template`,
                  { template_id: templateId, fabric_id: fabricId, agent_id: agent.id, feedback: finalFeedback || null, master_feedback: null, enable_grounding_check: !!agentGrounding[agent.id], ...getAgentPersistRunOptions(agent) },
                  { headers });
                statusFenceRef.current = Date.now();
                if (response.data.status === 'failed') { onError(response.data.message || 'Failed to run agent'); }
                else {
                  setAgentFeedback(prev => { const next = { ...prev }; delete next[agent.id]; return next; });
                  clearSelectionAttachments(agent.id);
                  clearCommentState(agent.id);
                  setSingleAgentExecution({ agentId: null, feedback: null });
                  await handleRefresh();
                }
              } catch (err) { onError(err.message || 'Failed to run agent'); }
              finally { if (onProcessingEnd) onProcessingEnd(); }
            }
            setPendingPromptVersionShow(null);
          }
        }}
        title="Running with Latest Prompt"
        message="You are viewing an older prompt. The agent will run using the latest saved prompt, not the one you are currently previewing. Want to continue?"
        confirmButtonText="Yes, Run"
        confirmButtonClass="btn-warning"
      />

      <ConfirmationModal
        show={showRunConfirmation}
        onClose={() => setShowRunConfirmation(false)}
        onConfirm={executeRunTemplate}
        title={`Confirm ${runType === 'single_agent' ? 'Run Agent' : 'Run Preset'}`}
        message="Are you sure you want to proceed?"
        confirmButtonText={runType === 'single_agent' ? 'Run Agent' : 'Run Preset'}
        confirmButtonClass="btn-primary"
        bodyContent={showRunConfirmation ? getRunModalBodyContent() : null}
        scrollable={runType === 'single_agent' && (getReportAnnotationTexts(singleAgentExecution.agentId).length > 0
          || !!(agentSelectedText[singleAgentExecution.agentId] || '').trim())}
      />

      <ConfirmationModal
        show={!!writingRulesAgentIds}
        onClose={() => setWritingRulesAgentIds(null)}
        onConfirm={executeWritingRules}
        title="Apply Writing Rules"
        confirmButtonText="Apply Rules"
        confirmButtonClass="btn-primary"
        bodyContent={writingRulesAgentIds ? (
          <div>
            {hasPendingChanges && (
              <div className="ev-alert-card-warning mb-3">
                <i className="bi bi-info-circle me-2"></i>
                You have unsaved changes. They will be saved first. To discard, click Refresh first.
              </div>
            )}
            <div className="ev-alert-card-success mb-3">
              <i className="bi bi-spellcheck me-2"></i>
              <strong>{writingRulesLabel} writing rules</strong> will be applied to {writingRulesAgentIds.length === 1
                ? 'this agent'
                : `${writingRulesAgentIds.length} agents`}: spelling, gender-inclusive language, numbers,
              dates, terms and footnotes are corrected in the existing response. Nothing is regenerated.
            </div>
            <ul className="mb-0 ps-3">
              <li>Saved as a new version; the current version stays in the version history.</li>
              <li>Citations, numbers and table structure are protected.</li>
              <li>Locked and not-yet-generated agents are skipped.</li>
            </ul>
          </div>
        ) : null}
      />

      <ValueMessageSelector
        isOpen={vmSelectorOpen}
        onClose={() => setVmSelectorOpen(false)}
        currentSectionForVM={currentSectionForVM}
        templateId={templateId}
        fabricId={formData.fabric_id}
        fabricName={formData.fabric_name}
        sectionsDict={formData.sectionsDict}
        onSelectionSave={handleVMSelection}
      />

      <DocumentPreview
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />

      <ReportModalForm
        isOpen={isGenReportModalVisible}
        onClose={onCloseGenerateReport}
        templateDetails={{ 'id': templateId, 'name': templateName }}
        updFunc={performSaveTemplate}
        formData={formData}
        capabilities={capabilities}
        hasPendingChanges={hasPendingChanges}
      />

      <ReportModalForm
        isOpen={isAbbrevModalVisible}
        abbreviationsOnly
        onClose={() => setIsAbbrevModalVisible(false)}
        templateDetails={{ 'id': templateId, 'name': templateName }}
        updFunc={performSaveTemplate}
        formData={formData}
        capabilities={capabilities}
      />

      <AgentConfigure
        isOpen={configureModalOpen}
        onClose={() => setConfigureModalOpen(false)}
        sectionKey={currentAgentConfig.sectionKey}
        agentKey={currentAgentConfig.agentKey}
        formData={formData}
        fabricTreeData={fabricTreeData}
        updateAgentField={updateAgentField}
        templateId={templateId}
        refAgentExpanded={refAgentExpanded}
        onRefAgentExpandedChange={setRefAgentExpanded}
        groundingEnabled={(() => {
          const aId = formData.sectionsDict?.[currentAgentConfig.sectionKey]
            ?.agentsDict?.[currentAgentConfig.agentKey]?.id;
          return aId ? !!agentGrounding[aId] : false;
        })()}
        onGroundingChange={(checked) => {
          const aId = formData.sectionsDict?.[currentAgentConfig.sectionKey]
            ?.agentsDict?.[currentAgentConfig.agentKey]?.id;
          if (!aId) return; // grounding only applies to saved (runnable) agents
          setAgentGrounding(prev => ({ ...prev, [aId]: checked }));
        }}
      />
      {comparePanelState && (
        <AgentVersionComparePanel
          agentId={comparePanelState.agentId}
          versionA={comparePanelState.versionA}
          versionB={comparePanelState.versionB}
          fabricId={fabricId}
          onClose={() => setComparePanelState(null)}
        />
      )}

    <SyncDocsModal
    isOpen={syncDocsModalOpen}
    onClose={() => setSyncDocsModalOpen(false)}
    changes={syncDocsChanges}
    onApply={handleApplySyncChanges}
    isLoading={syncDocsLoading}
    />

      <FilterPickerModal
        isOpen={filterPickerOpen}
        onClose={() => setFilterPickerOpen(false)}
        formData={formData}
        agentFilter={agentFilter}
        setAgentFilter={setAgentFilter}
        selectedAgentIds={selectedAgentIds}
        toggleAgentSelection={toggleAgentSelection}
        handleSectionCheckbox={handleSectionCheckbox}
        isAgentVisible={isAgentVisible}
        isSectionVisibleUnderFilter={isSectionVisibleUnderFilter}
        getVisibleAgentIdsInSection={getVisibleAgentIdsInSection}
        selectAllVisible={selectAllVisible}
        deselectAllVisible={deselectAllVisible}
        naturalCompare={naturalCompare}
        // Null hides the whole "Apply to all agents" block on projects that hide Extend/Amend
        applyGlobalAgentSetting={showBulkExtendAmend ? applyGlobalAgentSetting : null}
      />

    </div>
  );
};
export default GenbiTemplateTabViewer;
